import { computed, inject, Inject, Injectable, OnDestroy, signal } from '@angular/core';
import { Observable, Subject, Subscription, takeUntil, catchError, EMPTY, of, map, tap, lastValueFrom, defaultIfEmpty, finalize, from, concatMap, scan, timeout, TimeoutError, retry, timer } from 'rxjs';

import { MESSAGING_PORT } from '../../app/ports/messaging/messaging.token';
import { MessagingPort } from '../../app/ports/messaging/messaging.port';
import { CHAT_DIRECTORY_PORT } from '../../app/ports/people/chat-directory.token';
import { ChatDirectoryPort } from '../../app/ports/people/chat-directory.port';
import { SessionCtl } from './session.ctl.service';
import { ChatContactUI, ChatGroupUI, ChatMessageReplyUI, ChatMessageUI, ChatConversationUI, GlobalContactUI, ChatTypingUserUI, ChatSeenByEntryUI, ChatUploadMediaKind } from '../../domain/models/messaging/chat-ui.model';
import { mapSnapshotToGroupOrContact, mapSnapshotToConversation, mapParticipantsToContacts, mapNewMessageToUi, mapHistoryMessageToUi, buildOptimisticTextMessage, buildOptimisticMediaMessage, generateClientMessageId, formatChatDisplayName, resolveLiveChatSenderName } from '../mappers/messaging';
import { ChatAttachmentUpload, DocumentS3UploadService } from './document-s3-upload.service';
import { ChatUploadManager } from './chat-upload-manager.service';
import { ChatMediaUrlPolicy } from './chat-media-url-policy.service';
import { colorForUserId } from '../mappers/messaging/chat-color.util';
import { formatLastSeen } from '../mappers/messaging/time-format.util';
import { MessagingSdk, PeopleSdk } from 'lib-klab-sdks';
import { deriveMessageReactionDetails, MessageReactionDetailsViewModel } from '../mappers/messaging/message-reaction-details.mapper';
import { normalizeText } from '../../shared/utils/text/normalize-text.util';

/** People Chat directory maximum page size. */
const CHAT_DIRECTORY_PAGE_SIZE = 50;
const CHAT_DIRECTORY_MAX_PAGE_SIZE = 100;
const CHAT_DIRECTORY_SEARCH_DEBOUNCE_MS = 300;
const PRESENCE_RECONCILIATION_INTERVAL_MS = 50_000;
const PRESENCE_RECONCILIATION_JITTER_MS = 5_000;
const PRESENCE_QUERY_BATCH_SIZE = 50;
/** MOMSG-1159: minimum gap between repairPresenceNow() runs — visibilitychange and
 * focus commonly fire back-to-back on tab regain, doubling heartbeat+query traffic. */
const PRESENCE_REPAIR_MIN_INTERVAL_MS = 1_000;
/** MOMSG-1159: retry a failed presence query before giving up on it silently. */
const PRESENCE_QUERY_RETRY_COUNT = 3;
const PRESENCE_QUERY_RETRY_DELAY_MS = 1_500;
const TYPING_START_THROTTLE_MS = 3_000;
const TYPING_STOP_DEBOUNCE_MS = 1_200;
const TYPING_INDICATOR_TIMEOUT_MS = 5_000;
const OPTIMISTIC_RECONCILIATION_WINDOW_MS = 30_000;
const OPTIMISTIC_CANONICAL_ECHO_WINDOW_MS = 10_000;
const MUTATION_CONFIRMATION_TIMEOUT_MS = 8_000;
const MUTATION_INTENT_STALE_GUARD_MS = 60_000;
const GROUP_ROLE_NOTICE_DEDUPE_WINDOW_MS = 5_000;

/** Datos necesarios para (re)ejecutar el pipeline de upload+send de un adjunto — ver {@link ChatStateService.runAttachmentUploadAndSend}. */
interface AttachmentUploadContext {
  conversationId: string;
  clientFileId: string;
  clientMessageId: string;
  caption: string;
  canonicalReplyTo: ChatMessageReplyUI | null;
  latestBackup: OptimisticLatestSnapshotBackup;
  /** Metadata de álbum de imágenes (MOMSG-1172). null si el mensaje no pertenece a un álbum. */
  album: MessagingSdk.AlbumAssociation | null;
}
const GROUP_STATUS_REFRESH_DEBOUNCE_MS = 75;
// Must stay within the messaging-ws contract cap (MessagesFetchPayload.limit <= 50);
// a larger page is rejected with KLAB409 "Invalid event payload" before the handler runs.
const GROUP_STATUS_REFRESH_PAGE_SIZE = 50;
const READ_RESULT_TIMEOUT_MS = 8_000;
const READ_RETRY_MAX_ATTEMPTS = 5;
const READ_RETRY_BASE_MS = 300;
const GROUP_STATUS_REFRESH_MAX_DELAY_MS = 5_000;
const HISTORY_LOAD_TIMEOUT_MS = 8_000;
const HISTORY_LOAD_MAX_RETRIES = 2;
const HISTORY_LOAD_RETRY_BASE_MS = 1_000;
const HISTORY_PAGE_SIZE = 30;
const HISTORY_WINDOW_MAX_MESSAGES = 200;
const HISTORY_RECENT_TAIL_RESERVE = 40;

interface HistoryState {
  hasMore: boolean;
  hasMoreAfter: boolean;
  nextBeforeId: string | null;
  nextAfterAnchorId: string | null;
  droppedBefore: boolean;
  droppedAfter: boolean;
  isLoading: boolean;
  requestKind: 'older' | 'newer' | 'around' | null;
  error: string | null;
}

interface InternalState {
  /** Raw snapshots indexed by conversationId. Source of truth from the server. */
  snapshots: Map<string, MessagingSdk.ConversationSnapshot>;
  /** Messages indexed by conversationId. Built from initial lastMessage + chat:message:new. */
  messages: Map<string, ChatMessageUI[]>;
  history: Map<string, HistoryState>;
  files: Map<string, ChatMessageUI[]>;
}

type DirectoryPhase = 'idle' | 'loading' | 'ready' | 'error';

interface DirectoryState {
  phase: DirectoryPhase;
  query: string;
  contacts: GlobalContactUI[];
  nextPage: number;
  totalPages: number | null;
  generation: number;
  error: unknown | null;
}

interface CanonicalIdentityEntry {
  userId: number;
  displayName: string;
  fullName: string;
  identityVersion: string | null;
  source: 'unversioned' | 'versioned-authoritative';
}

type OptimisticLatestSnapshotBackup = Pick<MessagingSdk.ConversationSnapshot, 'lastMessage' | 'unreadCount' | 'updatedAt'> | null;
type MessageInsertionResult = 'inserted' | 'reconciled-optimistic' | 'duplicate';
type OptimisticGroupEvent = 'chat:group:update' | 'chat:group:member:add' | 'chat:group:member:remove' | 'chat:group:member:role:update' | 'chat:group:delete';

interface OptimisticGroupBackup {
  token: number;
  event: OptimisticGroupEvent;
  conversationId: string;
  backup: MessagingSdk.ConversationSnapshot | null;
  optimisticSnapshot: MessagingSdk.ConversationSnapshot | null;
  timeout?: ReturnType<typeof setTimeout>;
  roleTargetUserId?: number;
  role?: 'MEMBER' | 'MODERATOR';
  commandId?: string;
}

interface QueuedRoleCommand {
  token: number;
  payload: MessagingSdk.UpdateGroupMemberRolePayload;
}

interface PendingMediaMutation {
  operation: MessagingSdk.MediaMutationOperation;
  correlationId: string;
  conversationId?: string;
  optimisticMessage?: ChatMessageUI;
  latestBackup?: OptimisticLatestSnapshotBackup;
  groupBackupToken?: number;
  file?: File;
  canonicalReference?: string;
  timeout: ReturnType<typeof setTimeout>;
}

type PendingMessageMutationEvent = 'chat:message:edit' | 'chat:message:delete' | 'chat:message:pin';
type PendingMessageMutationAction = 'pin' | 'unpin';

interface PendingMessageMutation {
  token: number;
  event: PendingMessageMutationEvent;
  action?: PendingMessageMutationAction;
  conversationId: string;
  messageId: string;
  backupMessage: ChatMessageUI | null;
  optimisticMessage: ChatMessageUI | null;
  backupLastMessage: MessagingSdk.ConversationSnapshot['lastMessage'] | undefined;
  backupUpdatedAt: string;
  optimisticLastMessage: MessagingSdk.ConversationSnapshot['lastMessage'] | undefined;
  optimisticUpdatedAt: string | undefined;
  backupPinnedMessages: MessagingSdk.PinnedMessageSnapshot[] | undefined;
  optimisticPinnedMessages: MessagingSdk.PinnedMessageSnapshot[] | undefined;
  timeout: ReturnType<typeof setTimeout>;
}

interface LatestMessageMutationIntent {
  token: number;
  event: PendingMessageMutationEvent;
  action?: PendingMessageMutationAction;
  newText?: string;
  conversationId: string;
  messageId: string;
  createdAtMs: number;
}

/**
 * Mecanismo de reconciliación optimista para el pin de conversaciones,
 * paralelo al de {@link PendingMessageMutation}. No reusa ese tipo: es un
 * union CERRADO con `messageId` obligatorio y backups a nivel de contenido
 * de mensaje (backupMessage, lastMessage, pinnedMessages) que no aplican
 * acá — pinear una conversación solo toca `pinnedAt` del participante
 * propio dentro del snapshot, sin mensaje involucrado.
 */
interface PendingConversationPinMutation {
  token: number;
  conversationId: string;
  action: MessagingSdk.PinAction;
  backupPinnedAt: string | null | undefined;
  timeout: ReturnType<typeof setTimeout>;
  createdAtMs: number;
}

interface LatestConversationPinMutationIntent {
  token: number;
  action: MessagingSdk.PinAction;
  createdAtMs: number;
}

/**
 * Estado de presencia de un usuario, derivado del stream
 * {@code chat:presence:update} del backend. Es transient: vive solo en
 * memoria del cliente y se refresca con cada evento.
 */
export interface UserPresence {
  isOnline: boolean;
  /** ISO 8601 del último seen reportado por el server. Solo se rellena cuando isOnline=false. */
  lastSeen: string | null;
  /** Server event timestamp used to reject stale presence updates when available. */
  updatedAt?: string | null;
  /** Monotonic server transition revision. Preferred over timestamps when present. */
  revision?: number;
  /** Redis generation. Revisions are comparable only within the same epoch. */
  epoch?: string;
}

/**
 * Payload del Toast in-app de mensaje nuevo — se emite cuando llega
 * {@code chat:message:new} para una conversación que el usuario no está
 * viendo en este momento (otra conversación seleccionada, o pestaña oculta).
 */
export interface NewMessageToastEvent {
  conversationId: string;
  /** Nombre del grupo cuando la conversación es GROUP; null en DIRECT (el título es el remitente). */
  conversationName: string | null;
  isGroup: boolean;
  /** Conteo de no leídos del snapshot, ya actualizado con este mensaje. */
  unreadCount: number;
  sender: MessagingSdk.NewMessagePayload['sender'];
  preview: string;
  messageId: string;
}

/** In-app notice emitted when the current user's group admin role changes. */
export interface GroupRoleNoticeEvent {
  conversationId: string;
  targetUserId: number;
  role: MessagingSdk.GroupMemberRoleUpdatedPayload['role'];
  text: string;
}

interface PendingMessageReaction {
  token: number;
  commandId: string;
  commandEmoji: string;
  conversationId: string;
  messageId: string;
  actorUserId: number;
  authoritativeReactions: MessagingSdk.MessageReaction[];
  authoritativeRevision: number;
  commandTargetActorReaction: MessagingSdk.MessageReaction | null;
  desiredActorReaction: MessagingSdk.MessageReaction | null;
  hasQueuedIntent: boolean;
  optimisticReactions: MessagingSdk.MessageReaction[];
  optimisticRevision: number;
}

export interface ChatAttachmentUploadOptions {
  mediaKind?: ChatUploadMediaKind;
  durationMs?: number | null;
  /** Metadata de álbum de imágenes (MOMSG-1172), minteada por el composer antes de emitir el lote. */
  album?: MessagingSdk.AlbumAssociation | null;
}

/**
 * Orchestrator that bridges the {@link MessagingPort} with the UI dashboard.
 *
 * <p>Responsibilities:
 * <ul>
 *   <li>Open the WebSocket and fetch the initial conversation list on {@link init}.</li>
 *   <li>Maintain a reactive state of conversations and messages using Angular signals.</li>
 *   <li>Map AsyncAPI payloads → UI-shaped models so the existing dashboard
 *       template needs no template changes.</li>
 *   <li>Translate UI intents (send text, mark read, ACK delivery) into port emits.</li>
 * </ul>
 *
 * <p>The connection lives for the whole authenticated session: the app shell
 * calls {@link init} on startup (chat surfaces may also call it — it is
 * idempotent) and {@link dispose} on logout. Individual chat screens must NOT
 * dispose on destroy, otherwise in-app notifications stop outside the chat.
 */
@Injectable({ providedIn: 'root' })
export class ChatStateService implements OnDestroy {
  // ── Private reactive state ────────────────────────────────────────────────
  private readonly state = signal<InternalState>({
    snapshots: new Map(),
    messages: new Map(),
    history: new Map(),
    files: new Map(),
  });

  /** True once a successful chat:init response has been received. */
  private readonly readySignal = signal(false);

  /** Backend-projected session capability for creating GROUP conversations. */
  private readonly canCreateGroupsSignal = signal(false);

  /** True only while the underlying socket is currently connected. */
  private readonly socketConnectedSignal = signal(false);

  /** Selected conversation id (controlled from the UI). */
  private readonly selectedIdSignal = signal<string | null>(null);

  /**
   * Mapa de presencia por userId. Se actualiza con cada chat:presence:update.
   * Si un usuario no figura en el mapa, su estado se trata como desconocido
   * y la UI cae a {@code isOnline:false} (estado conservador).
   */
  private readonly presenceSignal = signal<Map<number, UserPresence>>(new Map());

  /** Single active People directory query. Pages from previous queries are never cached. */
  private readonly directoryState = signal<DirectoryState>({
    phase: 'idle',
    query: '',
    contacts: [],
    nextPage: 0,
    totalPages: null,
    generation: 0,
    error: null,
  });
  private readonly directoryRolesSignal = signal<string[]>([]);
  private directorySearchTimer: ReturnType<typeof setTimeout> | null = null;
  private directoryRequest: Subscription | null = null;
  /** Session-scoped canonical identity; cleared on dispose and never evicted mid-session. */
  private readonly identityCache = new Map<number, CanonicalIdentityEntry>();
  private readonly identityRevisionSignal = signal(0);

  /** Last server-side chat error received through chat:error. */
  private readonly lastErrorSignal = signal<MessagingSdk.ErrorPayload | null>(null);

  private readonly lastMediaRejectionSignal = signal<MessagingSdk.MediaMutationRejectedPayload | null>(null);
  private readonly rejectedMediaFileSignal = signal<File | null>(null);

  /** Last conversation confirmed by chat:conversation:created for this client. */
  private readonly lastCreatedConversationSignal = signal<MessagingSdk.ConversationSnapshot | null>(null);

  /** True only while this UI is waiting for its own create-conversation confirmation. */
  private pendingCreateSelection = false;
  private pendingDirectParticipantUserId: number | null = null;
  /** True solo mientras esperamos abrir un chat DIRECT al que acabamos de reenviar. */
  private pendingForwardParticipantUserId: number | null = null;

  private presenceReconciliationTimer: ReturnType<typeof setTimeout> | null = null;
  private presenceRepairListenersStarted = false;
  private lastPresenceRepairAtMs = 0;
  private readonly typingSignal = signal<Map<string, Map<number, ChatTypingUserUI>>>(new Map());
  private readonly typingFallbackTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly localTypingStartedAt = new Map<string, number>();
  private readonly localTypingStopTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly optimisticGroupBackups: OptimisticGroupBackup[] = [];
  private optimisticGroupTokenSeq = 0;
  private readonly pendingMessageMutations: PendingMessageMutation[] = [];
  private readonly latestMessageMutationIntents = new Map<string, LatestMessageMutationIntent>();
  private readonly pendingMessageReactions = new Map<string, PendingMessageReaction>();
  private readonly pendingConversationPinMutations: PendingConversationPinMutation[] = [];
  private readonly latestConversationPinMutationIntents = new Map<string, LatestConversationPinMutationIntent>();
  private pendingConversationPinMutationTokenSeq = 0;
  private readonly deletedConversationIds = new Set<string>();
  private readonly removedMembershipGenerations = new Map<string, number>();
  private readonly messageMutationOccurredAt = new Map<string, number>();
  private readonly roleMutationOccurredAt = new Map<string, number>();
  /** Alias local→canónico registrado cuando el eco del servidor reemplaza al mensaje optimista. */
  private readonly reconciledLocalMessageIds = new Map<string, string>();
  /** Authoritative rollback base for each in-flight optimistic latest message. */
  private readonly optimisticLatestBackups = new Map<string, OptimisticLatestSnapshotBackup>();
  private readonly readReceiptsByConversation = new Map<string, Map<number, number>>();
  private readonly deliveryReceiptsByConversation = new Map<string, Map<number, number>>();
  private readonly groupStatusRefreshTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly groupStatusRefreshInFlight = new Set<string>();
  private readonly groupStatusRefreshLoadedMessages = new Map<string, boolean>();
  private readonly groupStatusRefreshAttempts = new Map<string, number>();
  private readonly historyLoadRetryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly historyLoadRetryAttempts = new Map<string, number>();
  /**
   * Anchors que devolvieron una página "nuevos" VACÍA pero con hasMoreAfter=true.
   * Evita re-emitir un request idéntico en cada scroll (loop sin progreso);
   * se limpia cuando hay progreso real, llegan mensajes nuevos o el server
   * reporta que no hay más.
   */
  private readonly historyNewerNoProgressAnchors = new Map<string, Set<string>>();
  private readonly readStates = new Map<string, PendingReadState>();
  private readonly readVisibilityClaims = new Map<string, string>();
  private readonly filesFetchInFlight = new Set<string>();
  private readonly filesLoaded = new Set<string>();
  private readonly groupRoleNoticeDedupe = new Map<string, number>();
  private readonly groupSendModeNoticeDedupe = new Map<string, number>();
  private readonly pendingMediaMutations = new Map<string, PendingMediaMutation>();
  private readonly reconcilingMediaMutations = new Set<string>();
  /**
   * Cola de envío de adjuntos por conversación. Las subidas a S3 corren en
   * paralelo (cada archivo tiene su propio job en ChatUploadManager), pero
   * el sendFileMessage de cada archivo se encadena acá para salir siempre
   * en el orden de selección del usuario, sin importar cuál termine de
   * subir primero (ej: un video de 1GB no debe dejar pasar adelante a una
   * foto liviana). Se limpia sola cuando la cola queda vacía, así no crece
   * sin límite durante una sesión larga con muchas conversaciones.
   */
  private readonly attachmentSendChains = new Map<string, Promise<void>>();
  /** Contexto de envío por mensaje optimista de adjunto (keyed por el `id` local del mensaje), necesario para poder reintentar (`retryAttachmentUpload`) sin volver a pedirle datos al caller original. Se borra al reconciliarse o cancelarse. */
  private readonly attachmentUploadContexts = new Map<string, AttachmentUploadContext>();
  /** Evita rehidratar de nuevo los adjuntos interrumpidos en cada reconexión. */
  private interruptedUploadsRehydrated = false;
  private pendingMessageMutationTokenSeq = 0;
  private pendingMessageReactionTokenSeq = 0;
  private conversationRefreshInFlight = false;
  private lastReconnectRefreshAt = 0;
  /**
   * Timestamp de la última vez que un refetch completo de conversaciones
   * (fetchInitialConversations, ej. al reconectar el socket) aplicó datos
   * frescos a los snapshots. Usado por rollbackPendingConversationPinMutation
   * para no pisar con un backup local viejo un pinnedAt que ya llegó
   * confirmado por esa vía, aunque la confirmación puntual por socket se haya
   * perdido durante el corte de conexión.
   */
  private lastConversationsSnapshotAppliedAtMs = 0;
  private latestPresenceQueryToken = 0;
  private presenceQuerySequence = 0;
  private lastPresenceQueryKey: string | null = null;
  private presenceEpoch: string | null = null;
  private readonly retiredPresenceEpochs = new Set<string>();
  /** Lifecycle-ceased identities must not be queried for presence. */
  private readonly unavailablePresenceUserIds = new Set<number>();
  /** Short-lived UI workflows contribute relevance without owning a presence loop. */
  private readonly presenceRelevanceClaims = new Map<string, ReadonlySet<number>>();
  private queuedRoleCommand: QueuedRoleCommand | null = null;

  private destroy$ = new Subject<void>();
  private initStarted = false;
  private streamsWired = false;

  // ── Public reactive views ─────────────────────────────────────────────────
  /** True once the conversation list has been loaded. */
  readonly ready = this.readySignal.asReadonly();

  /** Currently selected conversationId, or null. */
  readonly selectedConversationId = this.selectedIdSignal.asReadonly();

  /** True only when the latest successful chat:init ACK explicitly grants it. */
  readonly canCreateGroups = this.canCreateGroupsSignal.asReadonly();

  /**
   * Mapa de presencia por userId. Expuesto en modo lectura para los
   * componentes que necesiten consultar isOnline/lastSeen por usuario
   * (filas de la consulta activa, flujos por propósito y participantes).
   */
  readonly presenceById = this.presenceSignal.asReadonly();

  /**
   * Consulta puntual de presencia por userId. Devuelve {@code null}
   * cuando no hay info reciente para ese usuario.
   */
  getPresence(userId: number): UserPresence | null {
    return this.presenceSignal().get(userId) ?? null;
  }

  /** Projects the session's canonical identity without changing contact-specific metadata. */
  reconcileContactIdentity(contact: GlobalContactUI): GlobalContactUI {
    this.identityRevisionSignal();
    const userId = contact.userId;
    if (!isFinitePositiveUserId(userId)) return contact;
    const canonical = this.identityCache.get(userId);
    return canonical ? identityToGlobalContact(canonical, contact) : contact;
  }

  /** Current active-query directory rows with self exclusion, dedupe and presence overlay. */
  readonly directoryContacts = computed<GlobalContactUI[]>(() => {
    const raw = this.directoryState().contacts;
    const presence = this.presenceSignal();
    const currentUserId = this.currentUserId();
    const currentEmployeeId = this.currentEmployeeId();

    return dedupeGlobalContacts(raw.filter((c) => !isSelfContact(c, currentUserId, currentEmployeeId)))
      .map((contact) => this.reconcileContactIdentity(contact))
      .map((c) => {
        if (c.userId === null) return c;
        const live = presence.get(c.userId);
        if (!live) return c;
        return {
          ...c,
          isOnline: live.isOnline,
          lastSeen: live.isOnline ? undefined : formatLastSeen(live.lastSeen),
        };
      });
  });

  /** Compatibility alias: this is no longer a complete company directory. */
  readonly allContacts = this.directoryContacts;
  readonly directoryQuery = computed(() => this.directoryState().query);
  readonly directoryRoles = this.directoryRolesSignal.asReadonly();
  readonly directoryLoading = computed(() => this.directoryState().phase === 'loading');
  readonly directoryError = computed<string | null>(() =>
    this.directoryState().error === null ? null : 'directory_load_failed',
  );
  readonly directoryHasMore = computed(() => {
    const state = this.directoryState();
    if (state.phase === 'idle') return false;
    if (state.totalPages === null) return state.phase === 'error';
    return state.nextPage < state.totalPages;
  });
  readonly contactsLoaded = computed(() => {
    const phase = this.directoryState().phase;
    return phase === 'ready' || phase === 'error';
  });

  /** Latest chat:error pushed by the server, exposed so UI can show visible feedback. */
  readonly lastError = this.lastErrorSignal.asReadonly();

  readonly lastMediaRejection = this.lastMediaRejectionSignal.asReadonly();
  readonly rejectedMediaFile = this.rejectedMediaFileSignal.asReadonly();

  /** Latest persisted conversation creation ack for the current client. */
  readonly lastCreatedConversation = this.lastCreatedConversationSignal.asReadonly();

  /** Emits the conversationId when chat:group:updated is confirmed by the server. */
  readonly groupUpdated$ = new Subject<string>();

  /** Emits removal confirmation details when chat:group:member:removed is confirmed by the server. */
  readonly groupMemberRemoved$ = new Subject<{
    conversationId: string;
    removedUserId: number;
  }>();

  /** Emits the conversationId when chat:group:deleted is confirmed by the server. */
  readonly groupDeleted$ = new Subject<string>();

  /** Emits when a new message arrives for a conversation the user isn't currently viewing. */
  readonly newMessageToast$ = new Subject<NewMessageToastEvent>();

  /** Emits when the current user is assigned or unassigned as group admin. */
  readonly groupRoleNotice$ = new Subject<GroupRoleNoticeEvent>();

  /** Emits when a group's "solo admins pueden escribir" mode toggles (MOMSG-1053). Excluye al usuario que hizo el cambio. */
  readonly groupSendModeNotice$ = new Subject<{ conversationId: string; text: string }>();

  /**
   * Emits when chat:message:forward:blocked llega para el propio usuario:
   * el forward se completó best-effort, pero uno o más destinos eran grupos
   * restringidos (onlyAdminsCanSend) a los que no pudo postear. Es respuesta
   * directa a la acción del propio usuario, no un broadcast — no necesita
   * dedupe.
   */
  readonly forwardBlockedNotice$ = new Subject<{ text: string }>();
  readonly visibilityEpochChanged$ = new Subject<string>();

  /** Emits the conversationId cada vez que llega un chat:message:read:update de grupo (MOMSG-1081). Alimenta el refresco en vivo del popover "Ver quién lo vio". */
  readonly readReceiptUpdated$ = new Subject<string>();

  /** All GROUP conversations as UI shapes. */
  readonly groups = computed<ChatGroupUI[]>(() => {
    const snaps = Array.from(this.state().snapshots.values());
    return snaps
      .filter((s) => s.type === 'GROUP')
      .map((s) => {
        const { group } = mapSnapshotToGroupOrContact(s, this.currentUserId());
        return group!;
      });
  });

  /**
   * All DIRECT conversations as UI shapes. Use this as the default "contacts"
   * list when the user is not browsing a specific group.
   *
   * <p>Aplica overlay con {@link presenceSignal} para que isConnected y
   * lastSeen reflejen el estado en tiempo real recibido por
   * chat:presence:update, no el snapshot inicial del chat:init.
   */
  readonly directContacts = computed<ChatContactUI[]>(() => {
    const snaps = Array.from(this.state().snapshots.values());
    const presence = this.presenceSignal();
    return snaps
      .filter((s) => s.type === 'DIRECT')
      .map((s) => {
        const { contact } = mapSnapshotToGroupOrContact(s, this.currentUserId());
        return overlayPresenceOnContact(contact!, s, this.currentUserId(), presence);
      });
  });

  /** Full UI conversation for the currently selected id (or null). */
  readonly currentConversation = computed<ChatConversationUI | null>(() => {
    const id = this.selectedIdSignal();
    if (!id) return null;
    const state = this.state();
    const snap = state.snapshots.get(id);
    if (!snap) return null;

    const baseConv = mapSnapshotToConversation(snap, this.currentUserId());
    const conversation = overlayPresenceOnConversation(baseConv, snap, this.currentUserId(), this.presenceSignal());
    const liveMessages = state.messages.get(id) ?? baseConv.messages;
    const history = this.getHistoryState(id);
    return {
      ...conversation,
      messages: this.projectLiveGroupMessageIdentities(id, liveMessages, state),
      pinnedMessages: this.projectLiveGroupMessageIdentities(id, conversation.pinnedMessages ?? [], state),
      files: state.files.get(id) ?? [],
      hasMoreHistory: history.hasMore,
      hasMoreAfterHistory: history.hasMoreAfter,
      isLoadingHistory: history.isLoading,
      historyError: history.error,
    };
  });

  /**
   * Derives reaction identities for a current message from existing in-memory
   * session, directory, participant and sender snapshots. This is read-only and
   * never loads data or emits a socket command.
   */
  reactionDetailsForMessage(
    message: ChatMessageUI,
    conversationId: string | null = this.selectedIdSignal(),
  ): MessageReactionDetailsViewModel {
    const snapshot = conversationId ? this.state().snapshots.get(conversationId) : undefined;
    const cachedActors = (message.reactions ?? [])
      .map((reaction) => this.identityCache.get(reaction.userId))
      .filter((identity): identity is CanonicalIdentityEntry => identity !== undefined)
      .map((identity) => identityToGlobalContact(identity));
    return deriveMessageReactionDetails(message.reactions, {
      currentUserId: this.currentUserId(),
      currentUser: this.sessionCtl.currentUser,
      contacts: mergeIdentityContacts(this.allContacts(), cachedActors),
      participants: snapshot?.participants,
      messageSender: message.senderSnapshot,
    });
  }

  /** Remote participants currently typing in the selected conversation. */
  readonly currentTypingUsers = computed<ChatTypingUserUI[]>(() => {
    const id = this.selectedIdSignal();
    if (!id) return [];
    return Array.from(this.typingSignal().get(id)?.values() ?? []);
  });

  /**
   * Members of the currently selected GROUP conversation as contacts.
   *
   * <p>Aplica overlay con {@link presenceSignal} para reflejar presencia
   * en tiempo real en lugar del snapshot inicial del backend.
   */
  readonly currentGroupContacts = computed<ChatContactUI[]>(() => {
    const id = this.selectedIdSignal();
    if (!id) return this.directContacts();
    const snap = this.state().snapshots.get(id);
    if (!snap || snap.type !== 'GROUP') return this.directContacts();
    const presence = this.presenceSignal();
    return mapParticipantsToContacts(snap, this.currentUserId()).map((c) => overlayPresenceOnGroupContact(c, presence));
  });

  /**
   * Members of an explicit GROUP conversation as contacts — mismo resultado
   * que {@link currentGroupContacts}, pero resuelto contra un
   * `conversationId` explícito en vez de `selectedIdSignal()` (MOMSG-1201:
   * el widget flotante tiene su propia conversación activa, independiente de
   * la seleccionada en el dashboard — mismo motivo por el que
   * {@link getMessageSeenBy} resuelve contra `conversationId` explícito, ver
   * su comentario).
   */
  getGroupContacts(conversationId: string): ChatContactUI[] {
    const snap = this.state().snapshots.get(conversationId);
    if (!snap || snap.type !== 'GROUP') return [];
    const presence = this.presenceSignal();
    return mapParticipantsToContacts(snap, this.currentUserId()).map((c) => overlayPresenceOnGroupContact(c, presence));
  }

  /**
   * MOMSG-1208: nombres de TODOS los participantes activos del grupo,
   * incluido el usuario actual — a diferencia de {@link getGroupContacts}
   * (que lo excluye a propósito, es para el picker de "a quién puedo
   * mencionar"), este índice alimenta el RESALTADO de @menciones ya
   * escritas en un mensaje (klab-chat-message-item). Si alguien te
   * menciona a vos, tu propio nombre tiene que poder resolverse también.
   */
  getGroupParticipantNames(conversationId: string): string[] {
    const snap = this.state().snapshots.get(conversationId);
    if (!snap || snap.type !== 'GROUP') return [];
    const names = new Set<string>();
    for (const participant of snap.participants ?? []) {
      if (participant.isActive === false) continue;
      const name = formatChatDisplayName({ displayName: participant.displayName, fullName: participant.fullName });
      if (name) names.add(name);
    }
    return [...names];
  }

  /** Active participant userIds for the selected GROUP, including the current user/creator. */
  readonly currentGroupParticipantUserIds = computed<string[]>(() => {
    const id = this.selectedIdSignal();
    if (!id) return [];
    const snap = this.state().snapshots.get(id);
    if (!snap || snap.type !== 'GROUP') return [];
    return (snap.participants ?? []).filter((p) => p.isActive !== false).map((p) => String(p.userId));
  });

  /**
   * Participantes que ya vieron el mensaje `messageId` de la conversación
   * `conversationId` (MOMSG-1081). La elegibilidad (participante activo, ya
   * presente en el grupo para ese mensaje, cursor de lectura ya superado) se
   * resuelve enteramente en el backend vía {@link MessagingPort.fetchMessageSeenBy}
   * — este método solo hace la network call y resuelve cada `userId` de la
   * respuesta contra el snapshot local para obtener nombre/avatar/color.
   *
   * <p>Resuelve participantes desde el snapshot de `conversationId`
   * explícitamente (NO desde {@link currentGroupContacts}, que está
   * hard-wired a `selectedIdSignal()`): si el usuario cambia de conversación
   * mientras el popover de una conversación previa sigue abierto,
   * `currentGroupContacts()` devolvería silenciosamente los participantes de
   * la conversación equivocada.
   */
  getMessageSeenBy(conversationId: string, messageId: string): Observable<{ entries: ChatSeenByEntryUI[]; hasUnrecordedReads: boolean }> {
    const snap = this.state().snapshots.get(conversationId);
    if (!snap || snap.type !== 'GROUP') return of({ entries: [], hasUnrecordedReads: false });
    return this.port.fetchMessageSeenBy(conversationId, messageId).pipe(
      map((response) => {
        const readAtByUserId = new Map(response.entries.map((entry) => [entry.userId, entry.readAt] as const));
        const eligibleParticipants = (snap.participants ?? []).filter((p) => readAtByUserId.has(p.userId));
        const contacts = mapParticipantsToContacts({ ...snap, participants: eligibleParticipants }, this.currentUserId());
        const entries = contacts
          .map((contact) => ({
            contact,
            readAt: (contact.userId != null ? readAtByUserId.get(contact.userId) : undefined) ?? null,
          }))
          .sort((a, b) => (a.readAt ?? '').localeCompare(b.readAt ?? ''));
        return { entries, hasUnrecordedReads: response.hasUnrecordedReads };
      }),
    );
  }

  // ── Dependencies ──────────────────────────────────────────────────────────
  constructor(
    @Inject(MESSAGING_PORT) private readonly port: MessagingPort,
    @Inject(CHAT_DIRECTORY_PORT) private readonly chatDirectoryPort: ChatDirectoryPort,
    private readonly sessionCtl: SessionCtl,
    private readonly documentS3Upload: DocumentS3UploadService,
    private readonly mediaUrlPolicy: ChatMediaUrlPolicy,
    private readonly chatUploadManager: ChatUploadManager
  ) {}

  ngOnDestroy(): void {
    this.dispose();
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  /**
   * Opens the socket connection, fetches the conversation list and wires
   * server-pushed event streams. Safe to call multiple times — additional
   * calls are no-ops once {@link ready} is true.
   */
  init(): void {
    if (this.readySignal() || this.initStarted) return;

    this.canCreateGroupsSignal.set(false);
    this.initStarted = true;

    this.port
      .connect()
      .pipe(
        takeUntil(this.destroy$),
        tap(() => this.socketConnectedSignal.set(true)),
        tap(() => this.wireStreams()),
        tap(() => this.startPresenceRepair()),
        // Bootstrap conversations before querying presence. Besides making the
        // chat usable sooner, this avoids opening presence subscriptions while
        // the initial chat:init ACK is still pending.
        tap(() => this.fetchInitialConversations()),
        catchError((err) => {
          console.error('[ChatState] connect failed', err);
          this.canCreateGroupsSignal.set(false);
          this.initStarted = false;
          return EMPTY;
        }),
      )
      .subscribe();
  }

  /**
   * Activates a chat surface. A ready session refreshes its existing
   * conversation snapshots once; concurrent activations share the in-flight
   * chat:init request.
   */
  activate(): void {
    if (!this.readySignal()) {
      this.init();
      return;
    }
    this.fetchInitialConversations();
  }

  /** Opens the directory without making authenticated-shell startup depend on People. */
  openContactsDirectory(): void {
    const state = this.directoryState();
    if (state.phase !== 'idle' || this.directorySearchTimer !== null) return;
    this.requestDirectoryPage(state.query, 0, state.generation);
  }

  /** Replaces the active query after an exact 300 ms debounce. */
  searchContactsDirectory(value: string): void {
    const query = value.trim();
    this.cancelDirectoryWork();
    const generation = this.directoryState().generation + 1;
    this.directoryState.set({
      phase: 'idle',
      query,
      contacts: [],
      nextPage: 0,
      totalPages: null,
      generation,
      error: null,
    });
    this.refreshPresenceForRelevantUsers();
    this.directorySearchTimer = setTimeout(() => {
      this.directorySearchTimer = null;
      this.requestDirectoryPage(query, 0, generation);
    }, CHAT_DIRECTORY_SEARCH_DEBOUNCE_MS);
  }

  /** Loads or retries the next page for the one active query. */
  loadNextContactsDirectory(): void {
    const state = this.directoryState();
    if (state.phase === 'idle' || state.phase === 'loading') return;
    if (state.totalPages !== null && state.nextPage >= state.totalPages) return;
    this.requestDirectoryPage(state.query, state.nextPage, state.generation);
  }

  /** Clears the active query and cancels both debounce and transport work. */
  resetContactsDirectory(): void {
    this.cancelDirectoryWork();
    const generation = this.directoryState().generation + 1;
    this.directoryState.set({
      phase: 'idle',
      query: '',
      contacts: [],
      nextPage: 0,
      totalPages: null,
      generation,
      error: null,
    });
    this.refreshPresenceForRelevantUsers();
  }

  /** Purpose-only page lookup. It never mutates the shared active-query state. */
  getContactsPage(query: string, page: number, size = CHAT_DIRECTORY_PAGE_SIZE): Observable<PeopleSdk.PagedChatContactResponse> {
    const normalizedQuery = query.trim();
    const safePage = Number.isInteger(page) && page >= 0 ? page : 0;
    const safeSize = Math.min(CHAT_DIRECTORY_MAX_PAGE_SIZE, Math.max(1, Math.trunc(size) || CHAT_DIRECTORY_PAGE_SIZE));
    return this.chatDirectoryPort.getContacts(safePage, safeSize, normalizedQuery || undefined);
  }

  /**
   * Maps purpose-only People rows and retains only their bounded identity data.
   * The shared personal directory state is intentionally untouched.
   */
  preparePurposeDirectoryContacts(
    contacts: readonly PeopleSdk.ChatContactResponse[],
    existingContacts: readonly GlobalContactUI[] = [],
  ): GlobalContactUI[] {
    const mapped = this.reconcileDirectoryContacts(contacts, existingContacts);
    if (mapped.length > 0) this.reenrichExistingReplySenderNames();
    return mapped;
  }

  /** Adds or clears one owner-scoped contribution to the existing presence relevance union. */
  setPresenceRelevanceClaim(owner: string, candidateUserIds: readonly number[]): void {
    const key = owner.trim();
    if (!key) return;
    const currentUserId = this.currentUserId();
    const userIds = new Set(candidateUserIds.filter(isFinitePositiveUserId).filter((userId) => userId !== currentUserId));
    if (userIds.size === 0) this.presenceRelevanceClaims.delete(key);
    else this.presenceRelevanceClaims.set(key, userIds);
    this.refreshPresenceForRelevantUsers();
  }

  /** Tears down all subscriptions and closes the socket. */
  dispose(): void {
    this.cancelDirectoryWork();
    const directoryGeneration = this.directoryState().generation + 1;
    this.directoryState.set({
      phase: 'idle', query: '', contacts: [], nextPage: 0,
      totalPages: null, generation: directoryGeneration, error: null,
    });
    this.stopPresenceRepair();
    // MOMSG-1159: without this, a dispose()+init() cycle within 1s (fast
    // logout/login, quick tab close+reopen on the same instance) would leave
    // the very first post-reinit repair silently dropped by a guard timestamp
    // left over from the previous session.
    this.lastPresenceRepairAtMs = 0;
    this.stopTypingForSelectedConversation();
    this.clearAllTypingState();
    this.clearOptimisticGroupTimeouts();
    this.clearPendingMessageMutationTimeouts();
    this.pendingMessageReactions.clear();
    this.clearGroupRoleNoticeDedupe();
    this.clearGroupSendModeNoticeDedupe();
    this.clearGroupStatusRefreshes();
    for (const timer of this.historyLoadRetryTimers.values()) clearTimeout(timer);
    this.historyLoadRetryTimers.clear();
    this.historyLoadRetryAttempts.clear();
    this.historyNewerNoProgressAnchors.clear();
    this.revokeAllLocalObjectUrls();
    this.destroy$.next();
    this.destroy$.complete();
    this.destroy$ = new Subject<void>();
    this.port.disconnect();
    this.readySignal.set(false);
    this.canCreateGroupsSignal.set(false);
    this.socketConnectedSignal.set(false);
    this.identityCache.clear();
    this.identityRevisionSignal.update((revision) => revision + 1);
    this.presenceSignal.set(new Map());
    this.presenceRelevanceClaims.clear();
    this.readReceiptsByConversation.clear();
    this.deliveryReceiptsByConversation.clear();
    this.clearReadStates();
    this.readVisibilityClaims.clear();
    this.reconciledLocalMessageIds.clear();
    this.optimisticLatestBackups.clear();
    this.filesFetchInFlight.clear();
    this.filesLoaded.clear();
    this.removedMembershipGenerations.clear();
    this.messageMutationOccurredAt.clear();
    this.roleMutationOccurredAt.clear();
    for (const pending of this.pendingMediaMutations.values()) clearTimeout(pending.timeout);
    this.pendingMediaMutations.clear();
    this.reconcilingMediaMutations.clear();
    this.queuedRoleCommand = null;
    this.initStarted = false;
    this.streamsWired = false;
    this.interruptedUploadsRehydrated = false;
    this.lastPresenceQueryKey = null;
    this.presenceEpoch = null;
    this.retiredPresenceEpochs.clear();
    this.unavailablePresenceUserIds.clear();
    this.state.set({
      snapshots: new Map(),
      messages: new Map(),
      history: new Map(),
      files: new Map(),
    });
    this.selectedIdSignal.set(null);
    this.lastCreatedConversationSignal.set(null);
    this.lastMediaRejectionSignal.set(null);
    this.rejectedMediaFileSignal.set(null);
  }

  /**
   * Read-only lookup for secondary chat surfaces that need to render an
   * existing conversation without changing the global dashboard selection or
   * emitting read receipts.
   */
  getConversationById(conversationId: string | null): ChatConversationUI | null {
    if (!conversationId) return null;
    const state = this.state();
    const snap = state.snapshots.get(conversationId);
    if (!snap) return null;

    const baseConv = mapSnapshotToConversation(snap, this.currentUserId());
    const conversation = overlayPresenceOnConversation(baseConv, snap, this.currentUserId(), this.presenceSignal());
    const liveMessages = state.messages.get(conversationId) ?? baseConv.messages;
    const history = this.getHistoryState(conversationId);
    return {
      ...conversation,
      messages: this.projectLiveGroupMessageIdentities(conversationId, liveMessages, state),
      pinnedMessages: this.projectLiveGroupMessageIdentities(conversationId, conversation.pinnedMessages ?? [], state),
      files: state.files.get(conversationId) ?? [],
      hasMoreHistory: history.hasMore,
      hasMoreAfterHistory: history.hasMoreAfter,
      isLoadingHistory: history.isLoading,
      historyError: history.error,
    };
  }

  loadPreviousMessages(conversationId = this.selectedIdSignal(), limit = 30): void {
    if (!conversationId) return;
    const history = this.getHistoryState(conversationId);
    if (history.isLoading || !history.hasMore) return;

    this.setHistoryState(conversationId, {
      ...history,
      isLoading: true,
      error: null,
    });
    this.port
      .fetchMessages({ conversationId, beforeId: history.nextBeforeId, limit })
      .pipe(timeout(HISTORY_LOAD_TIMEOUT_MS), takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success === false) {
            this.setHistoryState(conversationId, {
              ...history,
              isLoading: false,
              error: 'history_load_failed',
            });
            return;
          }
          this.clearHistoryLoadRetry(conversationId);
          this.historyLoadRetryAttempts.delete(conversationId);
          if (!this.responseMatchesCurrentVisibility(conversationId, response.visibilityEpoch)) {
            this.setHistoryState(conversationId, {
              ...this.getHistoryState(conversationId),
              isLoading: false,
            });
            return;
          }
          const older = (response.messages ?? []).map((m) => mapHistoryMessageToUi(m, this.currentUserId(), this.mediaUrlPolicy));
          this.prependHistoryMessages(conversationId, older);
          this.afterCanonicalMessagesMerged(conversationId);
          this.setHistoryState(conversationId, {
            ...this.getHistoryState(conversationId),
            hasMore: response.hasMore,
            nextBeforeId: response.nextBeforeId ?? null,
            droppedBefore: false,
            isLoading: false,
            error: null,
          });
        },
        error: (err) => {
          console.error('[ChatState] fetchMessages failed', err);
          const isInitialLoad = history.nextBeforeId === null;
          const isRetriable = err instanceof TimeoutError
            || (err instanceof Error && /messages_fetch_(TIMEOUT|KAFKA_UNAVAILABLE)/.test(err.message));
          const attempts = this.historyLoadRetryAttempts.get(conversationId) ?? 0;
          if (isInitialLoad && isRetriable && attempts < HISTORY_LOAD_MAX_RETRIES) {
            this.historyLoadRetryAttempts.set(conversationId, attempts + 1);
            this.setHistoryState(conversationId, {
              ...history,
              isLoading: false,
              error: null,
            });
            this.scheduleHistoryLoadRetry(conversationId, attempts);
            return;
          }
          this.setHistoryState(conversationId, {
            ...history,
            isLoading: false,
            error: 'history_load_failed',
          });
        },
      });
  }

  /** Carga mensajes posteriores al extremo visible mediante el contrato around existente. */
  loadNewerMessages(conversationId = this.selectedIdSignal(), limit = HISTORY_PAGE_SIZE): void {
    if (!conversationId) return;
    const history = this.getHistoryState(conversationId);
    const anchor = history.nextAfterAnchorId ?? (this.state().messages.get(conversationId) ?? []).at(-1)?.id;
    if (history.isLoading || !history.hasMoreAfter || !anchor) return;
    // Non-progress guard: once the same anchor came back empty with
    // hasMoreAfter still true, do not re-emit an identical request on the
    // next scroll. Only advance with actual progress or a server "no more".
    if (this.historyNewerNoProgressAnchors.get(conversationId)?.has(anchor)) return;

    this.setHistoryState(conversationId, { ...this.getHistoryState(conversationId), isLoading: true, requestKind: 'newer', error: null });
    this.port.fetchMessagesAround({ conversationId, messageId: anchor, beforeLimit: 0, afterLimit: limit })
      .pipe(timeout(HISTORY_LOAD_TIMEOUT_MS), takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success === false) {
            console.warn('[ChatState] fetch newer messages rejected', response.errorCode);
            this.historyNewerNoProgressAnchors.delete(conversationId);
            this.setHistoryState(conversationId, { ...this.getHistoryState(conversationId), isLoading: false, requestKind: 'newer', error: 'history_load_failed' });
            return;
          }
          if (!this.responseMatchesCurrentVisibility(conversationId, response.visibilityEpoch)) {
            // Stale-epoch response: discard it. Only clear the loading flags —
            // never write the captured cursor/history back over fresh state so
            // concurrent live appends and retention are preserved.
            this.setHistoryState(conversationId, { ...this.getHistoryState(conversationId), isLoading: false, requestKind: null });
            return;
          }
          const newer = (response.messages ?? []).map((message) => mapHistoryMessageToUi(message, this.currentUserId(), this.mediaUrlPolicy));
          if (newer.length === 0 && response.hasMoreAfter) {
            // Server claims more messages after the anchor but returned none:
            // keep the cursor unchanged and remember the anchor so the same
            // request is not re-emitted on the next scroll (no-progress loop).
            const noProgress = this.historyNewerNoProgressAnchors.get(conversationId) ?? new Set<string>();
            noProgress.add(anchor);
            this.historyNewerNoProgressAnchors.set(conversationId, noProgress);
            this.setHistoryState(conversationId, { ...this.getHistoryState(conversationId), isLoading: false, requestKind: null });
            return;
          }
          this.historyNewerNoProgressAnchors.delete(conversationId);
          this.mergeMessagesChronologically(conversationId, newer);
          this.afterCanonicalMessagesMerged(conversationId);
          this.setHistoryState(conversationId, {
            ...this.getHistoryState(conversationId),
            hasMoreAfter: response.hasMoreAfter,
            nextAfterAnchorId: newer.at(-1)?.id ?? anchor,
            droppedAfter: false,
            isLoading: false,
            requestKind: null,
            error: null,
          });
        },
        error: (err) => {
          console.error('[ChatState] fetch newer messages failed', err);
          // A transport failure must not burn the anchor: keep it retriable.
          this.historyNewerNoProgressAnchors.delete(conversationId);
          this.setHistoryState(conversationId, { ...this.getHistoryState(conversationId), isLoading: false, requestKind: 'newer', error: 'history_load_failed' });
        },
      });
  }

  /**
   * Reintento manual de la carga inicial del historial — pensado para el
   * botón "Reintentar" de la UI cuando {@code historyError} está visible.
   * Resetea el contador de reintentos automáticos y dispara el fetch.
   */
  retryInitialHistoryLoad(conversationId = this.selectedIdSignal()): void {
    if (!conversationId) return;
    this.historyLoadRetryAttempts.delete(conversationId);
    this.clearHistoryLoadRetry(conversationId);
    this.loadPreviousMessages(conversationId);
  }

  /**
   * Reintento manual de cualquier falla de historial, no solo la carga
   * inicial: si la última falla fue una paginación "nuevos" (requestKind
   * 'newer'), reintenta con {@link loadNewerMessages}; de lo contrario usa
   * dirección "más antiguos" ({@link loadPreviousMessages}). Es el handler
   * que usa el botón "Reintentar" de la UI — reintentando en la MISMA
   * dirección no quedamos con el banner pegado en un hasMore=false.
   */
  retryHistoryLoad(conversationId = this.selectedIdSignal()): void {
    if (!conversationId) return;
    this.historyLoadRetryAttempts.delete(conversationId);
    this.clearHistoryLoadRetry(conversationId);
    const history = this.getHistoryState(conversationId);
    if (history.requestKind === 'newer' && history.hasMoreAfter) {
      this.loadNewerMessages(conversationId);
      return;
    }
    this.loadPreviousMessages(conversationId);
  }

  private scheduleHistoryLoadRetry(conversationId: string, attempt: number): void {
    this.clearHistoryLoadRetry(conversationId);
    const timer = setTimeout(() => {
      this.historyLoadRetryTimers.delete(conversationId);
      // Solo reintenta si la carga inicial sigue pendiente — si otra carga
      // ya trajo la primera página, el reintento sería una paginación espuria.
      if (this.getHistoryState(conversationId).nextBeforeId !== null) return;
      this.loadPreviousMessages(conversationId);
    }, HISTORY_LOAD_RETRY_BASE_MS * 2 ** attempt);
    this.historyLoadRetryTimers.set(conversationId, timer);
  }

  private clearHistoryLoadRetry(conversationId: string): void {
    const timer = this.historyLoadRetryTimers.get(conversationId);
    if (timer) clearTimeout(timer);
    this.historyLoadRetryTimers.delete(conversationId);
  }

  private ensureInitialHistoryLoaded(conversationId: string): void {
    const history = this.getHistoryState(conversationId);
    if (history.isLoading || history.nextBeforeId !== null || !history.hasMore) return;

    this.loadPreviousMessages(conversationId);
  }

  // ── UI intents ────────────────────────────────────────────────────────────

  /** Select a conversation by its conversationId (Mongo ObjectId). */
  selectConversation(conversationId: string | null): void {
    const previousId = this.selectedIdSignal();
    if (previousId && previousId !== conversationId) {
      this.clearPendingMessageReactionsForConversation(previousId, true);
    }
    if (previousId && previousId !== conversationId) {
      this.clearHistoryLoadRetry(previousId);
      this.historyLoadRetryAttempts.delete(previousId);
    }
    this.stopTypingForSelectedConversation();
    this.selectedIdSignal.set(conversationId);
    if (conversationId) {
      this.ensureInitialHistoryLoaded(conversationId);
      this.fetchConversationFiles(conversationId);
    }
    this.refreshPresenceForRelevantUsers();
  }

  fetchConversationFiles(conversationId: string, beforeId: string | null = null, limit = 50): void {
    if (!this.ensureSocketActionReady('fetchConversationFiles')) return;
    if (typeof this.port.fetchConversationFiles !== 'function') return;
    if (beforeId === null && (this.filesFetchInFlight.has(conversationId) || this.filesLoaded.has(conversationId))) return;
    this.filesFetchInFlight.add(conversationId);
    this.port
      .fetchConversationFiles({ conversationId, beforeId, limit })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (!this.responseMatchesCurrentVisibility(conversationId, response.visibilityEpoch)) return;
          const files = (response.files ?? []).map((message) => mapHistoryMessageToUi(message, this.currentUserId(), this.mediaUrlPolicy));
          this.mergeConversationFiles(conversationId, files);
          this.filesFetchInFlight.delete(conversationId);
          if (beforeId === null) this.filesLoaded.add(conversationId);
        },
        error: (err) => {
          this.filesFetchInFlight.delete(conversationId);
          console.error('[ChatState] fetchConversationFiles failed', err);
        },
      });
  }

  /**
   * `onComplete(skipped)` (WARNING Judgment Day, race condition de
   * navegación real confirmada por los 2 jueces): `skipped=true` cuando
   * NUNCA se llegó a pedir nada (socket no ready, puerto sin implementar el
   * método, o ya había OTRO fetch en curso) — a diferencia de "se completó
   * y el mensaje no estaba". El llamador (navegación por flechas del
   * widget) debe reintentar el mismo target en el caso `skipped`, no
   * tratarlo como "no encontrado".
   *
   * `isStillRelevant` (WARNING Judgment Day, punto 3, ronda 2): este método
   * ya validaba `responseMatchesCurrentVisibility` (epoch de membership),
   * pero eso responde "¿el snapshot sigue siendo válido?", no "¿esto sigue
   * siendo lo que el llamador quiere?". Clickear las flechas de navegación
   * rápido podía hacer que una respuesta vieja reemplazara en silencio la
   * ventana de mensajes visible real vía `replaceHistoryRangeAroundMessage`,
   * aunque el estado de presentación del llamador (índice/resaltado) ya
   * hubiera avanzado a otro match — el fence de generación del widget solo
   * protegía SU propio estado, no lo que este método aplicaba acá. Se
   * evalúa DENTRO de `next`, antes de tocar el historial, para que una
   * respuesta obsoleta se descarte igual que un epoch de visibilidad
   * obsoleto. Opcional: si se omite, se preserva el comportamiento previo.
   */
  fetchMessagesAround(
    conversationId: string,
    messageId: string,
    onComplete?: (skipped?: boolean) => void,
    isStillRelevant?: () => boolean,
  ): void {
    if (!this.ensureSocketActionReady('fetchMessagesAround')) {
      onComplete?.(true);
      return;
    }
    if (typeof this.port.fetchMessagesAround !== 'function') {
      onComplete?.(true);
      return;
    }
    const history = this.getHistoryState(conversationId);
    if (history.isLoading) {
      onComplete?.(true);
      return;
    }
    this.setHistoryState(conversationId, { ...history, isLoading: true, requestKind: 'around', error: null });
    this.port
      .fetchMessagesAround({ conversationId, messageId, beforeLimit: 15, afterLimit: 15 })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => onComplete?.(false)),
      )
      .subscribe({
        next: (response) => {
          if (!this.responseMatchesCurrentVisibility(conversationId, response.visibilityEpoch)) {
            this.setHistoryState(conversationId, { ...this.getHistoryState(conversationId), isLoading: false, requestKind: null });
            return;
          }
          if (isStillRelevant && !isStillRelevant()) {
            this.setHistoryState(conversationId, { ...this.getHistoryState(conversationId), isLoading: false, requestKind: null });
            return;
          }
          const messages = (response.messages ?? []).map((message) => mapHistoryMessageToUi(message, this.currentUserId(), this.mediaUrlPolicy));
          if (messages.length === 0) {
            this.setHistoryState(conversationId, { ...this.getHistoryState(conversationId), isLoading: false, requestKind: null });
            return;
          }

          this.replaceHistoryRangeAroundMessage(conversationId, messages);
          this.afterCanonicalMessagesMerged(conversationId);
          this.setHistoryState(conversationId, {
            hasMore: response.hasMoreBefore,
            hasMoreAfter: response.hasMoreAfter,
            nextBeforeId: messages[0]?.id ?? null,
            nextAfterAnchorId: messages.at(-1)?.id ?? null,
            droppedBefore: false,
            droppedAfter: false,
            isLoading: false,
            requestKind: null,
            error: null,
          });
        },
        error: (err) => {
          console.error('[ChatState] fetchMessagesAround failed', err);
          this.setHistoryState(conversationId, { ...this.getHistoryState(conversationId), isLoading: false, requestKind: null, error: 'history_load_failed' });
        },
      });
  }

  /** UI intent: the active conversation input changed. Emits typing start/stop with spam guards. */
  handleTypingInput(text: string): void {
    const conversationId = this.selectedIdSignal();
    if (!conversationId) return;
    if (!this.readySignal() || !this.socketConnectedSignal() || !this.canEmitConversationScopedEvent(conversationId)) return;

    if ((text ?? '').trim()) {
      this.sendTypingStartIfNeeded(conversationId);
      this.scheduleTypingStop(conversationId);
      return;
    }

    this.sendTypingStop(conversationId);
  }

  /**
   * Inicia una conversación 1-a-1 con el userId indicado.
   *
   * <p>Si ya existe una DIRECT con ese participante, la selecciona. Si ya hay
   * una creación pendiente para el mismo participante, no emite otra creación.
   * Si no existe, emite chat:conversation:create. El server responde con
   * chat:conversation:created al creador, lo cual:
   *   1. dispara onConversationCreated$ → upsertSnapshot
   *   2. wireStreams completa y agrega el snapshot al state
   * Después el componente puede selectConversation(newId) para abrirla.
   */
  createDirectConversation(participantUserId: number): void {
    if (!participantUserId || participantUserId === this.currentUserId()) {
      console.warn('[ChatState] createDirectConversation: invalid participantUserId', participantUserId);
      return;
    }

    if (!this.ensureSocketActionReady('createDirectConversation')) return;

    const existingId = this.findDirectConversationIdByParticipant(participantUserId);
    if (existingId) {
      this.selectConversation(existingId);
      return;
    }

    if (this.pendingCreateSelection && this.pendingDirectParticipantUserId === participantUserId) {
      return;
    }

    this.pendingCreateSelection = true;
    this.pendingDirectParticipantUserId = participantUserId;
    this.port.createDirectConversation({ type: 'DIRECT', participantUserId });
  }

  /**
   * Returns the existing DIRECT conversation id for a participant userId.
   *
   * <p>This keeps secondary chat surfaces from guessing by display name or
   * duplicating backend conversations when the conversation already exists.
   */
  findDirectConversationIdByParticipant(participantUserId: number | null | undefined): string | null {
    if (!Number.isFinite(participantUserId) || !participantUserId || participantUserId === this.currentUserId()) {
      return null;
    }

    const direct = Array.from(this.state().snapshots.values()).find((snap) => snap.type === 'DIRECT' && (snap.participants ?? []).some((participant) => participant.userId === participantUserId && participant.isActive !== false));

    return direct?.conversationId ?? null;
  }

  /**
   * Abre el chat DIRECT con el destinatario de un reenvío a un único destino.
   * Si ya existe la conversación localmente, navega ya. Si no, deja la selección
   * pendiente hasta que llegue chat:conversation:created / :new del reenvío.
   * NO emite creación: el reenvío (chat:message:forward) ya la dispara server-side.
   */
  navigateAfterForward(participantUserId: number): void {
    if (!Number.isFinite(participantUserId) || !participantUserId || participantUserId === this.currentUserId()) {
      return;
    }
    const existingId = this.findDirectConversationIdByParticipant(participantUserId);
    if (existingId) {
      this.selectConversation(existingId);
      return;
    }
    this.pendingForwardParticipantUserId = participantUserId;
  }

  /** Selects an existing DIRECT conversation or requests one from the backend. */
  selectOrCreateDirectConversation(participantUserId: number | null | undefined): void {
    if (!Number.isFinite(participantUserId) || !participantUserId || participantUserId === this.currentUserId()) {
      console.warn('[ChatState] selectOrCreateDirectConversation: invalid participantUserId', participantUserId);
      return;
    }

    if (!this.ensureSocketActionReady('selectOrCreateDirectConversation')) return;

    const existingId = this.findDirectConversationIdByParticipant(participantUserId);
    if (existingId) {
      this.selectConversation(existingId);
      return;
    }

    if (this.pendingCreateSelection && this.pendingDirectParticipantUserId === participantUserId) {
      return;
    }

    this.createDirectConversation(participantUserId);
  }

  /**
   * Crea un grupo nuevo. Solo deberían llamar este método los componentes
   * cuya UI ya validó {@code SessionCtl.canCreate()}. El backend igual
   * valida con su propio guard y responde con chat:error KLAB403 si el
   * usuario no tiene permisos.
   *
   * <p>El grupo nace con el creador más los {@code memberUserIds} elegidos
   * en el paso 2 del wizard (MOMSG-1116, opcional). Se pueden seguir
   * agregando miembros después via {@link addMembers}.
   */
  createGroupClientId(): string {
    return generateClientMessageId();
  }

  createGroupMutationId(): string {
    return generateClientMessageId();
  }

  createGroup(
    name: string,
    description?: string | null,
    avatar?: string | ChatAttachmentUpload | null,
    clientConversationId = generateClientMessageId(),
    sourceFile?: File,
    memberUserIds: number[] = [],
  ): void {
    const trimmed = (name ?? '').trim();
    if (!trimmed) {
      console.warn('[ChatState] createGroup: empty name');
      return;
    }
    if (!this.ensureSocketActionReady('createGroup')) return;
    const currentUserId = this.currentUserId();
    const avatarS3Key = typeof avatar === 'string' ? avatar : avatar?.reference ?? null;
    const avatarReceipt = typeof avatar === 'object' && avatar ? avatar.receipt : null;
    this.pendingCreateSelection = true;
    if (avatarReceipt) {
      this.rememberPendingMediaMutation({
        operation: 'GROUP_AVATAR_CREATE',
        correlationId: clientConversationId,
        file: sourceFile,
        canonicalReference: avatarS3Key ?? undefined,
      });
    }
    const participantUserIds = [...new Set([currentUserId, ...memberUserIds])].filter(
      (id) => Number.isFinite(id) && id > 0,
    );
    this.port.createGroupConversation({
      type: 'GROUP',
      clientConversationId,
      participantUserIds,
      groupInfo: {
        name: trimmed,
        description: description ?? null,
        avatarS3Key: avatarS3Key ?? null,
        avatarReceipt,
      },
    });
  }

  /**
   * Suma 1..N miembros al grupo indicado.
   *
   * <p>UI guard: solo deberían llamarlo componentes que validaron
   * permisos de actualización de grupos. El backend igual valida con guard
   * propio y emite chat:error KLAB403 si no procede.
   *
   * <p>El snapshot del grupo se actualiza cuando llega
   * chat:group:member:added — wireStreams hace el upsert con los nuevos
   * participantes.
   */
  addMembers(conversationId: string, userIds: number[]): void {
    if (!this.ensureSocketActionReady('addMembers')) return;
    const currentUserId = this.currentUserId();
    const actionableUserIds = [...new Set(userIds)].filter((userId) => Number.isFinite(userId) && userId > 0 && userId !== currentUserId);

    if (!conversationId || actionableUserIds.length === 0) {
      console.warn('[ChatState] addMembers: missing args', {
        conversationId,
        userIds,
      });
      return;
    }
    if (!this.canCurrentUserManageGroup(conversationId)) {
      console.warn('[ChatState] addMembers: current user is not a group admin', { conversationId });
      return;
    }
    this.applyOptimisticGroupMembersAdded(conversationId, actionableUserIds);
    this.port.addGroupMembers({ conversationId, userIds: actionableUserIds });
  }

  private readonly MAX_GROUP_MEMBER_REMOVE_BATCH = 100;

  /**
   * Elimina 1..100 miembros del grupo indicado en una sola operación atómica.
   *
   * <p>UI guard: solo deberían llamarlo componentes que validaron permisos de
   * actualización de grupos. El backend igual valida con guard propio y emite
   * chat:error KLAB403 si no procede.
   *
   * <p>Si cualquiera de los ids falla el guard por-permisos, se descarta TODA
   * la llamada (nada se envía ni se optimiza).
   */
  removeMembers(conversationId: string, userIds: number[]): void {
    if (!this.ensureSocketActionReady('removeMember')) return;
    if (!conversationId || !Array.isArray(userIds) || userIds.length === 0 || userIds.length > this.MAX_GROUP_MEMBER_REMOVE_BATCH) {
      console.warn('[ChatState] removeMembers: missing or invalid args', {
        conversationId,
        userIds,
      });
      return;
    }
    if (!this.canCurrentUserManageGroup(conversationId)) {
      console.warn('[ChatState] removeMembers: current user is not a group admin', { conversationId });
      return;
    }
    const sanitized = [...new Set(userIds)].filter((userId) => Number.isFinite(userId) && userId > 0);
    if (sanitized.length === 0 || sanitized.length > this.MAX_GROUP_MEMBER_REMOVE_BATCH) {
      console.warn('[ChatState] removeMembers: sanitized batch out of range', {
        conversationId,
        userIds,
        sanitizedLength: sanitized.length,
      });
      return;
    }
    for (const userId of sanitized) {
      if (!this.canCurrentUserRemoveGroupMember(conversationId, userId)) {
        console.warn('[ChatState] removeMembers: current user cannot remove a group member, aborting whole batch', {
          conversationId,
          userId,
        });
        return;
      }
    }
    this.applyOptimisticGroupMemberRemoved(conversationId, sanitized);
    this.port.removeGroupMember({ conversationId, userIds: sanitized });
  }

  removeMember(conversationId: string, userId: number): void {
    this.removeMembers(conversationId, [userId]);
  }

  canCurrentUserRemoveGroupMember(conversationId: string, userId: number): boolean {
    const snap = this.state().snapshots.get(conversationId);
    if (!snap || snap.type !== 'GROUP') return false;
    const currentUserId = this.currentUserId();
    const currentParticipant = (snap.participants ?? []).find(
      (participant) => participant.userId === currentUserId && participant.isActive === true,
    );
    const targetParticipant = (snap.participants ?? []).find(
      (participant) => participant.userId === userId && participant.isActive === true,
    );

    if (!currentParticipant || !targetParticipant) return false;
    if (currentParticipant.role !== 'ADMIN' && currentParticipant.role !== 'MODERATOR') return false;
    return currentParticipant.role !== 'MODERATOR' || targetParticipant.role !== 'ADMIN';
  }

  deleteGroup(conversationId: string): void {
    if (!this.ensureSocketActionReady('deleteGroup')) return;
    if (!conversationId) {
      console.warn('[ChatState] deleteGroup: missing conversationId', {
        conversationId,
      });
      return;
    }
    if (!this.canCurrentUserDeleteGroup(conversationId)) {
      console.warn('[ChatState] deleteGroup: current user is not a group admin', { conversationId });
      return;
    }
    this.applyOptimisticGroupDeleted(conversationId);
    this.port.deleteGroup({ conversationId });
  }

  /**
   * Renames a group conversation using the existing chat:group:update contract.
   * The server remains the authority: UI updates when chat:group:updated arrives.
   */
  updateGroupName(conversationId: string, name: string): void {
    this.updateGroup(conversationId, name);
  }

  /**
   * Updates group metadata through the existing chat:group:update contract.
   * Omitted avatar keeps the current avatar; explicit null clearing is not exposed by UI.
   */
  updateGroup(
    conversationId: string,
    name: string,
    avatar?: string | ChatAttachmentUpload | null,
    mutationId?: string,
    sourceFile?: File,
    onlyAdminsCanSend?: boolean,
  ): void {
    const trimmed = (name ?? '').trim();
    if (!conversationId || !trimmed) {
      console.warn('[ChatState] updateGroup: missing args', {
        conversationId,
        name,
      });
      return;
    }
    if (!this.ensureSocketActionReady('updateGroup')) return;
    if (!this.canCurrentUserAdminGroup(conversationId)) {
      console.warn('[ChatState] updateGroup: current user is not a group admin', { conversationId });
      return;
    }

    const groupInfo: MessagingSdk.GroupInfoInput = { name: trimmed };
    if (avatar !== undefined) {
      groupInfo.avatarS3Key = typeof avatar === 'string' ? avatar : avatar?.reference ?? null;
      groupInfo.avatarReceipt = typeof avatar === 'object' && avatar ? avatar.receipt : null;
    }
    if (onlyAdminsCanSend !== undefined) {
      groupInfo.onlyAdminsCanSend = onlyAdminsCanSend;
    }

    const groupBackupToken = this.applyOptimisticGroupUpdate(conversationId, groupInfo);
    const mediaMutationId = groupInfo.avatarReceipt
      ? mutationId ?? generateClientMessageId()
      : undefined;
    if (mediaMutationId) {
      this.rememberPendingMediaMutation({
        operation: 'GROUP_AVATAR_UPDATE',
        correlationId: mediaMutationId,
        conversationId,
        groupBackupToken,
        file: sourceFile,
        canonicalReference: groupInfo.avatarS3Key ?? undefined,
      });
    }
    this.port.updateGroup({
      conversationId,
      ...(mediaMutationId ? { mutationId: mediaMutationId } : {}),
      groupInfo,
    });
  }

  designateModerator(conversationId: string, userId: number): void {
    this.updateGroupMemberRole(conversationId, userId, 'MODERATOR');
  }

  unassignModerator(conversationId: string, userId: number): void {
    this.updateGroupMemberRole(conversationId, userId, 'MEMBER');
  }

  private updateGroupMemberRole(conversationId: string, userId: number, role: 'MEMBER' | 'MODERATOR'): void {
    if (!this.readySignal()) {
      console.warn('[ChatState] updateGroupMemberRole: chat state is not ready');
      return;
    }
    if (!conversationId || !Number.isFinite(userId) || userId <= 0) {
      console.warn('[ChatState] updateGroupMemberRole: missing args', { conversationId, userId, role });
      return;
    }
    if (!this.canCurrentUserDesignateModerator(conversationId)) {
      console.warn('[ChatState] updateGroupMemberRole: current user is not a group admin or moderator', { conversationId });
      return;
    }
    const target = this.state().snapshots.get(conversationId)?.participants?.find((participant) => participant.userId === userId);
    const expectedCurrentRole = role === 'MODERATOR' ? 'MEMBER' : 'MODERATOR';
    if (!target || target.isActive !== true || target.role !== expectedCurrentRole) {
      console.warn('[ChatState] updateGroupMemberRole: target role is not eligible', { conversationId, userId, role });
      return;
    }
    const commandId = crypto.randomUUID();
    const payload = { commandId, conversationId, userId, role };
    const token = this.applyOptimisticGroupMemberRoleUpdated(conversationId, userId, role, commandId);
    if (this.socketConnectedSignal()) {
      this.sendRoleCommand({ token, payload });
      return;
    }
    if (this.queuedRoleCommand) this.rollbackOptimisticGroupBackup(this.queuedRoleCommand.token);
    this.queuedRoleCommand = { token, payload };
  }

  /**
   * Sends a TEXT message in the currently selected conversation.
   * Renders an optimistic local copy immediately for UX, then awaits the
   * server's broadcast to update palomitas (status 'sent' → 'delivered').
   *
   * <p>Si se pasa {@code replyTo}, el mensaje se envía como respuesta y la
   * burbuja optimistic ya renderiza el quote. El server lo broadcastea en
   * chat:message:new con el mismo campo replyTo en el payload.
   */
  sendText(text: string, replyTo?: ChatMessageReplyUI | null, mentions: MessagingSdk.MessageMention[] = []): void {
    void this.sendTextAndWait(this.selectedIdSignal(), text, replyTo, mentions).catch((err) => console.error('[ChatState] sendTextMessage failed', err));
  }

  /**
   * Sends a TEXT message to a specific conversation without changing the
   * global dashboard selection — used by the in-app Toast quick-reply so
   * replying doesn't mark the conversation as read as an unwanted side effect.
   */
  sendTextToConversation(conversationId: string | null | undefined, text: string, replyTo?: ChatMessageReplyUI | null, mentions: MessagingSdk.MessageMention[] = []): void {
    void this.sendTextAndWait(conversationId ?? null, text, replyTo, mentions).catch((err) => console.error('[ChatState] sendTextMessage failed', err));
  }

  forwardMessages(
    sourceConversationId: string,
    messageIds: string[],
    destinations: MessagingSdk.ForwardDestinationPayload[],
  ): boolean {
    if (!sourceConversationId || messageIds.length === 0 || destinations.length === 0) return false;
    if (!this.ensureSocketActionReady('forwardMessages')) return false;
    const visibleMessageIds = [...new Set(messageIds)].filter((messageId) =>
      this.hasCurrentMessage(sourceConversationId, messageId),
    );
    if (visibleMessageIds.length === 0) return false;

    this.port.forwardMessages({
      clientForwardId: generateClientMessageId(),
      sourceConversationId,
      messageIds: visibleMessageIds,
      destinations,
    });
    return true;
  }

  private async sendTextAndWait(conversationId: string | null, text: string, replyTo?: ChatMessageReplyUI | null, mentions: MessagingSdk.MessageMention[] = []): Promise<boolean> {
    const leadingWhitespace = text.length - text.trimStart().length;
    const trimmed = text.trim();
    if (!conversationId || !trimmed) return false;
    if (!this.ensureSocketActionReady('sendText')) return false;

    const canonicalReplyTo = this.normalizeReplyToForSend(conversationId, replyTo);
    if (replyTo && !canonicalReplyTo) return false;

    this.sendTypingStop(conversationId);

    const userId = this.currentUserId();
    const clientMessageId = generateClientMessageId();
    const canonicalMentions = mentions
      .map((mention) => ({ ...mention, start: mention.start - leadingWhitespace }))
      .filter((mention) => mention.start >= 0);
    const optimistic = buildOptimisticTextMessage(conversationId, trimmed, userId, clientMessageId, canonicalReplyTo, canonicalMentions);
    const latestBackup = this.captureOptimisticLatestSnapshotBackup(conversationId);
    this.appendMessage(conversationId, optimistic);
    this.applyOptimisticLatestMessageToSnapshot(conversationId, optimistic, latestBackup);

    try {
      await lastValueFrom(
        this.port
          .sendTextMessage({
            conversationId,
            clientMessageId,
            type: 'TEXT',
            content: { text: trimmed, mentions: canonicalMentions },
            replyTo: canonicalReplyTo
              ? {
                  messageId: canonicalReplyTo.messageId,
                  senderId: Number(canonicalReplyTo.senderId),
                  contentPreview: canonicalReplyTo.contentPreview,
                }
              : null,
          })
          .pipe(takeUntil(this.destroy$), defaultIfEmpty(void 0)),
      );
    } catch (err) {
      this.removeMessageById(conversationId, optimistic.id);
      this.rollbackOptimisticLatestMessageSnapshot(conversationId, optimistic, latestBackup);
      throw err;
    }

    return true;
  }

  /**
   * Editar un mensaje propio. El backend valida que el sender sea el actor.
   * No hay optimistic update — esperamos chat:message:edited para reflejar
   * el cambio. Eso evita inconsistencias si el server rechaza por permisos
   * o porque el mensaje fue eliminado en el medio.
   */
  editMessage(messageId: string, newText: string, conversationId = this.selectedIdSignal(), mentions: MessagingSdk.MessageMention[] = []): void {
    const originalText = newText ?? '';
    const leadingWhitespace = originalText.length - originalText.trimStart().length;
    const trimmed = originalText.trim();
    if (!conversationId || !messageId || !trimmed) return;
    if (!this.ensureSocketActionReady('editMessage')) return;
    const editableMessageId = this.resolveConfirmedMessageId(conversationId, messageId);
    if (!editableMessageId) {
      console.warn('[ChatState] editMessage: optimistic id without canonical alias, ignored', messageId);
      return;
    }
    if (!this.hasCurrentMessage(conversationId, editableMessageId)) return;
    this.sendTypingStop(conversationId);
    const token = this.rememberPendingMessageMutation('chat:message:edit', conversationId, editableMessageId, undefined, trimmed);
    this.applyOptimisticEdit(conversationId, editableMessageId, trimmed);
    this.rememberPendingMessageMutationOptimisticState(token);
    try {
      this.port.editMessage({
        conversationId,
        messageId: editableMessageId,
        newText: trimmed,
        mentions: mentions
          .map((mention) => ({ ...mention, start: mention.start - leadingWhitespace }))
          .filter((mention) => mention.start >= 0),
      });
    } catch (err) {
      this.rollbackPendingMessageMutation(token, 'emit_failed');
      console.error('[ChatState] editMessage emit failed', { conversationId, messageId: editableMessageId, err });
    }
  }

  /**
   * Eliminar un mensaje propio para todos los participantes. No hay
   * optimistic update — esperamos chat:message:deleted.
   */
  deleteMessage(messageId: string, conversationId = this.selectedIdSignal()): void {
    if (!conversationId || !messageId) return;
    if (!this.ensureSocketActionReady('deleteMessage')) return;
    if (isLocalMessageId(messageId)) {
      // Borrar un optimistic local sin confirmar: lo quitamos del state
      // directamente; nunca llegó al server.
      this.state.update((s) => {
        const next = new Map(s.messages);
        const list = next.get(conversationId);
        if (!list) return s;
        this.revokeLocalObjectUrls(list.filter((m) => m.id === messageId));
        next.set(
          conversationId,
          list.filter((m) => m.id !== messageId),
        );
        return { ...s, messages: next };
      });
      return;
    }
    if (!this.hasCurrentMessage(conversationId, messageId)) return;
    const token = this.rememberPendingMessageMutation('chat:message:delete', conversationId, messageId);
    this.applyOptimisticDelete(conversationId, messageId);
    this.rememberPendingMessageMutationOptimisticState(token);
    try {
      this.port.deleteMessage({ conversationId, messageId });
    } catch (err) {
      this.rollbackPendingMessageMutation(token, 'emit_failed');
      console.error('[ChatState] deleteMessage emit failed', { conversationId, messageId, err });
    }
  }

  /**
   * Pin / unpin de un mensaje. Para v1, solo afecta el flag isPinned visual
   * en la burbuja. Sprint 2.1 agregará el banner sticky tipo WhatsApp con
   * preview y scroll-to-message.
   */
  togglePin(messageId: string, currentlyPinned: boolean, conversationId = this.selectedIdSignal()): void {
    if (!conversationId || !messageId) return;
    if (!this.ensureSocketActionReady('togglePin')) return;
    const pinnableMessageId = this.resolveConfirmedMessageId(conversationId, messageId);
    if (!pinnableMessageId) {
      console.warn('[ChatState] togglePin: optimistic id without canonical alias, ignored', messageId);
      return;
    }
    if (!this.hasCurrentMessage(conversationId, pinnableMessageId)) return;
    const action: PendingMessageMutationAction = currentlyPinned ? 'unpin' : 'pin';
    const token = this.rememberPendingMessageMutation('chat:message:pin', conversationId, pinnableMessageId, action);
    this.applyOptimisticPin(conversationId, pinnableMessageId, !currentlyPinned);
    this.rememberPendingMessageMutationOptimisticState(token);
    try {
      this.port.pinMessage({
        conversationId,
        messageId: pinnableMessageId,
        action,
      });
    } catch (err) {
      this.rollbackPendingMessageMutation(token, 'emit_failed');
      console.error('[ChatState] pinMessage emit failed', { conversationId, messageId: pinnableMessageId, err });
    }
  }

  /**
   * Pin / unpin de una conversación. Preferencia privada por participante:
   * solo actualiza el pinnedAt del participante propio en el snapshot, nunca
   * el de otros. Sync multi-dispositivo vía chat:conversation:pinned.
   */
  toggleConversationPin(conversationId: string, currentlyPinned: boolean): void {
    if (!conversationId) return;
    if (!this.ensureSocketActionReady('toggleConversationPin')) return;
    if (!this.state().snapshots.has(conversationId)) return;
    const action: MessagingSdk.PinAction = currentlyPinned ? 'unpin' : 'pin';
    const token = this.rememberPendingConversationPinMutation(conversationId, action);
    this.applyOptimisticConversationPin(conversationId, currentlyPinned ? null : new Date().toISOString());
    try {
      this.port.pinConversation({ conversationId, action });
    } catch (err) {
      this.rollbackPendingConversationPinMutation(token, 'emit_failed');
      console.error('[ChatState] pinConversation emit failed', { conversationId, err });
    }
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private requestDirectoryPage(query: string, page: number, generation: number): void {
    const current = this.directoryState();
    if (current.generation !== generation || current.query !== query || current.nextPage !== page) return;
    this.directoryState.set({ ...current, phase: 'loading', error: null });
    this.directoryRequest = this.getContactsPage(query, page).subscribe({
      next: (response) => {
        const active = this.directoryState();
        if (active.generation !== generation || active.query !== query || active.nextPage !== page) return;
        const mapped = this.reconcileDirectoryContacts(
          response?.content ?? [],
          page === 0 ? [] : active.contacts,
        );
        this.directoryRolesSignal.set(response?.roles ?? []);
        const contacts = appendDedupedDirectoryContacts(page === 0 ? [] : active.contacts, mapped);
        const totalPages = finiteNonNegativeInteger(response?.page?.totalPages);
        this.directoryState.set({
          phase: 'ready',
          query,
          contacts,
          nextPage: page + 1,
          totalPages,
          generation,
          error: null,
        });
        this.reenrichExistingReplySenderNames();
        this.refreshPresenceForRelevantUsers();
      },
      error: (error) => {
        const active = this.directoryState();
        if (active.generation !== generation || active.query !== query || active.nextPage !== page) return;
        this.directoryState.set({ ...active, phase: 'error', error });
        console.warn('[ChatState] directory page failed', { query, page, error });
      },
    });
  }

  private cancelDirectoryWork(): void {
    if (this.directorySearchTimer !== null) clearTimeout(this.directorySearchTimer);
    this.directorySearchTimer = null;
    this.directoryRequest?.unsubscribe();
    this.directoryRequest = null;
  }

  private rememberIdentity(
    contact: CanonicalIdentityEntry,
    identityVersion: string | null = null,
  ): CanonicalIdentityEntry | null {
    const userId = contact.userId;
    if (!isFinitePositiveUserId(userId)) return null;
    const existing = this.identityCache.get(userId);
    if (identityVersion && existing?.identityVersion && isStaleIdentityVersion(existing.identityVersion, identityVersion)) {
      console.warn('[ChatState] ignoring stale identity update', { userId, prev: existing.identityVersion, new: identityVersion });
      return null;
    }
    if (!identityVersion && existing?.source === 'versioned-authoritative') return existing;

    contact = {
      ...contact,
      identityVersion,
      source: identityVersion === null ? 'unversioned' : 'versioned-authoritative',
    };
    this.identityCache.set(userId, contact);
    this.identityRevisionSignal.update((revision) => revision + 1);
    return contact;
  }

  private reconcileDirectoryContacts(
    contacts: readonly PeopleSdk.ChatContactResponse[],
    existingContacts: readonly GlobalContactUI[],
  ): GlobalContactUI[] {
    const currentUserId = this.currentUserId();
    const currentEmployeeId = this.currentEmployeeId();
    return appendDedupedDirectoryContacts(existingContacts, contacts.map(mapDirectoryContactToGlobalContact))
      .slice(existingContacts.length)
      .filter((contact) => !isSelfContact(contact, currentUserId, currentEmployeeId))
      .map((contact) => {
        if (!isFinitePositiveUserId(contact.userId)) return contact;
        const identity = this.rememberIdentity(identityContact(contact.userId, contact.searchText, contact.fullName));
        return identity ? identityToGlobalContact(identity, contact) : contact;
      });
  }

  private rememberSnapshotIdentities<T extends { participants?: MessagingSdk.ParticipantSnapshot[] }>(snapshot: T): T {
    const participants = (snapshot.participants ?? []).map((participant) => {
      if (!isFinitePositiveUserId(participant.userId)) return participant;
      const identity = this.rememberIdentity(identityContact(
        participant.userId, participant.fullName, participant.displayName,
      ));
      return identity ? {
        ...participant,
        displayName: identity.displayName,
        fullName: identity.fullName,
      } : participant;
    });
    return { ...snapshot, participants } as T;
  }

  /** Patches live participant and directory projections without touching historical messages. */
  private handleParticipantIdentityUpdated(payload: MessagingSdk.ParticipantIdentityUpdatedPayload): void {
    if (!isFinitePositiveUserId(payload.userId) || !payload.identityVersion) return;
    const identity = this.rememberIdentity(
      identityContact(payload.userId, payload.fullName, payload.displayName),
      payload.identityVersion,
    );
    if (!identity) return;

    const employmentStatus = payload.employmentStatus;
    if (employmentStatus === 'INACTIVE') this.unavailablePresenceUserIds.add(payload.userId);
    else if (employmentStatus === 'ACTIVE') this.unavailablePresenceUserIds.delete(payload.userId);
    const current = this.state();
    const snapshots = new Map(current.snapshots);
    let changed = false;
    for (const [conversationId, snapshot] of snapshots) {
      if (!(snapshot.participants ?? []).some((participant) => participant.userId === payload.userId)) continue;
      snapshots.set(conversationId, {
        ...snapshot,
        participants: snapshot.participants.map((participant) => participant.userId === payload.userId
          ? {
              ...participant,
              employeeId: payload.employeeId,
              displayName: identity.displayName,
              fullName: identity.fullName,
              roleName: payload.roleName ?? participant.roleName,
              employmentStatus: employmentStatus ?? participant.employmentStatus,
              // An ACTIVE lifecycle patch preserves the prior offline state; a
              // presence query or reconnect remains authoritative for ONLINE.
              isOnline: employmentStatus === 'INACTIVE' ? false : participant.isOnline,
              // Legacy confirmedDeletedAt remains the DIRECT unavailable UI bridge.
              confirmedDeletedAt: employmentStatus === undefined
                ? participant.confirmedDeletedAt
                : snapshot.type === 'DIRECT' && employmentStatus === 'INACTIVE'
                  ? payload.occurredAt
                  : null,
            }
          : participant),
      });
      changed = true;
    }
    if (changed) this.state.set({ ...current, snapshots });

    const directory = this.directoryState();
    const contacts = directory.contacts.map((contact) => contact.userId === payload.userId
      ? identityToGlobalContact(identity, contact)
      : contact);
    if (contacts.some((contact, index) => contact !== directory.contacts[index])) {
      this.directoryState.set({ ...directory, contacts });
    }
    this.refreshPresenceForRelevantUsers();
  }

  private rememberMessageIdentity(message: ChatMessageUI): void {
    const sender = message.senderSnapshot;
    if (!sender || !isFinitePositiveUserId(sender.userId)) return;
    // Versioned SenderSnapshots carry an event-time displayName that is
    // authoritative like the live identity (participant displayName ??
    // canonical session identity). Legacy snapshots without displayName must never
    // overwrite it — otherwise a stale fullName would clobber the live
    // contact name. Render projections may overlay a newer canonical identity
    // without modifying this event-time snapshot.
    const snapshotDisplayName = (sender as { displayName?: unknown }).displayName;
    const displayName = typeof snapshotDisplayName === 'string' && snapshotDisplayName.trim().length > 0
      ? snapshotDisplayName
      : undefined;
    this.rememberIdentity(
      identityContact(
        sender.userId,
        sender.fullName,
        displayName,
      ),
    );
  }

  private rememberMessageIdentities(messages: readonly ChatMessageUI[]): void {
    for (const message of messages) this.rememberMessageIdentity(message);
  }

  private getHistoryState(conversationId: string): HistoryState {
    return (
      this.state().history.get(conversationId) ?? {
        hasMore: true,
        hasMoreAfter: false,
        nextBeforeId: null,
        nextAfterAnchorId: null,
        droppedBefore: false,
        droppedAfter: false,
        isLoading: false,
        requestKind: null,
        error: null,
      }
    );
  }

  private setHistoryState(
    conversationId: string,
    nextState: HistoryState,
  ): void {
    this.state.update((s) => {
      const history = new Map(s.history);
      history.set(conversationId, nextState);
      return { ...s, history };
    });
  }

  private prependHistoryMessages(conversationId: string, older: ChatMessageUI[]): void {
    if (older.length === 0) return;
    this.rememberMessageIdentities(older);
    this.state.update((s) => {
      const messages = new Map(s.messages);
      const current = messages.get(conversationId) ?? [];
      const ordered = orderMessages(dedupeMessages([...current, ...older]));
      const retained = this.retainHistoryWindow(ordered, 'older');
      messages.set(conversationId, this.enrichReplySenderNames(conversationId, retained, s));
      if (retained.length !== ordered.length) {
        const history = new Map(s.history);
        const currentHistory = this.getHistoryState(conversationId);
        history.set(conversationId, {
          ...currentHistory,
          hasMoreAfter: true,
          nextAfterAnchorId: retained.at(-1)?.id ?? null,
          droppedAfter: true,
        });
        return { ...s, messages, history };
      }
      return { ...s, messages };
    });
  }

  private fetchInitialConversations(forceTrustPinnedAtFor?: ReadonlySet<string>): void {
    if (this.conversationRefreshInFlight) return;
    const wasReady = this.readySignal();
    if (!wasReady) this.canCreateGroupsSignal.set(false);
    this.conversationRefreshInFlight = true;
    this.port
      .initSession()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.conversationRefreshInFlight = false;
        }),
      )
      .subscribe({
        next: (response) => {
          // Runtime compatibility with the previous adapter, which exposed the
          // conversations array directly. It never grants session capability.
          const isLegacyConversationArray = Array.isArray(response);
          const incomingSnapshots = isLegacyConversationArray ? response : response.conversations;
          const snapshots = incomingSnapshots.map((snapshot) => this.rememberSnapshotIdentities(snapshot));
          for (const snap of snapshots) {
            const current = this.state().snapshots.get(snap.conversationId);
            if (this.shouldAcceptSnapshot(current, snap) && this.visibilityEpochChanged(current, snap)) {
              this.purgeConversationVisibilityState(snap.conversationId);
            }
          }
          this.state.update((s) => {
            const isAuthoritativeBootstrap = !this.readySignal();
            const next = isAuthoritativeBootstrap ? new Map<string, MessagingSdk.ConversationSnapshot>() : new Map(s.snapshots);
            for (const snap of snapshots) {
              if (this.deletedConversationIds.has(snap.conversationId)) continue;
              const current = s.snapshots.get(snap.conversationId);
              if (!this.shouldAcceptSnapshot(current, snap)) continue;
              this.removedMembershipGenerations.delete(snap.conversationId);
              next.set(
                snap.conversationId,
                forceTrustPinnedAtFor?.has(snap.conversationId)
                  ? snap
                  : this.preserveInFlightPinnedAt(snap, current),
              );
            }
            const messages = new Map(s.messages);
            if (isAuthoritativeBootstrap) {
              for (const conversationId of Array.from(messages.keys())) {
                if (!next.has(conversationId)) messages.delete(conversationId);
              }
            }
            return { ...s, snapshots: next, messages };
          });
          this.lastConversationsSnapshotAppliedAtMs = Date.now();
          for (const snap of snapshots) {
            this.reconcileConfirmedReadFromSnapshot(snap);
            // MOMSG-1081: sin esto, readReceiptsByConversation queda vacío tras
            // reload/reconexión hasta que llegue un evento en vivo — "vistos por"
            // muestra "nadie lo vio" para lecturas ya persistidas en Mongo.
            this.mergeSnapshotReadReceipts(snap, snap.conversationId);
          }
          this.readySignal.set(true);
          this.canCreateGroupsSignal.set(
            !isLegacyConversationArray && response.canCreateGroups === true,
          );
          this.rehydrateInterruptedUploads();
          this.reconcilePendingGroupMedia(snapshots);
          this.refreshPresenceForRelevantUsers();
        },
        error: (err) => {
          console.error('[ChatState] initSession failed', err);
          if (!wasReady) {
            this.canCreateGroupsSignal.set(false);
            this.initStarted = false;
          }
        },
      });
  }

  /**
   * Reconstruye como mensajes los adjuntos cuya subida quedó cortada por una
   * recarga de la página. NO son reanudables: el navegador no puede persistir
   * el handle del `File`, así que los bytes murieron con la pestaña. Lo que se
   * recupera es la evidencia de que el archivo nunca se envió — antes
   * desaparecía sin dejar rastro en la conversación.
   *
   * Se ejecuta una sola vez por sesión: `fetchInitialConversations` también
   * corre en reconexiones, y ahí los placeholders ya están en la lista.
   */
  private rehydrateInterruptedUploads(): void {
    if (this.interruptedUploadsRehydrated) return;
    this.interruptedUploadsRehydrated = true;

    const userId = this.currentUserId();
    // Por savedAt: los placeholders se reconstruyen en el mismo orden en que el
    // usuario eligió los archivos, no en el orden en que quedaron guardados.
    const uploads = [...this.chatUploadManager.interruptedUploads()].sort((a, b) => a.savedAt - b.savedAt);
    for (const upload of uploads) {
      // El usuario pudo haber sido sacado de esa conversación mientras no
      // estaba: sin snapshot no hay dónde colgar el mensaje.
      if (!this.state().snapshots.has(upload.conversationId)) continue;

      const placeholder = buildOptimisticMediaMessage(
        upload.conversationId,
        { name: upload.fileName, type: upload.declaredMime, size: upload.fileSize },
        '',
        userId,
        upload.clientMessageId,
        null,
        undefined,
        upload.captionText,
        this.mediaUrlPolicy,
        // El progreso real sobrevive a la recarga: las partes que S3 ya confirmó
        // siguen ahí. Mostrar 0% seria mentir sobre lo que llegó a subirse.
        {
          clientFileId: upload.clientFileId,
          uploadState: 'interrupted',
          uploadProgress: upload.progress,
          mediaKind: upload.mediaKind,
          durationMs: upload.durationMs,
        },
        // Conserva el instante original de la subida en vez de la marca de
        // tiempo de la recarga.
        upload.savedAt,
        upload.album,
      );
      // Sin entrada en attachmentUploadContexts a propósito: no hay upload que
      // manejar, y su ausencia es justamente lo que mantiene a
      // retryAttachmentUpload como no-op para estos mensajes.
      this.appendMessage(upload.conversationId, placeholder);
    }
  }

  async sendAttachment(file: File, replyTo?: ChatMessageReplyUI | null, captionText?: string | null, options?: ChatAttachmentUploadOptions): Promise<boolean> {
    return this.sendAttachmentToConversation(this.selectedIdSignal(), file, replyTo, captionText, null, options);
  }

  /** Applies an identity-keyed optimistic toggle and awaits the durable Store ACK. */
  toggleMessageReaction(conversationId: string, messageId: string, emoji: string): boolean {
    const normalizedEmoji = emoji?.trim();
    if (!conversationId || !messageId || !normalizedEmoji) return false;
    if (!this.ensureSocketActionReady('toggleMessageReaction')) return false;
    const actorUserId = this.currentUserId();
    if (!actorUserId || isLocalMessageId(messageId) || !this.isMessageAccessible(conversationId, messageId)) return false;

    const message = this.state().messages.get(conversationId)?.find((candidate) => candidate.id === messageId);
    if (!message || message.isDeleted) return false;

    const key = messageReactionPendingKey(conversationId, messageId, actorUserId);
    const existingPending = this.pendingMessageReactions.get(key);
    const optimisticReactions = toggleActorReaction(message.reactions, actorUserId, normalizedEmoji);
    const desiredActorReaction = findActorReaction(optimisticReactions, actorUserId);

    if (existingPending) {
      existingPending.desiredActorReaction = cloneMessageReaction(desiredActorReaction);
      existingPending.hasQueuedIntent = true;
      this.renderPendingMessageReaction(existingPending);
      return true;
    }

    this.startMessageReactionCommand(
      key,
      conversationId,
      messageId,
      actorUserId,
      message.reactions,
      message.reactionsRevision,
      desiredActorReaction,
    );
    return true;
  }

  /** Sends an attachment to a specific conversation without changing the global dashboard selection. */
  async sendAttachmentToConversation(conversationId: string | null | undefined, file: File, replyTo?: ChatMessageReplyUI | null, captionText?: string | null, localObjectUrl?: string | null, options?: ChatAttachmentUploadOptions): Promise<boolean> {
    if (!conversationId || !file) return false;
    if (!this.ensureSocketActionReady('sendAttachment')) return false;

    const caption = captionText?.trim() ?? '';
    const canonicalReplyTo = this.normalizeReplyToForSend(conversationId, replyTo);
    if (replyTo && !canonicalReplyTo) return false;

    this.sendTypingStop(conversationId);

    const clientFileId = generateClientMessageId();
    const clientMessageId = generateClientMessageId();
    const mediaKind = options?.mediaKind
      ?? (file.type.startsWith('audio/') ? 'ATTACHED_AUDIO' as const : 'GENERAL' as const);
    const durationMs = mediaKind === 'VOICE_NOTE' ? options?.durationMs ?? null : null;
    const album = options?.album ?? null;
    const userId = this.currentUserId();
    // The message owns this URL independently from any composer draft URL.
    // ChatState revokes it only when the message stops referencing it.
    let messageObjectUrl = localObjectUrl ?? null;
    if (mediaKind === 'VOICE_NOTE') {
      try {
        messageObjectUrl = this.mediaUrlPolicy.createObjectUrl(file);
      } catch {
        messageObjectUrl = null;
      }
    }

    // Mensaje optimista visible desde el frame 1, mismo mecanismo ya probado
    // para texto (buildOptimisticTextMessage + appendMessage en
    // sendTextAndWait): nunca desaparece durante la subida, solo transiciona
    // de estado (uploadState) en el mismo `id` hasta que la reconciliación
    // por clientMessageId de appendMessage lo reemplace por el mensaje real
    // cuando llegue chat:message:new. Esto también resuelve el orden: el
    // mensaje ocupa su posición (orden de selección) desde ya, en la MISMA
    // lista que los mensajes confirmados — ya no hay un tray separado cuyo
    // orden de desaparición dependa de qué upload termina primero.
    const optimistic = buildOptimisticMediaMessage(
      conversationId,
      file,
      '',
      userId,
      clientMessageId,
      canonicalReplyTo,
      messageObjectUrl ?? undefined,
      caption || null,
      this.mediaUrlPolicy,
      {
        clientFileId,
        uploadState: 'uploading',
        uploadProgress: 0,
        mediaKind,
        durationMs,
      },
      undefined,
      album,
    );
    const latestBackup = this.captureOptimisticLatestSnapshotBackup(conversationId);
    this.appendMessage(conversationId, optimistic);
    this.applyOptimisticLatestMessageToSnapshot(conversationId, optimistic, latestBackup);

    const { accepted, rejected } = this.chatUploadManager.enqueue(conversationId, [{
      clientFileId,
      file,
      mediaKind,
      durationMs,
      // VOICE_NOTE playback belongs to the optimistic message, not the upload
      // manager, whose settle lifecycle can finish before the canonical echo.
      localObjectUrl: mediaKind === 'VOICE_NOTE' ? null : localObjectUrl ?? null,
      // Se persisten con la sesión para poder rehidratar el mensaje si la
      // página se recarga con la subida en vuelo.
      clientMessageId,
      captionText: caption || null,
      album,
    }]);

    if (rejected.length > 0 || accepted.length === 0) {
      this.removeMessageById(conversationId, optimistic.id);
      this.rollbackOptimisticLatestMessageSnapshot(conversationId, optimistic, latestBackup);
      return false;
    }

    const context: AttachmentUploadContext = {
      conversationId,
      clientFileId,
      clientMessageId,
      caption,
      canonicalReplyTo: canonicalReplyTo ?? null,
      latestBackup,
      album,
    };
    this.attachmentUploadContexts.set(optimistic.id, context);

    // La subida a S3 arranca ya (paralela, vía ChatUploadManager), pero el
    // sendFileMessage se encadena por conversación: no se emite hasta que
    // el archivo anterior de la cola haya terminado su propio envío (éxito
    // o error, nunca bloquea a los siguientes). Así el orden de mensajes
    // en el chat siempre respeta el orden en que el usuario seleccionó los
    // archivos, independientemente de cuál pese más o tarde más en subir.
    this.chainAttachmentSend(conversationId, () =>
      this.runAttachmentUploadAndSend(context, optimistic.id),
    );

    return true;
  }

  /** Reintenta el upload de un adjunto que quedó en uploadState 'failed', conservando el mismo mensaje/posición. */
  retryAttachmentUpload(conversationId: string, messageId: string): void {
    const context = this.attachmentUploadContexts.get(messageId);
    if (!context) return;
    this.patchLocalMessage(conversationId, messageId, { uploadState: 'uploading', uploadProgress: 0 });
    this.chatUploadManager.retry(context.clientFileId);
    this.chainAttachmentSend(conversationId, () =>
      this.runAttachmentUploadAndSend(context, messageId),
    );
  }

  /** Cancela un adjunto en curso o fallido. Si el mensaje todavía es puramente local (nunca se persistió), lo saca de la lista. */
  cancelAttachmentUpload(conversationId: string, messageId: string): void {
    // Soltar la correlación del mensaje, no la del contexto: el contexto se
    // borra apenas `sendFileMessage` resuelve, pero el mensaje sigue esperando
    // el eco del server, y ahí todavía se puede cancelar. Sin esto, un rechazo
    // (o el vencimiento de la ventana de reconciliación) que llega DESPUÉS de
    // cancelar dispara un aviso de "no se pudo guardar el archivo" por algo que
    // el usuario descartó a propósito.
    const localMessage = this.state().messages.get(conversationId)
      ?.find((message) => message.id === messageId);
    const pending = this.releasePendingMediaMutationOf(conversationId, messageId);

    const context = this.attachmentUploadContexts.get(messageId);
    if (context) {
      this.chatUploadManager.cancel(context.clientFileId);
      this.attachmentUploadContexts.delete(messageId);
    } else {
      // Un placeholder rehidratado no tiene contexto (no hay upload que
      // manejar), pero su clientFileId sigue identificando el registro que el
      // manager restauró: cancelarlo borra la burbuja del tray y su entrada en
      // localStorage, para que el placeholder no vuelva en la próxima recarga.
      const interrupted = this.state().messages.get(conversationId)
        ?.find((m) => m.id === messageId && m.uploadState === 'interrupted');
      if (interrupted?.clientFileId) {
        this.chatUploadManager.cancel(interrupted.clientFileId);
      }
    }
    if (messageId.startsWith('local-')) {
      this.removeMessageById(conversationId, messageId);
      if (localMessage) {
        this.rollbackOptimisticLatestMessageSnapshot(
          conversationId,
          localMessage,
          pending?.latestBackup ?? context?.latestBackup ?? null,
        );
      }
    }
  }

  /**
   * Descarta una subida identificándola por `clientFileId` en vez de por
   * mensaje. Es la puerta para superficies que solo conocen ese id — la bandeja
   * global de subidas, que lista adjuntos de conversaciones que no están
   * abiertas.
   *
   * Antes esas superficies llamaban `chatUploadManager.cancel()` directo, y eso
   * borraba la burbuja y el registro de localStorage pero dejaba el mensaje
   * local colgado en el hilo hasta la próxima recarga. Cancelar tiene que
   * dejar el mismo estado sin importar desde dónde se dispare.
   */
  discardUploadByClientFileId(clientFileId: string): void {
    this.chatUploadManager.cancel(clientFileId);
    for (const [conversationId, messages] of this.state().messages) {
      const local = messages.find(
        (m) => m.clientFileId === clientFileId && m.id.startsWith('local-'),
      );
      if (!local) continue;
      // Mismo motivo que en cancelAttachmentUpload: soltar la correlación para
      // que la reconciliación no reporte como fallido lo que se descartó.
      const pending = this.releasePendingMediaMutationOf(conversationId, local.id);
      const context = this.attachmentUploadContexts.get(local.id);
      this.attachmentUploadContexts.delete(local.id);
      this.removeMessageById(conversationId, local.id);
      this.rollbackOptimisticLatestMessageSnapshot(
        conversationId,
        local,
        pending?.latestBackup ?? context?.latestBackup ?? null,
      );
      return;
    }
  }

  /**
   * Espera a que ChatUploadManager termine (éxito o error) el upload de
   * `context.clientFileId`, reflejando el progreso en vivo sobre el mensaje
   * optimista (`messageId`, mismo id todo el ciclo), y recién entonces emite
   * `sendFileMessage`. Compartida entre el envío inicial y `retryAttachmentUpload`.
   */
  private async runAttachmentUploadAndSend(context: AttachmentUploadContext, messageId: string): Promise<void> {
    const { conversationId, clientFileId, clientMessageId, caption, canonicalReplyTo, album } = context;
    const progressTimer = setInterval(() => {
      const bubble = this.chatUploadManager.bubbleFor(clientFileId);
      if (!bubble || (bubble.state !== 'uploading' && bubble.state !== 'pending')) return;
      this.patchLocalMessage(conversationId, messageId, { uploadState: 'uploading', uploadProgress: bubble.progress });
    }, 200);

    try {
      await this.chatUploadManager.waitFor(clientFileId);
    } finally {
      clearInterval(progressTimer);
    }

    const bubble = this.chatUploadManager.bubbleFor(clientFileId);
    if (!bubble || bubble.state !== 'completed' || !bubble.reference || !bubble.receipt) {
      this.patchLocalMessage(conversationId, messageId, { uploadState: 'failed' });
      const failedMessage = this.state().messages.get(conversationId)
        ?.find((message) => message.id === messageId);
      if (failedMessage) {
        this.rollbackOptimisticLatestMessageSnapshot(conversationId, failedMessage, context.latestBackup);
      }
      return;
    }

    // Bytes ya en S3, falta la confirmación del server — la tarjeta muestra
    // "Finalizando..." en vez de saltar directo a completed (evita leer
    // "listo" antes de que el mensaje exista realmente en el servidor).
    this.patchLocalMessage(conversationId, messageId, { uploadState: 'finalizing' });

    const type: 'IMAGE' | 'FILE' = bubble.declaredMime.startsWith('image/') ? 'IMAGE' : 'FILE';
    const payload: MessagingSdk.SendFileMessagePayload = {
      conversationId,
      clientMessageId,
      type,
      content: {
        text: caption || null,
        s3Key: bubble.reference,
        mimeType: bubble.declaredMime,
        fileName: bubble.fileName,
        fileSize: bubble.size,
        reference: bubble.reference,
        mediaReceipt: bubble.receipt,
        actualMime: bubble.declaredMime,
        size: bubble.size,
        mediaKind: bubble.mediaKind,
        durationMs: bubble.durationMs ?? null,
      },
      replyTo: canonicalReplyTo
        ? {
            messageId: canonicalReplyTo.messageId,
            senderId: Number(canonicalReplyTo.senderId),
            contentPreview: canonicalReplyTo.contentPreview,
          }
        : null,
      album,
    };

    // Registrar la correlación ANTES de emitir. El servidor puede rechazar el
    // adjunto (chat:media:rejected, p.ej. MEDIA_RECEIPT_INVALID) y
    // `handleMediaRejected` busca el pendiente por `correlationId`: sin esta
    // entrada hacía `if (!pending) return` y el rechazo se descartaba en
    // silencio — la burbuja quedaba clavada en 100% para siempre y recién
    // desaparecía al recargar. El id tiene que ser `clientMessageId`, que es lo
    // que el server usa como correlationId (`clientMessageId ?? messageId`).
    //
    // El pendiente se limpia solo por los tres caminos que ya existían: llega
    // el mensaje real (chat:message:new), llega el rechazo, o vence la ventana
    // de reconciliación y se re-consulta el historial.
    this.rememberPendingMediaMutation({
      operation: 'MESSAGE_ATTACHMENT',
      correlationId: clientMessageId,
      conversationId,
      optimisticMessage: this.state().messages.get(conversationId)
        ?.find((message) => message.id === messageId),
      latestBackup: context.latestBackup,
    });

    try {
      await lastValueFrom(this.port.sendFileMessage(payload).pipe(takeUntil(this.destroy$), defaultIfEmpty(void 0)));
      // El mensaje real llega por chat:message:new y se reconcilia solo (por
      // clientMessageId, en appendMessage) — no hace falta marcar 'sent' acá.
      this.attachmentUploadContexts.delete(messageId);
    } catch (err) {
      console.error('[ChatState] sendFileMessage failed after upload', err);
      // El envío ni salió: el mensaje se marca fallido acá y ofrece reintentar.
      // Hay que soltar el pendiente o la ventana de reconciliación lo borraría
      // de la lista por no encontrarlo en el historial.
      const pending = this.takePendingMediaMutation(clientMessageId);
      this.patchLocalMessage(conversationId, messageId, { uploadState: 'failed' });
      const failedMessage = this.state().messages.get(conversationId)
        ?.find((message) => message.id === messageId);
      if (failedMessage) {
        this.rollbackOptimisticLatestMessageSnapshot(
          conversationId,
          failedMessage,
          pending?.latestBackup ?? context.latestBackup,
        );
      }
    }
  }

  /**
   * Suelta la correlación de media asociada a un mensaje optimista, si todavía
   * está pendiente. Se identifica por `clientMessageId`, que es exactamente lo
   * que el servidor devuelve como `correlationId` al rechazar un adjunto.
   */
  private releasePendingMediaMutationOf(conversationId: string, messageId: string): PendingMediaMutation | undefined {
    const message = this.state().messages.get(conversationId)
      ?.find((candidate) => candidate.id === messageId);
    if (message?.clientMessageId) {
      return this.takePendingMediaMutation(message.clientMessageId);
    }
    return undefined;
  }

  /** Actualiza campos puntuales de un mensaje ya presente en la lista, por id exacto, sin reordenar ni pasar por la reconciliación de appendMessage. Usado para progreso/estado de upload en vivo. */
  private patchLocalMessage(conversationId: string, messageId: string, patch: Partial<ChatMessageUI>): void {
    this.state.update((s) => {
      const list = s.messages.get(conversationId);
      if (!list) return s;
      const idx = list.findIndex((m) => m.id === messageId);
      if (idx === -1) return s;
      const nextList = [...list];
      nextList[idx] = { ...nextList[idx], ...patch };
      const next = new Map(s.messages);
      next.set(conversationId, nextList);
      return { ...s, messages: next };
    });
  }

  /**
   * Encadena `task` detrás del último envío pendiente de esa conversación,
   * garantizando orden estricto de sendFileMessage por conversación sin
   * bloquear otras conversaciones entre sí. `task` nunca debe rechazar
   * (los errores ya se capturan adentro); el catch de acá es una red de
   * seguridad para que un fallo inesperado no rompa la cola para los
   * archivos que quedan atrás.
   */
  private chainAttachmentSend(conversationId: string, task: () => Promise<void>): void {
    const previous = this.attachmentSendChains.get(conversationId) ?? Promise.resolve();
    const next = previous.then(task).catch((err) => {
      console.error('[ChatState] attachment send chain task failed unexpectedly', err);
    });
    this.attachmentSendChains.set(conversationId, next);
    void next.finally(() => {
      if (this.attachmentSendChains.get(conversationId) === next) {
        this.attachmentSendChains.delete(conversationId);
      }
    });
  }

  private wireStreams(): void {
    if (this.streamsWired) return;

    this.streamsWired = true;

    this.port
      .isConnected$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isConnected) => {
        const wasConnected = this.socketConnectedSignal();
        this.socketConnectedSignal.set(isConnected);
        if (isConnected) {
          this.startPresenceRepair();
          if (!wasConnected) this.refreshPresenceForRelevantUsers(true);
          this.flushQueuedRoleCommand();
          this.refreshConversationsAfterReconnect();
          if (!wasConnected) this.reconcilePendingMediaMessages();
          this.retryVisibleReads('reconnect');
        } else {
          this.canCreateGroupsSignal.set(false);
          this.stopPresenceRepair();
          this.lastPresenceQueryKey = null;
          this.latestPresenceQueryToken++;
          this.releaseReadCommandsForRetry();
        }
      });

    // chat:message:new
    this.port
      .onNewMessage$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload) => this.handleNewMessage(payload));

    this.port
      .onMediaRejected$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload) => this.handleMediaRejected(payload));

    // chat:conversation:new (user added to a new conversation)
    this.port
      .onConversationNew$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload) => this.handleConversationNew(payload.conversation));

    // chat:conversation:created (creator confirmation)
    this.port
      .onConversationCreated$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload) => this.handleConversationCreated(payload));

    // chat:message:delivered:update — participant delivery evidence for tick aggregation.
    this.port
      .onMessageDeliveredUpdate$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((u) => this.handleMessageDeliveredUpdate(u));

    // chat:message:read:update — palomitas → leídas when the available receipt data proves it.
    this.port
      .onMessageReadUpdate$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((u) => this.handleMessageReadUpdate(u));

    this.port
      .onMessageReadResult$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => this.handleMessageReadResult(result));

    // chat:presence:update — refresca el mapa de presencia. Los computeds
    // directContacts y currentGroupContacts aplican overlay con este mapa,
    // así que cuando alguien conecta/desconecta, todos los dots verdes/grises
    // se actualizan sin tocar el snapshot original del init.
    this.port
      .onPresenceUpdate$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => this.handlePresenceUpdate(p));

    // chat:typing:indicator — mantiene estado transient por conversación.
    this.port
      .onTypingIndicator$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => this.handleTypingIndicator(p));

    // chat:group:member:added — mergea los nuevos participantes al snapshot
    // del grupo (todos los que están en el grupo reciben el evento, incluido
    // el propio agregado). Si el snapshot aún no existe (caso "fui agregado
    // a un grupo que no conocía"), backend también emite chat:conversation:new
    // que upsertea el snapshot completo; este handler solo lo actualiza.
    this.port
      .onGroupMemberAdded$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => this.handleGroupMemberAdded(p));

    // chat:group:updated — mergea groupInfo parcial confirmado por Store.
    this.port
      .onGroupUpdated$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => this.handleGroupUpdated(p));

    // chat:message:forward:blocked — llega solo al socket propio cuando un
    // forward best-effort saltó uno o más grupos restringidos.
    this.port
      .onMessageForwardBlocked$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => this.handleMessageForwardBlocked(p));

    // chat:group:member:removed — current user leaves the conversation;
    // remaining participants get a member list update.
    this.port
      .onGroupMemberRemoved$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => this.handleGroupMemberRemoved(p));

    this.port
      .onGroupMemberRoleUpdated$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => this.handleGroupMemberRoleUpdated(p));

    this.port
      .onGroupMemberRoleUpdateResult$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => this.handleGroupMemberRoleUpdateResult(p));

    // chat:group:deleted — remove the group from all local views.
    this.port
      .onGroupDeleted$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => this.handleGroupDeleted(p));

    // chat:message:edited — reemplaza el texto del mensaje y marca isEdited.
    this.port
      .onMessageEdited$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => this.handleMessageEdited(p));

    // chat:message:deleted — marca el mensaje como eliminado (no lo quita
    // del array para mantener orden estable). El render usa isDeleted para
    // mostrar "Mensaje eliminado" en lugar del contenido.
    this.port
      .onMessageDeleted$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => this.handleMessageDeleted(p));

    // chat:message:pinned — toggle del flag isPinned en la burbuja.
    this.port
      .onMessagePinned$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => this.handleMessagePinned(p));

    this.port
      .onMessageReactionsChanged$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload) => this.handleMessageReactionsChanged(payload));

    // chat:conversation:pinned — sync multi-dispositivo del pin propio.
    this.port
      .onConversationPinned$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => this.handleConversationPinned(p));

    this.port
      .onParticipantIdentityUpdated$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload) => this.handleParticipantIdentityUpdated(payload));

    // chat:error
    this.port
      .onError$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((e) => this.handleServerError(e));
  }

  /** Claims actual on-screen read ownership for an independently rendered chat surface. */
  setReadVisibilityClaim(owner: string, conversationId: string | null): void {
    const previousConversationId = this.readVisibilityClaims.get(owner) ?? null;
    if (conversationId) this.readVisibilityClaims.set(owner, conversationId);
    else this.readVisibilityClaims.delete(owner);
    if (previousConversationId && !this.hasReadVisibilityClaim(previousConversationId)) {
      const state = this.readStates.get(previousConversationId);
      if (state?.retryTimer) clearTimeout(state.retryTimer);
      if (state) state.retryTimer = null;
    }
    if (conversationId && document.visibilityState === 'visible') this.markConversationAsRead(conversationId);
  }

  // MOMSG-1159: this client no longer sends its own presence lease renewal —
  // ws-ms's PresenceHandler.renewLocalSockets() renews every socket it holds
  // from the server side every 10s, independent of this tab's visibility/
  // throttling state, which is a more reliable source than a client-driven
  // heartbeat (background tabs get their setInterval throttled by the
  // browser). What remains here is presence *query* repair — resubscribing to
  // other users' presence-watch rooms after a reconnect or a backgrounded tab.
  private startPresenceRepair(): void {
    if (this.presenceReconciliationTimer === null) {
      this.scheduleNextPresenceReconciliation();
    }
    if (this.presenceRepairListenersStarted) return;
    document.addEventListener('visibilitychange', this.handlePresenceVisibilityChange);
    window.addEventListener('focus', this.handlePresenceFocus);
    this.presenceRepairListenersStarted = true;
  }

  private stopPresenceRepair(): void {
    if (this.presenceReconciliationTimer !== null) clearTimeout(this.presenceReconciliationTimer);
    this.presenceReconciliationTimer = null;
    if (!this.presenceRepairListenersStarted) return;
    document.removeEventListener('visibilitychange', this.handlePresenceVisibilityChange);
    window.removeEventListener('focus', this.handlePresenceFocus);
    this.presenceRepairListenersStarted = false;
  }

  private scheduleNextPresenceReconciliation(): void {
    const jitterMs = Math.floor(Math.random() * (PRESENCE_RECONCILIATION_JITTER_MS + 1));
    this.presenceReconciliationTimer = setTimeout(() => {
      this.presenceReconciliationTimer = null;
      if (document.visibilityState === 'visible') this.refreshPresenceForRelevantUsers(true);
      if (this.socketConnectedSignal()) this.scheduleNextPresenceReconciliation();
    }, PRESENCE_RECONCILIATION_INTERVAL_MS + jitterMs);
  }

  private readonly handlePresenceVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.repairPresenceNow();
      this.retryVisibleReads('visibility');
    }
  };

  private readonly handlePresenceFocus = (): void => {
    if (document.visibilityState === 'visible') {
      this.repairPresenceNow();
      this.retryVisibleReads('focus');
    }
  };

  private repairPresenceNow(): void {
    if (!this.socketConnectedSignal()) return;
    // MOMSG-1159: visibilitychange and focus commonly fire back-to-back when a
    // tab regains attention — without this guard both handlers call this method
    // in the same tick, doubling the presence-query traffic for no benefit.
    const now = Date.now();
    if (now - this.lastPresenceRepairAtMs < PRESENCE_REPAIR_MIN_INTERVAL_MS) return;
    this.lastPresenceRepairAtMs = now;
    this.refreshPresenceForRelevantUsers(true);
  }

  private handleConversationCreated(payload: MessagingSdk.ConversationCreatedPayload): void {
    const snap = payload.conversation;
    this.upsertSnapshot(snap);
    this.lastCreatedConversationSignal.set(snap);
    const correlationId = payload.clientConversationId ?? payload.requestId;
    if (correlationId) {
      const pending = this.pendingMediaMutations.get(correlationId);
      if (pending?.operation === 'GROUP_AVATAR_CREATE') {
        this.takePendingMediaMutation(correlationId);
      }
    }

    if (this.shouldSelectPendingCreatedConversation(snap)) {
      this.pendingCreateSelection = false;
      this.pendingDirectParticipantUserId = null;
      this.selectConversation(snap.conversationId);
    }
    this.resolvePendingForwardSelection(snap); // corre siempre, es no-op si no hay pendiente de reenvío
  }

  private handleConversationNew(snap: MessagingSdk.ConversationSnapshot): void {
    this.upsertSnapshot(snap);

    if (this.pendingDirectParticipantUserId && snap.type === 'DIRECT'
        && this.snapshotHasParticipant(snap, this.pendingDirectParticipantUserId)) {
      this.pendingCreateSelection = false;
      this.pendingDirectParticipantUserId = null;
      this.selectConversation(snap.conversationId);
    }

    this.resolvePendingForwardSelection(snap); // corre siempre, mismo motivo que en handleConversationCreated
  }

  /** Resuelve la selección pendiente de "abrir tras reenviar" si este snapshot coincide. */
  private resolvePendingForwardSelection(snap: MessagingSdk.ConversationSnapshot): void {
    const pending = this.pendingForwardParticipantUserId;
    if (!pending) return;
    if (snap.type !== 'DIRECT') return;
    if (!this.snapshotHasParticipant(snap, pending)) return;
    this.pendingForwardParticipantUserId = null;
    this.selectConversation(snap.conversationId);
  }

  private handleServerError(error: MessagingSdk.ErrorPayload): void {
    this.canCreateGroupsSignal.set(false);
    this.pendingCreateSelection = false;
    this.pendingDirectParticipantUserId = null;
    this.pendingForwardParticipantUserId = null;
    if (error.event?.startsWith('chat:group:')) {
      this.rollbackOptimisticGroupOperation(error.event);
    }
    this.lastErrorSignal.set(error);
    console.warn('[ChatState] server error', error);
  }

  private handleMediaRejected(payload: MessagingSdk.MediaMutationRejectedPayload): void {
    const pending = this.pendingMediaMutations.get(payload.correlationId);
    if (!pending || pending.operation !== payload.operation) return;

    this.takePendingMediaMutation(payload.correlationId);
    this.lastMediaRejectionSignal.set(payload);
    this.rollbackPendingMediaMutation(pending);
  }

  private rememberPendingMediaMutation(
    pending: Omit<PendingMediaMutation, 'timeout'>,
  ): void {
    const existing = this.pendingMediaMutations.get(pending.correlationId);
    if (existing) clearTimeout(existing.timeout);
    const timer = setTimeout(() => {
      const current = this.pendingMediaMutations.get(pending.correlationId);
      if (!current) return;
      if (current.operation === 'MESSAGE_ATTACHMENT') {
        void this.reconcilePendingMediaMessage(pending.correlationId);
        return;
      }
      this.failPendingMediaMutation(pending.correlationId);
    }, OPTIMISTIC_RECONCILIATION_WINDOW_MS);
    this.pendingMediaMutations.set(pending.correlationId, {
      ...pending,
      timeout: timer,
    });
  }

  private takePendingMediaMutation(correlationId: string): PendingMediaMutation | undefined {
    const pending = this.pendingMediaMutations.get(correlationId);
    if (!pending) return undefined;
    clearTimeout(pending.timeout);
    this.pendingMediaMutations.delete(correlationId);
    this.reconcilingMediaMutations.delete(correlationId);
    return pending;
  }

  private failPendingMediaMutation(correlationId: string): void {
    const pending = this.takePendingMediaMutation(correlationId);
    if (pending) this.rollbackPendingMediaMutation(pending);
  }

  private rollbackPendingMediaMutation(pending: PendingMediaMutation): void {
    this.rejectedMediaFileSignal.set(pending.file ?? null);
    if (pending.operation === 'MESSAGE_ATTACHMENT' && pending.conversationId && pending.optimisticMessage) {
      this.removeMessageById(pending.conversationId, pending.optimisticMessage.id);
      this.rollbackOptimisticLatestMessageSnapshot(
        pending.conversationId,
        pending.optimisticMessage,
        pending.latestBackup ?? null,
      );
      return;
    }
    if (pending.operation === 'GROUP_AVATAR_UPDATE' && pending.groupBackupToken !== undefined) {
      this.rollbackOptimisticGroupBackup(pending.groupBackupToken);
      return;
    }
    this.pendingCreateSelection = false;
  }

  private reconcilePendingMediaMessages(): void {
    for (const [correlationId, pending] of this.pendingMediaMutations) {
      if (pending.operation === 'MESSAGE_ATTACHMENT') {
        void this.reconcilePendingMediaMessage(correlationId);
      }
    }
  }

  private async reconcilePendingMediaMessage(correlationId: string): Promise<void> {
    const pending = this.pendingMediaMutations.get(correlationId);
    if (
      !pending?.conversationId ||
      pending.operation !== 'MESSAGE_ATTACHMENT' ||
      this.reconcilingMediaMutations.has(correlationId)
    ) return;
    this.reconcilingMediaMutations.add(correlationId);
    try {
      const response = await lastValueFrom(
        this.port.fetchMessages({
          conversationId: pending.conversationId,
          beforeId: null,
          limit: GROUP_STATUS_REFRESH_PAGE_SIZE,
        }).pipe(timeout(HISTORY_LOAD_TIMEOUT_MS), takeUntil(this.destroy$)),
      );
      if (!this.pendingMediaMutations.has(correlationId)) return;
      const canonical = response.success
        ? response.messages.find((message) => message.clientMessageId === correlationId)
        : undefined;
      if (!canonical || (canonical.type !== 'FILE' && canonical.type !== 'IMAGE')) {
        this.failPendingMediaMutation(correlationId);
        return;
      }
      this.takePendingMediaMutation(correlationId);
      const message = mapHistoryMessageToUi(canonical, this.currentUserId(), this.mediaUrlPolicy);
      this.applyCanonicalMessage(pending.conversationId, message, {
        messageId: canonical.messageId,
        type: canonical.type,
        content: canonical.content,
        sender: canonical.sender,
        sentAt: canonical.sentAt,
        album: canonical.album,
      }, message.isOwn);
    } catch {
      this.failPendingMediaMutation(correlationId);
    } finally {
      this.reconcilingMediaMutations.delete(correlationId);
    }
  }

  private reconcilePendingGroupMedia(
    snapshots: MessagingSdk.ConversationSnapshot[],
  ): void {
    for (const [correlationId, pending] of [...this.pendingMediaMutations]) {
      if (pending.operation === 'MESSAGE_ATTACHMENT') continue;
      const matchingSnapshot = snapshots.find((snapshot) =>
        snapshot.type === 'GROUP' &&
        snapshot.groupInfo?.avatarS3Key === pending.canonicalReference &&
        (pending.operation === 'GROUP_AVATAR_UPDATE'
          ? snapshot.conversationId === pending.conversationId
          : snapshot.groupInfo?.createdBy === this.currentUserId()),
      );
      if (!matchingSnapshot) {
        this.failPendingMediaMutation(correlationId);
        continue;
      }
      this.takePendingMediaMutation(correlationId);
      if (pending.groupBackupToken !== undefined) {
        this.clearOptimisticGroupBackup(pending.groupBackupToken);
      }
    }
  }

  private refreshConversationsAfterReconnect(): void {
    // MOMSG-953: única vía de reintento de chat:init en cada reconexión.
    // Antes gateaba por readySignal — si el chat:init inicial de init()
    // fallaba (timeout, blip transitorio), readySignal quedaba en false
    // para siempre y este reintento se autodesactivaba justo en el caso que
    // debía cubrir. Ahora solo se abstiene mientras el primer intento de
    // init() sigue en curso (initStarted=true, todavía sin asentar éxito ni
    // error) — así no compite con esa llamada directa en la primera
    // conexión (wireStreams() se suscribe a isConnected$ antes de esa
    // llamada, en el mismo tick). Una vez que ese intento terminó (éxito o
    // error, initStarted vuelve a false), cualquier reconexión posterior sí
    // reintenta acá.
    if (this.conversationRefreshInFlight) return;
    if (this.initStarted && !this.readySignal()) return;
    const now = Date.now();
    if (this.lastReconnectRefreshAt !== 0 && now - this.lastReconnectRefreshAt < 500) return;
    this.lastReconnectRefreshAt = now;
    this.fetchInitialConversations();
  }

  private snapshotHasParticipant(snap: MessagingSdk.ConversationSnapshot, participantUserId: number): boolean {
    return (snap.participants ?? []).some((participant) => participant.userId === participantUserId && participant.isActive !== false);
  }

  private shouldSelectPendingCreatedConversation(snap: MessagingSdk.ConversationSnapshot): boolean {
    if (!this.pendingCreateSelection) return false;

    const pendingDirectParticipantUserId = this.pendingDirectParticipantUserId;
    if (pendingDirectParticipantUserId) {
      return snap.type === 'DIRECT' && this.snapshotHasParticipant(snap, pendingDirectParticipantUserId);
    }

    return snap.type === 'GROUP';
  }

  private handlePresenceUpdate(payload: MessagingSdk.PresenceUpdatePayload): void {
    const relevantUserIds = this.currentRelevantPresenceUserIds();
    this.prunePresenceToRelevantUsers(relevantUserIds);
    if (!isFinitePositiveUserId(payload.userId) || !relevantUserIds.has(payload.userId)) return;
    if (!this.acceptPresenceEpoch(payload.epoch)) return;
    this.presenceSignal.update((prev) => {
      const incoming: UserPresence = {
        isOnline: payload.isOnline,
        lastSeen: payload.lastSeen ?? null,
        updatedAt: payload.updatedAt ?? payload.lastSeen ?? null,
        revision: payload.revision,
        epoch: payload.epoch,
      };
      const current = prev.get(payload.userId);
      if (current && isPresenceUpdateStale(current, incoming)) return prev;

      const next = new Map(prev);
      next.set(payload.userId, incoming);
      return next;
    });
  }

  private handleTypingIndicator(payload: MessagingSdk.TypingIndicatorPayload): void {
    const userId = payload.user?.userId;
    if (!payload.conversationId || !Number.isFinite(userId) || userId === this.currentUserId()) return;

    if (!payload.isTyping) {
      this.removeTypingUser(payload.conversationId, userId);
      return;
    }

    this.typingSignal.update((prev) => {
      const next = new Map(prev);
      const users = new Map(next.get(payload.conversationId) ?? []);
      users.set(userId, {
        userId,
        fullName: formatChatDisplayName({ fullName: payload.user.fullName, fallback: `Usuario ${userId}` }),
      });
      next.set(payload.conversationId, users);
      return next;
    });
    this.scheduleTypingFallback(payload.conversationId, userId);
  }

  private sendTypingStartIfNeeded(conversationId: string): void {
    if (!this.readySignal() || !this.socketConnectedSignal() || !this.canEmitConversationScopedEvent(conversationId)) return;
    const now = Date.now();
    const lastStartedAt = this.localTypingStartedAt.get(conversationId) ?? 0;
    if (now - lastStartedAt < TYPING_START_THROTTLE_MS) return;

    try {
      this.port.sendTypingStart({ conversationId });
      this.localTypingStartedAt.set(conversationId, now);
    } catch (err) {
      console.warn('[ChatState] sendTypingStart failed', err);
    }
  }

  private scheduleTypingStop(conversationId: string): void {
    this.clearLocalTypingStopTimer(conversationId);
    this.localTypingStopTimers.set(
      conversationId,
      setTimeout(() => this.sendTypingStop(conversationId), TYPING_STOP_DEBOUNCE_MS),
    );
  }

  private stopTypingForSelectedConversation(): void {
    const conversationId = this.selectedIdSignal();
    if (conversationId) this.sendTypingStop(conversationId);
  }

  private sendTypingStop(conversationId: string): void {
    this.clearLocalTypingStopTimer(conversationId);
    if (!this.localTypingStartedAt.has(conversationId)) return;
    if (!this.readySignal() || !this.socketConnectedSignal() || !this.canEmitConversationScopedEvent(conversationId)) {
      this.localTypingStartedAt.delete(conversationId);
      return;
    }

    try {
      this.port.sendTypingStop({ conversationId });
    } catch (err) {
      console.warn('[ChatState] sendTypingStop failed', err);
    } finally {
      this.localTypingStartedAt.delete(conversationId);
    }
  }

  private clearLocalTypingStopTimer(conversationId: string): void {
    const timer = this.localTypingStopTimers.get(conversationId);
    if (!timer) return;
    clearTimeout(timer);
    this.localTypingStopTimers.delete(conversationId);
  }

  private scheduleTypingFallback(conversationId: string, userId: number): void {
    const key = typingTimerKey(conversationId, userId);
    const previous = this.typingFallbackTimers.get(key);
    if (previous) clearTimeout(previous);

    this.typingFallbackTimers.set(
      key,
      setTimeout(() => this.removeTypingUser(conversationId, userId), TYPING_INDICATOR_TIMEOUT_MS),
    );
  }

  private removeTypingUser(conversationId: string, userId: number): void {
    const key = typingTimerKey(conversationId, userId);
    const timer = this.typingFallbackTimers.get(key);
    if (timer) clearTimeout(timer);
    this.typingFallbackTimers.delete(key);

    this.typingSignal.update((prev) => {
      const users = prev.get(conversationId);
      if (!users?.has(userId)) return prev;
      const next = new Map(prev);
      const nextUsers = new Map(users);
      nextUsers.delete(userId);
      if (nextUsers.size === 0) {
        next.delete(conversationId);
      } else {
        next.set(conversationId, nextUsers);
      }
      return next;
    });
  }

  private clearAllTypingState(): void {
    for (const timer of this.typingFallbackTimers.values()) clearTimeout(timer);
    for (const timer of this.localTypingStopTimers.values()) clearTimeout(timer);
    this.typingFallbackTimers.clear();
    this.localTypingStopTimers.clear();
    this.localTypingStartedAt.clear();
    this.typingSignal.set(new Map());
  }

  private queryPresenceForContacts(contacts: GlobalContactUI[]): void {
    this.queryPresenceForUserIds(
      contacts
        .filter((contact) => !isSelfContact(contact, this.currentUserId(), this.currentEmployeeId()))
        .map((contact) => contact.userId),
    );
  }

  private queryPresenceForUserIds(candidateUserIds: Array<number | null | undefined>, force = false): void {
    if (!this.socketConnectedSignal()) {
      console.warn('[ChatState] queryPresenceForUserIds: chat socket is not connected');
      return;
    }

    const currentUserId = this.currentUserId();
    const userIds = [
      ...new Set(
        candidateUserIds
          .filter(isFinitePositiveUserId),
      ),
    ].filter((userId) => userId !== currentUserId);

    // Do not emit an empty replacement query during bootstrap. Fence any
    // in-flight response and allow the same IDs to be queried again once they
    // become relevant after a clear/reset.
    if (userIds.length === 0) {
      this.lastPresenceQueryKey = null;
      this.latestPresenceQueryToken++;
      return;
    }

    const queryKey = userIds.join(',');
    if (!force && queryKey === this.lastPresenceQueryKey) return;
    this.lastPresenceQueryKey = queryKey;
    const queryToken = ++this.latestPresenceQueryToken;
    const queryRequestedAt = new Date().toISOString();
    const queryId = `${Date.now()}-${++this.presenceQuerySequence}`;
    const chunks = Array.from(
      { length: Math.ceil(userIds.length / PRESENCE_QUERY_BATCH_SIZE) },
      (_, index) => userIds.slice(index * PRESENCE_QUERY_BATCH_SIZE, (index + 1) * PRESENCE_QUERY_BATCH_SIZE),
    );

    from(chunks)
      .pipe(
        concatMap((chunk, index) => this.port.queryPresence({
          userIds: chunk,
          mode: index === 0 ? 'replace' : 'append',
          finalize: index === chunks.length - 1,
          queryId,
        }).pipe(
          // MOMSG-1159: retry only THIS chunk's request. Retrying the whole
          // from(chunks) → concatMap → scan chain would re-send chunks that
          // already succeeded (wasteful, and burns into the server's
          // presence-query rate limit) and reset scan's accumulator, so this
          // must wrap the individual request, not the assembled pipeline.
          retry({
            count: PRESENCE_QUERY_RETRY_COUNT,
            delay: (_err, retryCount) => timer(PRESENCE_QUERY_RETRY_DELAY_MS * retryCount),
          }),
        )),
        scan((all, statuses) => [...all, ...(statuses ?? [])], [] as MessagingSdk.PresenceUpdatePayload[]),
        // MOMSG-1159: a timed-out/failed query used to give up silently and wait
        // for the next periodic trigger (up to 50s) to self-correct. Retrying
        // per-chunk above already covers most cases; this is the final
        // fallback once retries are exhausted.
        takeUntil(this.destroy$),
        catchError((err) => {
          console.warn('[ChatState] queryPresence failed', err);
          this.lastPresenceQueryKey = null;
          return EMPTY;
        }),
      )
      .subscribe((statuses) => {
        if (queryToken !== this.latestPresenceQueryToken) return;
        const relevantUserIds = this.currentRelevantPresenceUserIds();
        this.presenceSignal.update((prev) => {
          const next = new Map(
            [...prev].filter(([userId]) => relevantUserIds.has(userId)),
          );
          for (const status of statuses ?? []) {
            if (!isFinitePositiveUserId(status.userId) || !relevantUserIds.has(status.userId)) continue;
            if (!this.acceptPresenceEpoch(status.epoch)) continue;
            const incoming: UserPresence = {
              isOnline: status.isOnline,
              lastSeen: status.lastSeen ?? null,
              updatedAt: status.updatedAt ?? queryRequestedAt,
              revision: status.revision,
              epoch: status.epoch,
            };
            const current = next.get(status.userId);
            if (!current || !isPresenceUpdateStale(current, incoming)) {
              next.set(status.userId, incoming);
            }
          }
          return next;
        });
      });
  }

  private acceptPresenceEpoch(epoch: string | undefined): boolean {
    if (!epoch) return true;
    if (epoch === this.presenceEpoch) return true;
    if (this.retiredPresenceEpochs.has(epoch)) return false;
    if (this.presenceEpoch) this.retiredPresenceEpochs.add(this.presenceEpoch);
    this.presenceEpoch = epoch;
    return true;
  }

  private refreshPresenceForRelevantUsers(force = false): void {
    const userIds = this.currentRelevantPresenceUserIds();
    this.prunePresenceToRelevantUsers(userIds);
    this.queryPresenceForUserIds(Array.from(userIds), force);
  }

  private currentRelevantPresenceUserIds(): Set<number> {
    const userIds = new Set<number>();
    for (const contact of this.directoryState().contacts) {
      if (isFinitePositiveUserId(contact.userId) && !isSelfContact(contact, this.currentUserId(), this.currentEmployeeId())) {
        userIds.add(contact.userId);
      }
    }
    for (const snapshot of this.state().snapshots.values()) {
      if (snapshot.type !== 'DIRECT') continue;
      for (const participant of snapshot.participants ?? []) {
        if (participant.isActive === false) continue;
        if (isFinitePositiveUserId(participant.userId) && participant.userId !== this.currentUserId()) {
          userIds.add(participant.userId);
        }
      }
    }
    const selectedConversationId = this.selectedIdSignal();
    const selectedSnapshot = selectedConversationId ? this.state().snapshots.get(selectedConversationId) : null;
    const snapshots = selectedSnapshot ? [selectedSnapshot] : [];
    for (const snap of snapshots) {
      for (const participant of snap.participants ?? []) {
        if (participant.isActive === false) continue;
        if (isFinitePositiveUserId(participant.userId) && participant.userId !== this.currentUserId()) {
          userIds.add(participant.userId);
        }
      }
    }
    for (const claim of this.presenceRelevanceClaims.values()) {
      for (const userId of claim) userIds.add(userId);
    }
    for (const unavailableUserId of this.unavailablePresenceUserIds) {
      userIds.delete(unavailableUserId);
    }
    return userIds;
  }

  private prunePresenceToRelevantUsers(relevantUserIds: ReadonlySet<number>): void {
    this.presenceSignal.update((prev) => {
      if ([...prev.keys()].every((userId) => relevantUserIds.has(userId))) return prev;
      return new Map([...prev].filter(([userId]) => relevantUserIds.has(userId)));
    });
  }

  private canEmitConversationScopedEvent(conversationId: string): boolean {
    const snap = this.state().snapshots.get(conversationId);
    if (!snap) return false;
    return this.snapshotHasParticipant(snap, this.currentUserId());
  }

  private rememberGroupBackup(
    conversationId: string,
    event: OptimisticGroupEvent,
    options: { roleTargetUserId?: number; role?: 'MEMBER' | 'MODERATOR'; commandId?: string; timeoutMs?: number } = {},
  ): number {
    const token = ++this.optimisticGroupTokenSeq;
    const backup: OptimisticGroupBackup = {
      token,
      event,
      conversationId,
      backup: this.state().snapshots.get(conversationId) ?? null,
      optimisticSnapshot: null,
      roleTargetUserId: options.roleTargetUserId,
      role: options.role,
      commandId: options.commandId,
    };
    if (options.timeoutMs !== undefined) {
      backup.timeout = setTimeout(() => this.rollbackOptimisticGroupBackup(token), options.timeoutMs);
    }
    this.optimisticGroupBackups.push(backup);
    return token;
  }

  private rememberOptimisticSnapshot(token: number, conversationId: string): void {
    const backup = this.optimisticGroupBackups.find((item) => item.token === token);
    if (!backup) return;
    backup.optimisticSnapshot = this.state().snapshots.get(conversationId) ?? null;
  }

  private applyOptimisticGroupUpdate(conversationId: string, groupInfo: MessagingSdk.GroupInfoInput): number {
    const token = this.rememberGroupBackup(conversationId, 'chat:group:update');
    this.state.update((s) => {
      const existing = s.snapshots.get(conversationId);
      if (!existing || existing.type !== 'GROUP') return s;
      const next = new Map(s.snapshots);
      next.set(conversationId, {
        ...existing,
        groupInfo: {
          ...(existing.groupInfo ?? { name: 'Grupo', description: null, avatarS3Key: null, onlyAdminsCanSend: false }),
          ...groupInfo,
        },
      });
      return { ...s, snapshots: next };
    });
    this.rememberOptimisticSnapshot(token, conversationId);
    return token;
  }

  private applyOptimisticGroupMembersAdded(conversationId: string, userIds: number[]): void {
    const token = this.rememberGroupBackup(conversationId, 'chat:group:member:add');
    this.state.update((s) => {
      const existing = s.snapshots.get(conversationId);
      if (!existing || existing.type !== 'GROUP') return s;
      const incoming = new Set(userIds);
      let changed = false;
      const participants = (existing.participants ?? []).map((participant) => {
        if (!incoming.has(participant.userId)) return participant;
        incoming.delete(participant.userId);
        changed = true;
        return { ...participant, isActive: true };
      });

      for (const userId of incoming) {
        const contact = this.directoryState().contacts.find((item) => item.userId === userId)
          ?? null;
        const identity = this.identityCache.get(userId);
        if (!contact && !identity) continue;
        changed = true;
        participants.push({
          userId,
          employeeId: contact?.employeeId ?? 0,
          displayName: identity?.displayName ?? contact?.fullName,
          fullName: identity?.fullName ?? contact?.fullName ?? 'Usuario',
          roleName: contact?.roleName ?? '',
          photoUrl: contact?.avatarUrl ?? null,
          role: 'MEMBER',
          isActive: true,
          isOnline: contact?.isOnline ?? false,
          lastSeen: null,
        });
      }

      if (!changed) return s;
      const next = new Map(s.snapshots);
      next.set(conversationId, { ...existing, participants });
      return { ...s, snapshots: next };
    });
    this.rememberOptimisticSnapshot(token, conversationId);
  }

  private applyOptimisticGroupMemberRemoved(conversationId: string, targetUserIds: number[]): void {
    const token = this.rememberGroupBackup(conversationId, 'chat:group:member:remove');
    const removedSet = new Set(targetUserIds);
    this.state.update((s) => {
      const existing = s.snapshots.get(conversationId);
      if (!existing || existing.type !== 'GROUP') return s;
      const participants = (existing.participants ?? []).map((participant) => (removedSet.has(participant.userId) ? { ...participant, isActive: false } : participant));
      const next = new Map(s.snapshots);
      next.set(conversationId, { ...existing, participants });
      return { ...s, snapshots: next };
    });
    this.rememberOptimisticSnapshot(token, conversationId);
  }

  private applyOptimisticGroupMemberRoleUpdated(conversationId: string, userId: number, role: 'MEMBER' | 'MODERATOR', commandId: string): number {
    const token = this.rememberGroupBackup(conversationId, 'chat:group:member:role:update', {
      roleTargetUserId: userId,
      role,
      commandId,
      timeoutMs: MUTATION_CONFIRMATION_TIMEOUT_MS,
    });
    this.state.update((s) => {
      const existing = s.snapshots.get(conversationId);
      if (!existing || existing.type !== 'GROUP') return s;
      const participants = (existing.participants ?? []).map((participant) => (
        participant.userId === userId ? { ...participant, role } : participant
      ));
      const next = new Map(s.snapshots);
      next.set(conversationId, { ...existing, participants });
      return { ...s, snapshots: next };
    });
    this.rememberOptimisticSnapshot(token, conversationId);
    return token;
  }

  private applyOptimisticGroupDeleted(conversationId: string): void {
    const token = this.rememberGroupBackup(conversationId, 'chat:group:delete');
    this.removeConversationLocally(conversationId);
    this.rememberOptimisticSnapshot(token, conversationId);
  }

  private rollbackOptimisticGroupOperation(event: string): void {
    const matching = this.optimisticGroupBackups.filter((backup) => backup.event === event);
    if (matching.length === 0) return;

    if (matching.length > 1) {
      this.fetchInitialConversations();
      this.clearOptimisticGroupBackupsForEvent(event);
      return;
    }

    const index = this.optimisticGroupBackups.findIndex((backup) => backup.token === matching[0].token);
    const [failed] = this.optimisticGroupBackups.splice(index, 1);
    this.restoreOptimisticGroupBackup(failed);
  }

  private rollbackOptimisticGroupBackup(token: number): void {
    const index = this.optimisticGroupBackups.findIndex((backup) => backup.token === token);
    if (index === -1) return;
    const [failed] = this.optimisticGroupBackups.splice(index, 1);
    if (this.queuedRoleCommand?.token === token) this.queuedRoleCommand = null;
    if (failed.event === 'chat:group:member:role:update') {
      if (failed.timeout) clearTimeout(failed.timeout);
      console.warn('[ChatState] group role update confirmation timed out; rolling back', {
        conversationId: failed.conversationId,
        targetUserId: failed.roleTargetUserId,
        role: failed.role,
      });
      this.restoreOptimisticGroupBackup(failed);
      return;
    }
    this.restoreOptimisticGroupBackup(failed);
  }

  private restoreOptimisticGroupBackup(failed: OptimisticGroupBackup): void {
    if (failed.timeout) clearTimeout(failed.timeout);
    this.state.update((s) => {
      const snapshots = new Map(s.snapshots);
      const currentSnapshot = snapshots.get(failed.conversationId) ?? null;
      if (currentSnapshot !== failed.optimisticSnapshot) {
        const reconciled = this.reconcileFailedOptimisticRoleBackup(failed, currentSnapshot);
        if (!reconciled) return s;
        snapshots.set(failed.conversationId, reconciled);
        return { ...s, snapshots };
      }
      if (failed.backup) snapshots.set(failed.conversationId, failed.backup);
      else snapshots.delete(failed.conversationId);
      return { ...s, snapshots };
    });
  }

  private reconcileFailedOptimisticRoleBackup(
    failed: OptimisticGroupBackup,
    currentSnapshot: MessagingSdk.ConversationSnapshot | null,
  ): MessagingSdk.ConversationSnapshot | null {
    if (!currentSnapshot || currentSnapshot.type !== 'GROUP') return null;
    if (!failed.backup || failed.backup.type !== 'GROUP') return null;
    if (failed.event === 'chat:group:update') {
      if (!failed.optimisticSnapshot || failed.optimisticSnapshot.type !== 'GROUP') return null;
      const currentInfo = currentSnapshot.groupInfo;
      const optimisticInfo = failed.optimisticSnapshot.groupInfo;
      const backupInfo = failed.backup.groupInfo;
      if (!currentInfo || !optimisticInfo || !backupInfo) return null;
      const groupInfo = { ...currentInfo };
      let changed = false;
      for (const key of ['name', 'description', 'avatarS3Key', 'onlyAdminsCanSend'] as const) {
        if (currentInfo[key] !== optimisticInfo[key]) continue;
        groupInfo[key] = backupInfo[key] as never;
        changed = true;
      }
      return changed ? { ...currentSnapshot, groupInfo } : null;
    }
    if (failed.event !== 'chat:group:member:role:update') return null;
    if (failed.roleTargetUserId === undefined || failed.role === undefined) return null;

    const backupParticipant = (failed.backup.participants ?? []).find((participant) => participant.userId === failed.roleTargetUserId);
    if (!backupParticipant) return null;

    let changed = false;
    const participants = (currentSnapshot.participants ?? []).map((participant) => {
      if (participant.userId !== failed.roleTargetUserId) return participant;
      if (participant.role !== failed.role) return participant;
      changed = true;
      return { ...participant, role: backupParticipant.role };
    });

    return changed ? { ...currentSnapshot, participants } : null;
  }

  private clearOptimisticGroupBackup(token: number): void {
    const index = this.optimisticGroupBackups.findIndex((backup) => backup.token === token);
    if (index === -1) return;
    const [backup] = this.optimisticGroupBackups.splice(index, 1);
    if (backup.timeout) clearTimeout(backup.timeout);
  }

  private clearOptimisticGroupBackups(
    conversationId: string,
    event?: OptimisticGroupEvent,
    excludedTokens = new Set<number>(),
  ): void {
    for (let index = this.optimisticGroupBackups.length - 1; index >= 0; index--) {
      const backup = this.optimisticGroupBackups[index];
      if (
        backup.conversationId === conversationId &&
        (!event || backup.event === event) &&
        !excludedTokens.has(backup.token)
      ) {
        if (backup.timeout) clearTimeout(backup.timeout);
        this.optimisticGroupBackups.splice(index, 1);
      }
    }
  }

  private clearOptimisticGroupRoleBackup(payload: MessagingSdk.GroupMemberRoleUpdatedPayload): void {
    const targetUserId = this.roleUpdateTargetUserId(payload);
    const role = this.normalizeGroupRole(payload.role);
    for (let index = this.optimisticGroupBackups.length - 1; index >= 0; index--) {
      const backup = this.optimisticGroupBackups[index];
      if (
        backup.event === 'chat:group:member:role:update'
        && backup.conversationId === payload.conversationId
        && backup.roleTargetUserId === targetUserId
        && backup.role === role
      ) {
        if (backup.timeout) clearTimeout(backup.timeout);
        this.optimisticGroupBackups.splice(index, 1);
      }
    }
  }

  private clearOptimisticGroupBackupsForEvent(event: string): void {
    for (let index = this.optimisticGroupBackups.length - 1; index >= 0; index--) {
      if (this.optimisticGroupBackups[index].event === event) {
        if (this.optimisticGroupBackups[index].timeout) clearTimeout(this.optimisticGroupBackups[index].timeout);
        this.optimisticGroupBackups.splice(index, 1);
      }
    }
  }

  private clearOptimisticGroupTimeouts(): void {
    for (const backup of this.optimisticGroupBackups) {
      if (backup.timeout) clearTimeout(backup.timeout);
    }
    this.optimisticGroupBackups.length = 0;
  }

  /**
   * Mergea los nuevos miembros al snapshot del grupo. Estrategia:
   *   - Mantenemos los participants existentes (que ya pueden tener flags
   *     como lastReadMessageId).
   *   - Añadimos los newMembers (deduplicando por userId).
   *
   * <p>Si el snapshot no existe, no ignoramos: usamos el snapshot completo
   * cuando viene en el payload o refrescamos la lista de conversaciones.
   */
  private handleGroupMemberAdded(payload: MessagingSdk.GroupMemberAddedPayload): void {
    this.clearOptimisticGroupBackups(payload.conversationId, 'chat:group:member:add');
    if (!this.state().snapshots.has(payload.conversationId)) {
      if (payload.conversation) {
        this.upsertSnapshot(payload.conversation);
        this.scheduleGroupStatusRefresh(payload.conversationId, true);
        return;
      }

      this.fetchInitialConversations();
      return;
    }

    this.state.update((s) => {
      const existing = s.snapshots.get(payload.conversationId);
      if (!existing) return s;

      const incomingMembers = (payload.newMembers ?? [])
        .flatMap((member) => this.rememberSnapshotIdentities({ participants: [member] }).participants);
      if (incomingMembers.length === 0) return s;

      const incomingByUserId = new Map(incomingMembers.map((p) => [p.userId, p]));
      const mergedParticipants = (existing.participants ?? []).map((current) => {
        const incoming = incomingByUserId.get(current.userId);
        if (!incoming) return current;
        incomingByUserId.delete(current.userId);
        return { ...current, ...incoming, isActive: true };
      });

      const additions = Array.from(incomingByUserId.values()).map((p) => ({ ...p, isActive: true }));

      const merged = {
        ...existing,
        participants: [...mergedParticipants, ...additions],
      };

      const next = new Map(s.snapshots);
      next.set(payload.conversationId, merged);
      return { ...s, snapshots: next };
    });
    this.scheduleGroupStatusRefresh(payload.conversationId, true);
  }

  /** Merges confirmed groupInfo changes into the source snapshot. */
  private handleGroupUpdated(payload: MessagingSdk.GroupUpdatedPayload): void {
    const pending = payload.mutationId
      ? this.pendingMediaMutations.get(payload.mutationId)
      : undefined;
    if (
      pending?.operation === 'GROUP_AVATAR_UPDATE' &&
      pending.conversationId === payload.conversationId
    ) {
      this.takePendingMediaMutation(payload.mutationId!);
      if (pending.groupBackupToken !== undefined) {
        this.clearOptimisticGroupBackup(pending.groupBackupToken);
      }
    } else if (!payload.mutationId) {
      const protectedTokens = new Set(
        [...this.pendingMediaMutations.values()]
          .filter((item) => item.operation === 'GROUP_AVATAR_UPDATE')
          .map((item) => item.groupBackupToken)
          .filter((token): token is number => token !== undefined),
      );
      this.clearOptimisticGroupBackups(
        payload.conversationId,
        'chat:group:update',
        protectedTokens,
      );
    }
    const sendModeChangeRef: { nextGroupInfo: MessagingSdk.GroupInfo | null } = { nextGroupInfo: null };
    this.state.update((s) => {
      const existing = s.snapshots.get(payload.conversationId);
      if (!existing || existing.type !== 'GROUP') return s;

      const currentGroupInfo = existing.groupInfo ?? {
        name: 'Grupo',
        description: null,
        avatarS3Key: null,
        onlyAdminsCanSend: false,
      };

      const nextGroupInfo: MessagingSdk.GroupInfo = { ...currentGroupInfo };
      if (Object.prototype.hasOwnProperty.call(payload.groupInfo, 'name') && payload.groupInfo.name !== null && payload.groupInfo.name !== undefined) {
        nextGroupInfo.name = payload.groupInfo.name;
      }
      if (Object.prototype.hasOwnProperty.call(payload.groupInfo, 'description')) {
        nextGroupInfo.description = payload.groupInfo.description;
      }
      if (Object.prototype.hasOwnProperty.call(payload.groupInfo, 'avatarS3Key')) {
        nextGroupInfo.avatarS3Key = payload.groupInfo.avatarS3Key;
      }
      if (Object.prototype.hasOwnProperty.call(payload.groupInfo, 'onlyAdminsCanSend') && payload.groupInfo.onlyAdminsCanSend !== null && payload.groupInfo.onlyAdminsCanSend !== undefined) {
        nextGroupInfo.onlyAdminsCanSend = payload.groupInfo.onlyAdminsCanSend;
      }

      if ((currentGroupInfo.onlyAdminsCanSend ?? false) !== (nextGroupInfo.onlyAdminsCanSend ?? false)) {
        sendModeChangeRef.nextGroupInfo = nextGroupInfo;
      }

      const next = new Map(s.snapshots);
      next.set(payload.conversationId, {
        ...existing,
        groupInfo: nextGroupInfo,
      });
      return { ...s, snapshots: next };
    });
    if (sendModeChangeRef.nextGroupInfo) this.emitGroupSendModeNoticeIfNotActor(payload, sendModeChangeRef.nextGroupInfo);
    this.groupUpdated$.next(payload.conversationId);
  }

  /**
   * Construye y emite el toast de chat:message:forward:blocked: informa al
   * propio usuario qué grupo(s) restringido(s) se saltearon en un forward
   * best-effort. El resto de los destinos ya recibió el mensaje.
   */
  private handleMessageForwardBlocked(payload: MessagingSdk.MessageForwardBlockedPayload): void {
    const names = (payload.blockedConversations ?? []).map((c) => c.groupName ?? 'un grupo');
    if (names.length === 0) return;

    const text = names.length === 1
      ? `No se pudo reenviar a ${names[0]}: solo administradores pueden enviar mensajes ahí`
      : `No se pudo reenviar a ${names.join(', ')}: solo administradores pueden enviar mensajes ahí`;

    this.forwardBlockedNotice$.next({ text });
  }

  /**
   * Emite el toast de aviso de cambio de modo "solo admins" (MOMSG-1053).
   * A diferencia de {@link emitGroupRoleNoticeIfCurrentUser} (que solo
   * notifica al usuario afectado por un cambio de rol), este notifica a
   * TODOS los que reciben el chat:group:updated — salvo al propio autor
   * del cambio, que no necesita ver su propia acción confirmada.
   */
  private emitGroupSendModeNoticeIfNotActor(
    payload: MessagingSdk.GroupUpdatedPayload,
    nextGroupInfo: MessagingSdk.GroupInfo,
  ): void {
    if (payload.updatedBy?.userId === this.currentUserId()) return;

    const dedupeKey = this.groupSendModeNoticeDedupeKey(payload, nextGroupInfo.onlyAdminsCanSend === true);
    this.pruneGroupSendModeNoticeDedupe();
    if (this.groupSendModeNoticeDedupe.has(dedupeKey)) return;
    this.groupSendModeNoticeDedupe.set(dedupeKey, Date.now());

    const groupName = nextGroupInfo.name ?? 'este grupo';
    const text = nextGroupInfo.onlyAdminsCanSend
      ? `Ahora solo administradores pueden enviar mensajes en ${groupName}`
      : `Ahora todos pueden enviar mensajes en ${groupName}`;

    this.groupSendModeNotice$.next({
      conversationId: payload.conversationId,
      text,
    });
  }

  private groupSendModeNoticeDedupeKey(payload: MessagingSdk.GroupUpdatedPayload, onlyAdminsCanSend: boolean): string {
    return [
      payload.conversationId,
      onlyAdminsCanSend,
      payload.updatedAt ?? '',
      positiveIntegerFromUnknown(payload.updatedBy?.userId) ?? payload.updatedBy?.userId ?? '',
    ].join('|');
  }

  private clearGroupSendModeNoticeDedupe(): void {
    this.groupSendModeNoticeDedupe.clear();
  }

  private pruneGroupSendModeNoticeDedupe(): void {
    const cutoff = Date.now() - GROUP_ROLE_NOTICE_DEDUPE_WINDOW_MS;
    for (const [key, emittedAt] of this.groupSendModeNoticeDedupe) {
      if (emittedAt < cutoff) this.groupSendModeNoticeDedupe.delete(key);
    }
  }

  private handleGroupMemberRemoved(payload: MessagingSdk.GroupMemberRemovedPayload): void {
    this.clearOptimisticGroupBackups(payload.conversationId, 'chat:group:member:remove');
    const removedUserId = payload.removedUserId ?? payload.removedMember?.userId;
    if (!Number.isFinite(removedUserId)) return;

    if (removedUserId === this.currentUserId()) {
      const currentGeneration = this.state().snapshots.get(payload.conversationId)?.visibility?.generation ?? 0;
      const removedGeneration = payload.membershipGeneration ?? currentGeneration;
      if (currentGeneration > removedGeneration) return;
      this.removedMembershipGenerations.set(
        payload.conversationId,
        Math.max(this.removedMembershipGenerations.get(payload.conversationId) ?? 0, removedGeneration),
      );
      this.removeConversationLocally(payload.conversationId);
      this.groupMemberRemoved$.next({
        conversationId: payload.conversationId,
        removedUserId,
      });
      return;
    }

    if (payload.conversation) {
      this.upsertSnapshot(payload.conversation);
      this.scheduleGroupStatusRefresh(payload.conversationId, true);
      this.groupMemberRemoved$.next({
        conversationId: payload.conversationId,
        removedUserId,
      });
      return;
    }

    this.state.update((s) => {
      const existing = s.snapshots.get(payload.conversationId);
      if (!existing || existing.type !== 'GROUP') return s;

      const nextParticipants = (existing.participants ?? []).map((p) => (p.userId === removedUserId ? { ...p, isActive: false } : p));

      const next = new Map(s.snapshots);
      next.set(payload.conversationId, {
        ...existing,
        participants: nextParticipants,
      });
      return { ...s, snapshots: next };
    });
    this.scheduleGroupStatusRefresh(payload.conversationId, true);
    this.groupMemberRemoved$.next({
      conversationId: payload.conversationId,
      removedUserId,
    });
  }

  private handleGroupDeleted(payload: MessagingSdk.GroupDeletedPayload): void {
    this.clearOptimisticGroupBackups(payload.conversationId, 'chat:group:delete');
    this.removeConversationLocally(payload.conversationId);
    this.deletedConversationIds.add(payload.conversationId);
    this.groupDeleted$.next(payload.conversationId);
  }

  /**
   * Reemplaza el contenido del mensaje y marca isEdited=true. No tocamos
   * timestamp original — la spec de UI muestra "(editado)" junto a la hora
   * original.
   */
  private handleMessageEdited(payload: MessagingSdk.MessageEditedPayload): void {
    const newText = this.editedTextFromPayload(payload);
    const targetMessageId = this.resolveEditedMessageTargetId(payload, newText);
    if (!this.acceptMutationTimestamp(
      this.messageMutationOccurredAt,
      `edit|${payload.conversationId}|${targetMessageId}`,
      payload.editedAt,
    )) return;
    const isStaleLocalConfirmation = this.isStaleLocalMessageMutationConfirmation('chat:message:edit', payload.conversationId, targetMessageId, {
      newText,
      actorUserId: payload.editedBy?.userId,
      occurredAt: payload.editedAt,
    });
    this.confirmPendingMessageMutation('chat:message:edit', payload.conversationId, targetMessageId, { newText });
    if (isStaleLocalConfirmation) return;
    this.updateMessageInPlace(payload.conversationId, targetMessageId, (m) => ({
      ...m,
      content: newText,
      mentions: payload.mentions ?? payload.content?.mentions ?? null,
      isEdited: true,
    }));
    this.updateEditedMessageDerivedPreviews(payload.conversationId, targetMessageId, payload.messageId, newText);
  }

  private handleGroupMemberRoleUpdated(payload: MessagingSdk.GroupMemberRoleUpdatedPayload): void {
    const targetUserId = this.roleUpdateTargetUserId(payload);
    const existing = this.state().snapshots.get(payload.conversationId);
    if (targetUserId === this.currentUserId()) {
      // targetMembershipGeneration is authoritative; membershipGeneration is the rolling-deploy fallback.
      const targetGeneration = payload.targetMembershipGeneration ?? payload.membershipGeneration;
      const removedGeneration = this.removedMembershipGenerations.get(payload.conversationId);
      if (removedGeneration !== undefined && (targetGeneration === undefined || targetGeneration <= removedGeneration)) return;
      if (existing) {
        const currentGeneration = existing.visibility?.generation ?? 0;
        if (targetGeneration !== undefined && targetGeneration !== currentGeneration) return;
      }
    }
    if (!this.acceptMutationTimestamp(
      this.roleMutationOccurredAt,
      `${payload.conversationId}|${targetUserId}`,
      payload.updatedAt,
    )) return;
    this.clearOptimisticGroupRoleBackup(payload);
    this.patchGroupMemberRole(payload);
    this.emitGroupRoleNoticeIfCurrentUser(payload);
    if (!existing && targetUserId === this.currentUserId()) this.fetchInitialConversations();
  }

  private handleGroupMemberRoleUpdateResult(payload: MessagingSdk.GroupMemberRoleUpdateResultPayload): void {
    const index = this.optimisticGroupBackups.findIndex((backup) =>
      backup.event === 'chat:group:member:role:update' && backup.commandId === payload.commandId,
    );
    if (index === -1) return;
    const [backup] = this.optimisticGroupBackups.splice(index, 1);
    if (this.queuedRoleCommand?.payload.commandId === payload.commandId) this.queuedRoleCommand = null;
    if (backup.timeout) clearTimeout(backup.timeout);
    if (payload.status === 'REJECTED') this.restoreOptimisticGroupBackup(backup);
  }

  private patchGroupMemberRole(payload: MessagingSdk.GroupMemberRoleUpdatedPayload): void {
    const targetUserId = this.roleUpdateTargetUserId(payload);
    const role = this.normalizeGroupRole(payload.role);
    if (!Number.isFinite(targetUserId) || !role) {
      console.warn('[ChatState] unusable group role update payload', {
        conversationId: payload.conversationId,
        targetUserId: payload.targetUserId,
        updatedMemberUserId: payload.updatedMember?.userId,
        role: payload.role,
      });
      return;
    }

    this.state.update((s) => {
      const existing = s.snapshots.get(payload.conversationId);
      if (!existing || existing.type !== 'GROUP') return s;
      const participants = (existing.participants ?? []).map((participant) =>
        participant.userId === targetUserId
          ? { ...participant, ...payload.updatedMember, userId: targetUserId, role }
          : participant,
      );
      const next = new Map(s.snapshots);
      next.set(payload.conversationId, { ...existing, participants });
      return { ...s, snapshots: next };
    });
  }

  private emitGroupRoleNoticeIfCurrentUser(payload: MessagingSdk.GroupMemberRoleUpdatedPayload): void {
    const targetUserId = this.roleUpdateTargetUserId(payload);
    const role = this.normalizeGroupRole(payload.role);
    if (targetUserId !== this.currentUserId()) return;
    if (!role) return;

    const actionText = role === 'MODERATOR'
      ? 'Has sido asignado admin de'
      : 'Has dejado de ser admin de';

    const dedupeKey = this.groupRoleNoticeDedupeKey(payload, targetUserId);
    this.pruneGroupRoleNoticeDedupe();
    if (this.groupRoleNoticeDedupe.has(dedupeKey)) return;
    this.groupRoleNoticeDedupe.set(dedupeKey, Date.now());

    const actorName = formatChatDisplayName({ fullName: payload.updatedBy?.fullName, fallback: 'Alguien' });
    const groupName = this.resolveGroupRoleNoticeGroupName(payload);
    this.groupRoleNotice$.next({
      conversationId: payload.conversationId,
      targetUserId,
      role,
      text: `${actionText} ${groupName} por ${actorName}`,
    });
  }

  private resolveGroupRoleNoticeGroupName(payload: MessagingSdk.GroupMemberRoleUpdatedPayload): string {
    const candidates = [
      payload.groupName,
      payload.conversation?.groupInfo?.name,
      this.state().snapshots.get(payload.conversationId)?.groupInfo?.name,
    ];
    return candidates.find((candidate) => typeof candidate === 'string' && candidate.trim().length > 0)?.trim() ?? 'este grupo';
  }

  private roleUpdateTargetUserId(payload: MessagingSdk.GroupMemberRoleUpdatedPayload): number {
    return positiveIntegerFromUnknown(payload.targetUserId) ?? positiveIntegerFromUnknown(payload.updatedMember?.userId) ?? Number.NaN;
  }

  private normalizeGroupRole(role: unknown): 'MEMBER' | 'MODERATOR' | null {
    const normalized = typeof role === 'string' ? role.trim().toUpperCase() : '';
    return normalized === 'MEMBER' || normalized === 'MODERATOR' ? normalized : null;
  }

  private groupRoleNoticeDedupeKey(payload: MessagingSdk.GroupMemberRoleUpdatedPayload, targetUserId: number): string {
    return [
      payload.conversationId,
      targetUserId,
      this.normalizeGroupRole(payload.role) ?? payload.role,
      payload.updatedAt ?? '',
      positiveIntegerFromUnknown(payload.updatedBy?.userId) ?? payload.updatedBy?.userId ?? '',
    ].join('|');
  }

  private clearGroupRoleNoticeDedupe(): void {
    this.groupRoleNoticeDedupe.clear();
  }

  private pruneGroupRoleNoticeDedupe(): void {
    const cutoff = Date.now() - GROUP_ROLE_NOTICE_DEDUPE_WINDOW_MS;
    for (const [key, emittedAt] of this.groupRoleNoticeDedupe) {
      if (emittedAt < cutoff) this.groupRoleNoticeDedupe.delete(key);
    }
  }

  /**
   * Marca isDeleted=true y vacía el contenido para que la UI muestre el
   * placeholder. Mantenemos el mensaje en el array (no remove) para
   * preservar el orden y los IDs estables que usa el dedupe.
   */
  private handleMessageDeleted(payload: MessagingSdk.MessageDeletedPayload): void {
    this.clearPendingMessageReactionsForMessage(payload.conversationId, payload.messageId);
    this.confirmPendingMessageMutation('chat:message:delete', payload.conversationId, payload.messageId);
    this.updateMessageInPlace(payload.conversationId, payload.messageId, (m) => ({
      ...m,
      content: '',
      isDeleted: true,
      isEdited: false,
      // Un mensaje borrado no debería seguir mostrando "pinned" ni quote.
      isPinned: false,
      replyTo: null,
      reactions: [],
    }));
    this.updateReplyPreviews(payload.conversationId, payload.messageId, 'Mensaje eliminado');
    this.patchLastMessagePreview(payload.conversationId, payload.messageId, 'Mensaje eliminado');
    this.removePinnedSnapshot(payload.conversationId, payload.messageId);
    this.removeConversationFile(payload.conversationId, payload.messageId);
  }

  /**
   * Pin/unpin: actualiza el flag de la burbuja según el action del payload.
   */
  private handleMessagePinned(payload: MessagingSdk.MessagePinnedPayload): void {
    const isStaleLocalConfirmation = this.isStaleLocalMessageMutationConfirmation('chat:message:pin', payload.conversationId, payload.messageId, {
      action: payload.action,
      actorUserId: payload.pinnedBy?.userId,
      occurredAt: payload.pinnedAt,
    });
    this.confirmPendingMessageMutation('chat:message:pin', payload.conversationId, payload.messageId, { action: payload.action });
    if (isStaleLocalConfirmation && !Array.isArray(payload.pinnedMessages)) return;
    const isPinned = payload.action === 'pin';
    this.updateMessageInPlace(payload.conversationId, payload.messageId, (m) => ({
      ...m,
      isPinned,
    }));
    this.patchPinnedSnapshots(payload);
  }

  /**
   * Pin/unpin de conversación: solo llega para el propio usuario (sync
   * multi-dispositivo), nunca para otros participantes.
   */
  private handleConversationPinned(payload: MessagingSdk.ConversationPinnedPayload): void {
    const isStaleLocalConfirmation = this.isStaleLocalConversationPinConfirmation(payload.conversationId, payload.action);
    this.confirmPendingConversationPinMutation(payload.conversationId, payload.action);
    if (isStaleLocalConfirmation) return;
    this.applyOptimisticConversationPin(payload.conversationId, payload.pinnedAt);
  }

  /**
   * fetchInitialConversations() reemplaza el snapshot completo con lo que
   * trae el servidor — pero ese fetch puede dispararse por muchos motivos
   * ajenos al pin (reconexión, retries de lectura, fallbacks de grupo) y
   * llegar con datos más viejos que un pin/unpin todavía sin confirmar
   * (latestConversationPinMutationIntents). Si eso pasa, pisar el pinnedAt
   * optimista local con el valor stale del fetch deja el chat visualmente
   * "atascado" en el estado incorrecto hasta el próximo evento que lo
   * corrija — que puede no llegar nunca. Mientras haya una mutación de pin
   * en curso para esta conversación, conservamos el pinnedAt local propio y
   * dejamos que la confirmación puntual (o el timeout de rollback) sea la
   * única fuente de verdad para ese campo específico.
   */
  private preserveInFlightPinnedAt(
    incoming: MessagingSdk.ConversationSnapshot,
    current: MessagingSdk.ConversationSnapshot | undefined,
  ): MessagingSdk.ConversationSnapshot {
    if (!current || !this.latestConversationPinMutationIntents.has(incoming.conversationId)) return incoming;
    const currentUserId = this.currentUserId();
    const localPinnedAt = current.participants?.find((p) => p.userId === currentUserId)?.pinnedAt ?? null;
    const participants = (incoming.participants ?? []).map((p) => (p.userId === currentUserId ? { ...p, pinnedAt: localPinnedAt } : p));
    return { ...incoming, participants };
  }

  /** Actualiza pinnedAt del participante propio en el snapshot, sin tocar a los demás. */
  private applyOptimisticConversationPin(conversationId: string, pinnedAt: string | null): void {
    const currentUserId = this.currentUserId();
    this.state.update((s) => {
      const existing = s.snapshots.get(conversationId);
      if (!existing) return s;
      const nextParticipants = (existing.participants ?? []).map((p) => (p.userId === currentUserId ? { ...p, pinnedAt } : p));
      const next = new Map(s.snapshots);
      next.set(conversationId, { ...existing, participants: nextParticipants });
      return { ...s, snapshots: next };
    });
  }

  private editedTextFromPayload(payload: MessagingSdk.MessageEditedPayload): string {
    if (typeof payload.newText === 'string') return payload.newText;
    const contentText = (payload as MessagingSdk.MessageEditedPayload & { content?: { text?: unknown } }).content?.text;
    return typeof contentText === 'string' ? contentText : '';
  }

  private patchPinnedSnapshots(payload: MessagingSdk.MessagePinnedPayload): void {
    this.state.update((s) => {
      const existing = s.snapshots.get(payload.conversationId);
      if (!existing) return s;

      if (Array.isArray(payload.pinnedMessages)) {
        const pinnedIds = new Set(payload.pinnedMessages.map((item) => item.messageId));
        const messages = this.syncPinnedFlagsFromCanonicalList(s.messages, payload.conversationId, pinnedIds);
        const snapshots = new Map(s.snapshots);
        snapshots.set(payload.conversationId, { ...existing, pinnedMessages: payload.pinnedMessages });
        return { ...s, messages, snapshots };
      }

      const current = existing.pinnedMessages ?? [];
      if (payload.action === 'unpin') {
        const snapshots = new Map(s.snapshots);
        snapshots.set(payload.conversationId, {
          ...existing,
          pinnedMessages: current.filter((item) => item.messageId !== payload.messageId),
        });
        return { ...s, snapshots };
      }

      const pinnedMessage = payload.pinnedMessage ?? this.buildPinnedSnapshotFromLocalMessage(s, payload.conversationId, payload.messageId);
      if (!pinnedMessage) return s;

      const pinnedMessages = [pinnedMessage, ...current.filter((item) => item.messageId !== payload.messageId)];

      const snapshots = new Map(s.snapshots);
      snapshots.set(payload.conversationId, { ...existing, pinnedMessages });
      return { ...s, snapshots };
    });
  }

  private syncPinnedFlagsFromCanonicalList(messages: Map<string, ChatMessageUI[]>, conversationId: string, pinnedIds: Set<string>): Map<string, ChatMessageUI[]> {
    const list = messages.get(conversationId);
    if (!list) return messages;

    let changed = false;
    const nextList = list.map((message) => {
      const nextPinned = pinnedIds.has(message.id);
      if (Boolean(message.isPinned) === nextPinned) return message;
      changed = true;
      return { ...message, isPinned: nextPinned };
    });

    if (!changed) return messages;

    const next = new Map(messages);
    next.set(conversationId, nextList);
    return next;
  }

  private buildPinnedSnapshotFromLocalMessage(state: InternalState, conversationId: string, messageId: string): MessagingSdk.PinnedMessageSnapshot | null {
    const message = state.messages.get(conversationId)?.find((item) => item.id === messageId);
    if (!message) return null;

    const sentAt = message.serverSentAt ?? (message.optimisticCreatedAtMs ? new Date(message.optimisticCreatedAtMs).toISOString() : new Date().toISOString());
    const senderId = Number(message.senderId);
    return {
      messageId: message.id,
      senderId: Number.isFinite(senderId) ? senderId : 0,
      senderEmployeeId: message.isOwn ? this.currentEmployeeId() : 0,
      senderName: formatChatDisplayName({ senderName: message.senderName }),
      // MOMSG-1172: individual, no la última de la conversación — nunca
      // resume como álbum (ver individualMessagePreview).
      contentPreview: individualMessagePreview(message),
      type: messageTypeForSnapshot(message),
      sentAt,
      pinnedAt: new Date().toISOString(),
    };
  }

  private removePinnedSnapshot(conversationId: string, messageId: string): void {
    this.state.update((s) => {
      const existing = s.snapshots.get(conversationId);
      if (!existing?.pinnedMessages?.length) return s;

      const snapshots = new Map(s.snapshots);
      snapshots.set(conversationId, {
        ...existing,
        pinnedMessages: existing.pinnedMessages.filter((item) => item.messageId !== messageId),
      });
      return { ...s, snapshots };
    });
  }

  /**
   * Helper: aplica un patch puro al mensaje indicado dentro de la lista
   * de la conversación. No-op si la conv o el mensaje no existen.
   */
  private updateMessageInPlace(conversationId: string, messageId: string, patch: (m: ChatMessageUI) => ChatMessageUI): void {
    this.state.update((s) => {
      const list = s.messages.get(conversationId) ?? (s.snapshots.has(conversationId) ? mapSnapshotToConversation(s.snapshots.get(conversationId)!, this.currentUserId()).messages : undefined);
      if (!list) return s;
      const idx = list.findIndex((m) => m.id === messageId);
      if (idx === -1) return s;
      const updated = patch(list[idx]);
      const nextList = [...list];
      nextList[idx] = updated;
      const next = new Map(s.messages);
      next.set(conversationId, nextList);
      return { ...s, messages: next };
    });
  }

  private applyOptimisticEdit(conversationId: string, messageId: string, newText: string): void {
    this.updateMessageInPlace(conversationId, messageId, (m) => {
      if (m.isDeleted) return m;
      return {
        ...m,
        content: newText,
        isEdited: true,
      };
    });
    this.updateReplyPreviews(conversationId, messageId, newText);
    this.patchLastMessagePreview(conversationId, messageId, newText);
    this.patchPinnedSnapshotPreview(conversationId, messageId, newText);
  }

  /**
   * Resuelve un id optimista (local-...) a su id canónico confirmado por el
   * servidor. Los ids no locales se devuelven tal cual. Devuelve null si el
   * mensaje aún no tiene alias canónico (el ack no llegó todavía).
   */
  private resolveConfirmedMessageId(conversationId: string, messageId: string): string | null {
    if (!isLocalMessageId(messageId)) return messageId;

    const reconciledId = this.reconciledLocalMessageIds.get(messageId);
    if (reconciledId) return reconciledId;

    const localMessage = this.findLocalMessage(conversationId, messageId);
    const clientMessageId = localMessage?.clientMessageId;
    if (!localMessage?.isOwn || !clientMessageId) return null;

    const canonicalAlias = (this.state().messages.get(conversationId) ?? []).find(
      (message) => !isLocalMessageId(message.id) && message.isOwn && message.clientMessageId === clientMessageId,
    );
    return canonicalAlias?.id ?? null;
  }

  private resolveEditedMessageTargetId(payload: MessagingSdk.MessageEditedPayload, newText: string): string {
    const conversationId = payload.conversationId;
    if (this.findLocalMessage(conversationId, payload.messageId)) return payload.messageId;

    const clientMessageId = (payload as MessagingSdk.MessageEditedPayload & { clientMessageId?: string | null }).clientMessageId;
    if (clientMessageId) {
      const clientAlias = (this.state().messages.get(conversationId) ?? []).find(
        (message) => message.isOwn && message.clientMessageId === clientMessageId,
      );
      if (clientAlias) return clientAlias.id;
    }

    const pendingAlias = this.findSafePendingEditAlias(conversationId, newText, payload.editedBy?.userId);
    return pendingAlias ?? payload.messageId;
  }

  private findSafePendingEditAlias(conversationId: string, newText: string, actorUserId?: number): string | null {
    if (actorUserId !== undefined && actorUserId !== this.currentUserId()) return null;

    const matches = this.pendingMessageMutations.filter((pending) => {
      if (pending.event !== 'chat:message:edit' || pending.conversationId !== conversationId) return false;
      if (pending.optimisticMessage?.content !== newText || pending.optimisticMessage.isOwn !== true) return false;
      return !!this.findLocalMessage(conversationId, pending.messageId);
    });

    return matches.length === 1 ? matches[0].messageId : null;
  }

  private updateEditedMessageDerivedPreviews(conversationId: string, targetMessageId: string, canonicalMessageId: string, newText: string): void {
    this.updateReplyPreviews(conversationId, targetMessageId, newText);
    this.patchLastMessagePreview(conversationId, targetMessageId, newText);
    this.patchPinnedSnapshotPreview(conversationId, targetMessageId, newText);

    if (canonicalMessageId === targetMessageId) return;
    this.updateReplyPreviews(conversationId, canonicalMessageId, newText);
    this.patchLastMessagePreview(conversationId, canonicalMessageId, newText);
    this.patchPinnedSnapshotPreview(conversationId, canonicalMessageId, newText);
  }

  private findLocalMessage(conversationId: string, messageId: string): ChatMessageUI | null {
    return this.state().messages.get(conversationId)?.find((message) => message.id === messageId) ?? null;
  }

  private applyOptimisticDelete(conversationId: string, messageId: string): void {
    this.clearPendingMessageReactionsForMessage(conversationId, messageId);
    this.updateMessageInPlace(conversationId, messageId, (m) => ({
      ...m,
      content: '',
      isDeleted: true,
      isEdited: false,
      isPinned: false,
      replyTo: null,
      reactions: [],
    }));
    this.updateReplyPreviews(conversationId, messageId, 'Mensaje eliminado');
    this.patchLastMessagePreview(conversationId, messageId, 'Mensaje eliminado');
    this.removePinnedSnapshot(conversationId, messageId);
  }

  private applyOptimisticPin(conversationId: string, messageId: string, isPinned: boolean): void {
    let target: ChatMessageUI | null = null;
    this.updateMessageInPlace(conversationId, messageId, (m) => {
      if (m.isDeleted) return m;
      target = { ...m, isPinned };
      return target;
    });

    if (isPinned && target) {
      this.addLocalPinnedSnapshot(conversationId, target);
      return;
    }

    this.removePinnedSnapshot(conversationId, messageId);
  }

  private applyOptimisticLatestMessageToSnapshot(
    conversationId: string,
    message: ChatMessageUI,
    backup: OptimisticLatestSnapshotBackup,
  ): void {
    const sentAt = message.serverSentAt ?? (message.optimisticCreatedAtMs ? new Date(message.optimisticCreatedAtMs).toISOString() : new Date().toISOString());
    this.optimisticLatestBackups.set(message.id, backup);
    this.state.update((s) => {
      const existing = s.snapshots.get(conversationId);
      if (!existing) return s;

      const snapshots = new Map(s.snapshots);
      snapshots.set(conversationId, {
        ...existing,
        lastMessage: {
          messageId: message.id,
          senderId: Number(message.senderId),
          contentPreview: messagePreviewForSnapshot(message),
          type: messageTypeForSnapshot(message),
          sentAt,
        },
        unreadCount: existing.unreadCount,
        updatedAt: sentAt,
      });
      return { ...s, snapshots };
    });
  }

  private captureOptimisticLatestSnapshotBackup(conversationId: string): OptimisticLatestSnapshotBackup {
    const existing = this.state().snapshots.get(conversationId);
    if (!existing) return null;
    const currentMessageId = existing.lastMessage?.messageId;
    if (currentMessageId && this.optimisticLatestBackups.has(currentMessageId)) {
      return this.optimisticLatestBackups.get(currentMessageId) ?? null;
    }
    if (currentMessageId?.startsWith('local-')) return null;
    return {
      lastMessage: existing.lastMessage,
      unreadCount: existing.unreadCount,
      updatedAt: existing.updatedAt,
    };
  }

  private rollbackOptimisticLatestMessageSnapshot(conversationId: string, failedMessage: ChatMessageUI, backup: OptimisticLatestSnapshotBackup): void {
    this.optimisticLatestBackups.delete(failedMessage.id);
    this.state.update((s) => {
      const existing = s.snapshots.get(conversationId);
      if (!existing?.lastMessage) return s;
      if (!this.snapshotStillPointsToOptimisticMessage(existing, failedMessage)) return s;

      const remainingLatest = this.latestValidMessageForSnapshot(s.messages.get(conversationId) ?? []);
      const remainingActivity = remainingLatest ? this.messageActivityMs(remainingLatest) : Number.NaN;
      const backupActivity = backup?.lastMessage?.sentAt
        ? Date.parse(backup.lastMessage.sentAt)
        : Number.NaN;
      const useRemaining = !!remainingLatest && Number.isFinite(remainingActivity) &&
        (!Number.isFinite(backupActivity) || remainingActivity > backupActivity);
      if (!useRemaining && !backup) return s;

      const replacementLastMessage = useRemaining
        ? this.lastMessageSnapshotFromUi(remainingLatest!)
        : backup!.lastMessage;
      const replacementUpdatedAt = useRemaining
        ? replacementLastMessage?.sentAt ?? existing.updatedAt
        : backup!.updatedAt;

      const snapshots = new Map(s.snapshots);
      snapshots.set(conversationId, {
        ...existing,
        lastMessage: replacementLastMessage,
        unreadCount: existing.unreadCount,
        updatedAt: replacementUpdatedAt,
      });
      return { ...s, snapshots };
    });
  }

  private latestValidMessageForSnapshot(messages: readonly ChatMessageUI[]): ChatMessageUI | null {
    let latest: ChatMessageUI | null = null;
    let latestActivity = Number.NEGATIVE_INFINITY;
    for (const message of messages) {
      if (message.id.startsWith('local-') &&
        (message.uploadState === 'failed' || message.uploadState === 'cancelled' || message.uploadState === 'interrupted')) {
        continue;
      }
      const activity = this.messageActivityMs(message);
      if (!Number.isFinite(activity) || activity <= latestActivity) continue;
      latest = message;
      latestActivity = activity;
    }
    return latest;
  }

  private messageActivityMs(message: ChatMessageUI): number {
    if (typeof message.optimisticCreatedAtMs === 'number' && Number.isFinite(message.optimisticCreatedAtMs)) {
      return message.optimisticCreatedAtMs;
    }
    return message.serverSentAt ? Date.parse(message.serverSentAt) : Number.NaN;
  }

  private lastMessageSnapshotFromUi(message: ChatMessageUI): MessagingSdk.LastMessageSnapshot {
    const sentAt = message.serverSentAt ?? new Date(message.optimisticCreatedAtMs!).toISOString();
    return {
      messageId: message.id,
      senderId: Number(message.senderId),
      contentPreview: messagePreviewForSnapshot(message),
      type: messageTypeForSnapshot(message),
      sentAt,
    };
  }

  private snapshotStillPointsToOptimisticMessage(snapshot: MessagingSdk.ConversationSnapshot, message: ChatMessageUI): boolean {
    const lastMessage = snapshot.lastMessage;
    if (!lastMessage || lastMessage.messageId !== message.id) return false;
    return lastMessage.contentPreview === messagePreviewForSnapshot(message) && lastMessage.type === messageTypeForSnapshot(message);
  }

  private addLocalPinnedSnapshot(conversationId: string, message: ChatMessageUI): void {
    const sentAt = message.serverSentAt ?? (message.optimisticCreatedAtMs ? new Date(message.optimisticCreatedAtMs).toISOString() : new Date().toISOString());
    const senderId = Number(message.senderId);
    this.state.update((s) => {
      const existing = s.snapshots.get(conversationId);
      if (!existing) return s;

      const snapshots = new Map(s.snapshots);
      const current = existing.pinnedMessages ?? [];
      snapshots.set(conversationId, {
        ...existing,
        pinnedMessages: [
          {
            messageId: message.id,
            senderId: Number.isFinite(senderId) ? senderId : 0,
            senderEmployeeId: message.isOwn ? this.currentEmployeeId() : 0,
            senderName: formatChatDisplayName({ senderName: message.senderName }),
            // MOMSG-1172: individual, no la última de la conversación — nunca
            // resume como álbum (ver individualMessagePreview).
            contentPreview: individualMessagePreview(message),
            type: messageTypeForSnapshot(message),
            sentAt,
            pinnedAt: new Date().toISOString(),
          },
          ...current.filter((item) => item.messageId !== message.id),
        ],
      });
      return { ...s, snapshots };
    });
  }

  private patchPinnedSnapshotPreview(conversationId: string, messageId: string, contentPreview: string): void {
    this.state.update((s) => {
      const existing = s.snapshots.get(conversationId);
      if (!existing?.pinnedMessages?.length) return s;

      let changed = false;
      const pinnedMessages = existing.pinnedMessages.map((item) => {
        if (item.messageId !== messageId) return item;
        changed = true;
        return { ...item, contentPreview };
      });
      if (!changed) return s;

      const snapshots = new Map(s.snapshots);
      snapshots.set(conversationId, { ...existing, pinnedMessages });
      return { ...s, snapshots };
    });
  }

  private handleNewMessage(payload: MessagingSdk.NewMessagePayload): void {
    if (payload.clientMessageId) this.takePendingMediaMutation(payload.clientMessageId);
    const isOwn = this.isOwnNewMessage(payload);
    // Live bubbles resolve sender.displayName (versioned event-time snapshot)
    // ?? participant displayName ?? identity-cache displayName, falling back
    // to the stale sender.fullName snapshot only for legacy senders. Read
    // projections subsequently overlay a newer canonical participant identity.
    const senderUserId = Number(payload.sender?.userId);
    const liveDisplayName = isFinitePositiveUserId(senderUserId)
      ? this.lookupLiveSenderDisplayName(senderUserId, payload.conversationId)
      : null;
    const ui = { ...mapNewMessageToUi(payload, this.currentUserId(), this.mediaUrlPolicy, liveDisplayName), isOwn };
    const insertionResult = this.applyCanonicalMessage(payload.conversationId, ui, payload, isOwn);
    const isNewMessage = insertionResult === 'inserted';

    // Send delivery ACK back to the server (only for messages from others).
    if (!isOwn) {
      this.port.acknowledgeDelivery({
        conversationId: payload.conversationId,
        messageId: payload.messageId,
      });
      if (this.hasReadVisibilityClaim(payload.conversationId)) {
        this.markConversationAsRead(payload.conversationId);
      }
    }

    // Toast in-app: solo si NO es un mensaje propio Y (no es la conversación
    // seleccionada O la pestaña no está visible). Con OR en vez de AND-NOT acá
    // te auto-notificarías de tus propios mensajes — y específicamente
    // responder desde el propio Toast entraría en bucle.
    const isNotCurrentlyViewing =
      !this.hasReadVisibilityClaim(payload.conversationId) ||
      document.visibilityState !== 'visible';
    if (isNewMessage && !ui.isOwn && isNotCurrentlyViewing) {
      // El snapshot ya fue actualizado por applyLatestMessageToSnapshot(),
      // así que unreadCount incluye este mensaje.
      const snapshot = this.state().snapshots.get(payload.conversationId);
      const isGroup = snapshot?.type === 'GROUP';
      this.newMessageToast$.next({
        conversationId: payload.conversationId,
        conversationName: isGroup ? snapshot?.groupInfo?.name ?? 'Grupo' : null,
        isGroup,
        unreadCount: snapshot?.unreadCount ?? 0,
        sender: payload.sender ? { ...payload.sender, fullName: resolveLiveChatSenderName({ participantDisplayName: readVersionedSenderDisplayName(payload.sender) ?? this.lookupLiveSenderDisplayName(senderUserId, payload.conversationId), fullName: payload.sender.fullName }) } : payload.sender,
        preview: this.buildMessagePreview(payload),
        messageId: payload.messageId,
      });
    }
  }

  private applyCanonicalMessage(
    conversationId: string,
    message: ChatMessageUI,
    activity: Pick<MessagingSdk.NewMessagePayload, 'messageId' | 'type' | 'content' | 'sender' | 'sentAt' | 'album'>,
    isOwn: boolean,
  ): MessageInsertionResult {
    const insertionResult = this.appendMessage(conversationId, message);
    this.updateOwnMessageStatuses(conversationId);
    if (this.isMediaMessage(message)) {
      this.mergeConversationFiles(conversationId, [message]);
    }
    if (insertionResult !== 'duplicate') {
      this.applyLatestMessageToSnapshot(conversationId, activity, isOwn);
    }
    return insertionResult;
  }

  private startMessageReactionCommand(
    key: string,
    conversationId: string,
    messageId: string,
    actorUserId: number,
    authoritativeReactions: readonly MessagingSdk.MessageReaction[],
    authoritativeRevision: number,
    desiredActorReaction: MessagingSdk.MessageReaction | null,
  ): void {
    const authoritative = cloneMessageReactions(authoritativeReactions);
    const desired = cloneMessageReaction(desiredActorReaction);
    const authoritativeActorReaction = findActorReaction(authoritative, actorUserId);
    if (actorReactionsSemanticallyEqual(authoritativeActorReaction, desired)) {
      this.replaceMessageReactionSnapshot(conversationId, messageId, authoritative, authoritativeRevision);
      return;
    }

    const commandEmoji = desired?.emoji ?? authoritativeActorReaction?.emoji;
    if (!commandEmoji) return;

    const token = ++this.pendingMessageReactionTokenSeq;
    const commandId = this.generateMessageReactionCommandId();
    const pending: PendingMessageReaction = {
      token,
      commandId,
      commandEmoji,
      conversationId,
      messageId,
      actorUserId,
      authoritativeReactions: authoritative,
      authoritativeRevision,
      commandTargetActorReaction: desired,
      desiredActorReaction: cloneMessageReaction(desired),
      hasQueuedIntent: false,
      optimisticReactions: [],
      optimisticRevision: authoritativeRevision,
    };
    this.pendingMessageReactions.set(key, pending);
    this.renderPendingMessageReaction(pending);

    const payload: MessagingSdk.ToggleMessageReactionPayload = {
      commandId,
      conversationId,
      messageId,
      emoji: commandEmoji,
    };
    try {
      this.port.toggleMessageReaction(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (ack) => this.handleMessageReactionAck(key, token, commandId, ack),
          error: () => this.failMessageReaction(key, token, commandId, 'transport'),
        });
    } catch {
      this.failMessageReaction(key, token, commandId, 'transport');
    }
  }

  private generateMessageReactionCommandId(): string {
    return generateClientMessageId();
  }

  private renderPendingMessageReaction(pending: PendingMessageReaction): void {
    const optimistic = overlayActorReaction(
      pending.authoritativeReactions,
      pending.actorUserId,
      pending.desiredActorReaction,
    );
    pending.optimisticReactions = cloneMessageReactions(optimistic);
    pending.optimisticRevision = pending.authoritativeRevision;
    this.replaceMessageReactionSnapshot(
      pending.conversationId,
      pending.messageId,
      optimistic,
      pending.authoritativeRevision,
    );
  }

  private handleMessageReactionAck(
    key: string,
    token: number,
    commandId: string,
    ack: MessagingSdk.MessageReactionAck,
  ): void {
    const pending = this.pendingMessageReactions.get(key);
    if (!this.isCurrentPendingMessageReaction(key, pending, token, commandId)) return;
    if (
      ack.commandId !== commandId ||
      ack.conversationId !== pending.conversationId ||
      ack.messageId !== pending.messageId ||
      ack.emoji !== pending.commandEmoji
    ) return;

    if (ack.status !== 'SUCCESS') {
      this.failMessageReaction(key, token, commandId, 'rejected');
      return;
    }

    const relation = compareReactionSnapshot(
      pending.authoritativeReactions,
      pending.authoritativeRevision,
      ack.reactions,
      ack.reactionsRevision,
    );
    if (relation === 'lower') {
      this.settleMessageReactionCommand(
        key,
        pending,
        pending.authoritativeReactions,
        pending.authoritativeRevision,
      );
      return;
    }
    if (relation === 'equal-identical') {
      this.settleMessageReactionCommand(key, pending, pending.authoritativeReactions, pending.authoritativeRevision);
      return;
    }
    if (relation === 'equal-conflicting') {
      const incomingActorReaction = findActorReaction(ack.reactions, pending.actorUserId);
      if (!actorReactionsSemanticallyEqual(incomingActorReaction, pending.commandTargetActorReaction)) return;
      const carefulAuthority = overlayActorReaction(
        pending.authoritativeReactions,
        pending.actorUserId,
        incomingActorReaction,
      );
      this.settleMessageReactionCommand(key, pending, carefulAuthority, pending.authoritativeRevision);
      return;
    }

    this.settleMessageReactionCommand(key, pending, ack.reactions, ack.reactionsRevision);
  }

  private handleMessageReactionsChanged(payload: MessagingSdk.MessageReactionsChangedPayload): void {
    const message = this.state().messages.get(payload.conversationId)?.find(
      (candidate) => candidate.id === payload.messageId,
    );
    if (!message || message.isDeleted) return;

    const actorUserId = this.currentUserId();
    const key = messageReactionPendingKey(payload.conversationId, payload.messageId, actorUserId);
    const pending = this.pendingMessageReactions.get(key);
    const authoritativeReactions = pending?.authoritativeReactions ?? message.reactions;
    const authoritativeRevision = pending?.authoritativeRevision ?? message.reactionsRevision;
    const relation = compareReactionSnapshot(
      authoritativeReactions,
      authoritativeRevision,
      payload.reactions,
      payload.reactionsRevision,
    );

    const incomingActorReaction = pending
      ? findActorReaction(payload.reactions, pending.actorUserId)
      : null;
    const matchesPendingResult = !!pending &&
      payload.actorUserId === pending.actorUserId &&
      payload.commandId === pending.commandId &&
      payload.emoji === pending.commandEmoji &&
      actorReactionsSemanticallyEqual(incomingActorReaction, pending.commandTargetActorReaction);

    if (relation === 'lower') {
      if (pending && matchesPendingResult) {
        this.settleMessageReactionCommand(key, pending, authoritativeReactions, authoritativeRevision);
      }
      return;
    }

    if (relation === 'equal-identical') {
      if (pending && matchesPendingResult) {
        this.settleMessageReactionCommand(key, pending, authoritativeReactions, authoritativeRevision);
      }
      return;
    }
    if (relation === 'equal-conflicting') {
      if (pending && matchesPendingResult) {
        const carefulAuthority = overlayActorReaction(
          authoritativeReactions,
          pending.actorUserId,
          incomingActorReaction,
        );
        this.settleMessageReactionCommand(key, pending, carefulAuthority, authoritativeRevision);
      }
      return;
    }

    if (!pending) {
      this.replaceMessageReactionSnapshot(
        payload.conversationId,
        payload.messageId,
        payload.reactions,
        payload.reactionsRevision,
      );
      return;
    }
    if (matchesPendingResult) {
      this.settleMessageReactionCommand(key, pending, payload.reactions, payload.reactionsRevision);
      return;
    }

    pending.authoritativeReactions = cloneMessageReactions(payload.reactions);
    pending.authoritativeRevision = payload.reactionsRevision;
    this.renderPendingMessageReaction(pending);
  }

  private settleMessageReactionCommand(
    key: string,
    pending: PendingMessageReaction,
    authoritativeReactions: readonly MessagingSdk.MessageReaction[],
    authoritativeRevision: number,
  ): void {
    if (!this.isCurrentPendingMessageReaction(key, pending, pending.token, pending.commandId)) return;
    this.pendingMessageReactions.delete(key);

    const authoritative = cloneMessageReactions(authoritativeReactions);
    const authoritativeActorReaction = findActorReaction(authoritative, pending.actorUserId);
    if (
      !pending.hasQueuedIntent ||
      actorReactionsSemanticallyEqual(authoritativeActorReaction, pending.desiredActorReaction)
    ) {
      this.replaceMessageReactionSnapshot(
        pending.conversationId,
        pending.messageId,
        authoritative,
        authoritativeRevision,
      );
      return;
    }

    this.startMessageReactionCommand(
      key,
      pending.conversationId,
      pending.messageId,
      pending.actorUserId,
      authoritative,
      authoritativeRevision,
      pending.desiredActorReaction,
    );
  }

  private failMessageReaction(
    key: string,
    token: number,
    commandId: string,
    kind: 'rejected' | 'transport',
  ): void {
    const pending = this.pendingMessageReactions.get(key);
    if (!this.isCurrentPendingMessageReaction(key, pending, token, commandId)) return;
    this.pendingMessageReactions.delete(key);
    this.lastErrorSignal.set({
      code: kind === 'rejected' ? 'KLAB409' : 'KLAB500',
      event: MessagingSdk.CHAT_EVENTS.MESSAGE_REACTION_TOGGLE,
      description: 'No se pudo actualizar la reacción. Inténtalo nuevamente.',
    });

    const authoritativeActorReaction = findActorReaction(
      pending.authoritativeReactions,
      pending.actorUserId,
    );
    if (
      !pending.hasQueuedIntent ||
      actorReactionsSemanticallyEqual(authoritativeActorReaction, pending.desiredActorReaction)
    ) {
      this.replaceMessageReactionSnapshot(
        pending.conversationId,
        pending.messageId,
        pending.authoritativeReactions,
        pending.authoritativeRevision,
      );
      return;
    }

    this.startMessageReactionCommand(
      key,
      pending.conversationId,
      pending.messageId,
      pending.actorUserId,
      pending.authoritativeReactions,
      pending.authoritativeRevision,
      pending.desiredActorReaction,
    );
  }

  private isCurrentPendingMessageReaction(
    key: string,
    pending: PendingMessageReaction | undefined,
    token: number,
    commandId: string,
  ): pending is PendingMessageReaction {
    return !!pending &&
      pending.token === token &&
      pending.commandId === commandId &&
      this.pendingMessageReactions.get(key) === pending;
  }

  private replaceMessageReactionSnapshot(
    conversationId: string,
    messageId: string,
    reactions: readonly MessagingSdk.MessageReaction[],
    reactionsRevision: number,
  ): void {
    this.state.update((state) => {
      const patchCollection = (collection: Map<string, ChatMessageUI[]>): Map<string, ChatMessageUI[]> => {
        const list = collection.get(conversationId);
        if (!list) return collection;
        let changed = false;
        const nextList = list.map((message) => {
          if (message.id !== messageId || message.isDeleted) return message;
          if (reactionsRevision < message.reactionsRevision) return message;
          changed = true;
          return {
            ...message,
            reactions: cloneMessageReactions(reactions),
            reactionsRevision,
          };
        });
        if (!changed) return collection;
        const next = new Map(collection);
        next.set(conversationId, nextList);
        return next;
      };
      const messages = patchCollection(state.messages);
      const files = patchCollection(state.files);
      return messages === state.messages && files === state.files ? state : { ...state, messages, files };
    });
  }

  private isMessageAccessible(conversationId: string, messageId: string): boolean {
    const snapshot = this.state().snapshots.get(conversationId);
    if (!snapshot || this.deletedConversationIds.has(conversationId)) return false;
    const actorUserId = this.currentUserId();
    const participant = snapshot.participants?.find((candidate) => candidate.userId === actorUserId);
    return participant?.isActive === true &&
      (this.state().messages.get(conversationId) ?? []).some((message) => message.id === messageId);
  }

  private clearPendingMessageReactionsForMessage(conversationId: string, messageId: string): void {
    for (const [key, pending] of this.pendingMessageReactions) {
      if (
        pending.conversationId === conversationId &&
        pending.messageId === messageId &&
        this.isCurrentPendingMessageReaction(key, pending, pending.token, pending.commandId)
      ) {
        this.pendingMessageReactions.delete(key);
      }
    }
  }

  private clearPendingMessageReactionsForConversation(conversationId: string, rollback: boolean): void {
    for (const [key, pending] of this.pendingMessageReactions) {
      if (pending.conversationId !== conversationId) continue;
      if (!this.isCurrentPendingMessageReaction(key, pending, pending.token, pending.commandId)) continue;
      this.pendingMessageReactions.delete(key);
      if (!rollback) continue;
      const current = this.state().messages.get(conversationId)?.find((message) => message.id === pending.messageId);
      if (
        current?.reactionsRevision === pending.optimisticRevision &&
        messageReactionsEqual(current.reactions, pending.optimisticReactions)
      ) {
        this.replaceMessageReactionSnapshot(
          conversationId,
          pending.messageId,
          pending.authoritativeReactions,
          pending.authoritativeRevision,
        );
      }
    }
  }

  // Mismo criterio de truncado/fallback que messageContentPreview() del Store
  // y buildMessagePreview() del FanoutHandler — texto si hay, si no fileName,
  // si no un texto genérico según el tipo.
  private buildMessagePreview(payload: MessagingSdk.NewMessagePayload): string {
    const text = payload.content?.text?.trim();
    if (text) return text.slice(0, 100);
    // MOMSG-1172: álbum de imágenes sin caption propio — antes de fileName,
    // que siempre es truthy y por eso nunca dejaba que este fallback corriera.
    //
    // Trade-off aceptado, documentado (hallazgo de revisión adversarial): a
    // diferencia del backend (que persiste un conteo REAL vía
    // `countByAlbumId`, así nunca sobre-reporta si algo del lote falla), acá
    // solo tenemos el `albumCount` DECLARADO por quien envía. Mientras el
    // álbum se sigue enviando, un participante con la pestaña abierta puede
    // ver por unos segundos "📷 5 imágenes" y que después, si 2 fallan,
    // nunca se autocorrija a "3" sin recargar — el próximo fetch/reload sí
    // trae el valor correcto del backend. Recomputar el conteo en vivo
    // exigiría que esta función (pura, sin acceso al estado de la
    // conversación) supiera cuántos miembros del álbum ya se vieron pasar,
    // lo cual movería bastante complejidad para corregir una inconsistencia
    // transitoria y autolimitada — no se justifica frente al costo.
    if (payload.album) return `📷 ${payload.album.albumCount} imágenes`;
    if (payload.content?.fileName) return payload.content.fileName.slice(0, 100);
    if (payload.type === 'IMAGE') return 'Imagen';
    if (payload.type === 'FILE') return 'Adjunto';
    return '';
  }

  private isOwnNewMessage(payload: MessagingSdk.NewMessagePayload): boolean {
    const senderUserId = Number(payload.sender?.userId);
    const currentUserId = this.currentUserId();
    if (currentUserId > 0 && Number.isFinite(senderUserId) && senderUserId === currentUserId) return true;
    if (Number.isFinite(senderUserId)) return false;

    const senderEmployeeId = Number(payload.sender?.employeeId);
    const currentEmployeeId = this.currentEmployeeId();
    if (currentEmployeeId > 0 && Number.isFinite(senderEmployeeId) && senderEmployeeId === currentEmployeeId) return true;
    if (Number.isFinite(senderEmployeeId)) return false;

    const clientMessageId = payload.clientMessageId;
    if (!clientMessageId) return false;

    return (this.state().messages.get(payload.conversationId) ?? []).some(
      (message) => message.id.startsWith('local-') && message.isOwn && message.clientMessageId === clientMessageId,
    );
  }

  // Distingue inserción, reconciliación optimista y replay real. El caller
  // actualiza actividad canónica para los dos primeros, pero solo suma unread
  // y muestra toast para una inserción nueva.
  private appendMessage(conversationId: string, message: ChatMessageUI): MessageInsertionResult {
    this.rememberMessageIdentity(message);
    let result: MessageInsertionResult = 'duplicate';
    this.state.update((s) => {
      const next = new Map(s.messages);
      const prev = next.get(conversationId) ?? [];

      const existingIdx = prev.findIndex((m) => m.id === message.id);
      if (existingIdx !== -1) {
        const existing = prev[existingIdx];
        this.revokeLocalObjectUrls([existing]);
        const nextList = [...prev];
        nextList[existingIdx] = {
          ...message,
          mediaKind: message.mediaKind ?? existing.mediaKind ?? null,
          durationMs: message.durationMs ?? existing.durationMs ?? null,
          status: nextMessageStatus(existing.status, message.status),
          ...highestReactionSnapshot(existing, message),
        };
        next.set(conversationId, this.enrichReplySenderNames(conversationId, orderMessages(nextList), s));
        return { ...s, messages: next };
      }

      const optimisticIdx = this.findMatchingOptimisticMessageIndex(prev, message);
      if (optimisticIdx !== -1) {
        const optimistic = prev[optimisticIdx];
        const optimisticId = optimistic.id;
        const canonical = {
          ...message,
          mediaKind: message.mediaKind ?? optimistic.mediaKind ?? null,
          durationMs: message.durationMs ?? optimistic.durationMs ?? null,
        };
        this.reconciledLocalMessageIds.set(optimisticId, message.id);
        this.optimisticLatestBackups.delete(optimisticId);
        this.revokeLocalObjectUrls([optimistic]);
        const nextList = prev
          .map((m, idx) => (idx === optimisticIdx ? canonical : rewriteReplyTarget(m, optimisticId, message.id)))
          .filter((m, idx) => idx === optimisticIdx || m.id !== message.id);
        next.set(conversationId, this.enrichReplySenderNames(conversationId, orderMessages(nextList), s));
        result = 'reconciled-optimistic';
        return { ...s, messages: next };
      }

      result = 'inserted';
      // A genuinely new arrival is actual progress: re-enable forward paging
      // even for anchors previously marked as non-progress.
      this.historyNewerNoProgressAnchors.delete(conversationId);
      const ordered = orderMessages([...prev, message]);
      const retained = this.retainHistoryWindow(ordered, 'newer');
      next.set(conversationId, this.enrichReplySenderNames(conversationId, retained, s));
      if (retained.length !== ordered.length) {
        const history = new Map(s.history);
        const currentHistory = this.getHistoryState(conversationId);
        history.set(conversationId, {
          ...currentHistory,
          hasMore: true,
          nextBeforeId: retained[0]?.id ?? null,
          droppedBefore: true,
        });
        return { ...s, messages: next, history };
      }
      return { ...s, messages: next };
    });
    return result;
  }

  private enrichReplySenderNames(conversationId: string, messages: ChatMessageUI[], state: InternalState): ChatMessageUI[] {
    const namesBySenderId = this.buildReplySenderNameIndex(conversationId, messages, state);
    let changed = false;
    const enriched = messages.map((message) => {
      let next = message;

      // MOMSG-1208: a diferencia de replyTo (abajo), acá SIEMPRE se prefiere
      // el nombre resuelto por participante/contacto cuando existe, no solo
      // cuando el actual está vacío — message.senderName llega poblado desde
      // el mapeo inicial (con el fullName crudo del SenderSnapshot del
      // mensaje, que no tiene displayName), así que puede estar "presente"
      // pero mal armado (ver buildReplySenderNameIndex).
      const resolvedSenderName = namesBySenderId.get(String(message.senderId));
      if (hasUsableDisplayName(resolvedSenderName) && resolvedSenderName !== next.senderName) {
        changed = true;
        next = { ...next, senderName: resolvedSenderName };
      }

      if (next.replyTo && !next.replyTo.isUnavailable && !hasUsableDisplayName(next.replyTo.senderName)) {
        const resolvedReplyName = namesBySenderId.get(String(next.replyTo.senderId));
        if (hasUsableDisplayName(resolvedReplyName)) {
          changed = true;
          next = { ...next, replyTo: { ...next.replyTo, senderName: resolvedReplyName } };
        }
      }

      return next;
    });
    return changed ? enriched : messages;
  }

  private reenrichExistingReplySenderNames(): void {
    this.state.update((s) => {
      let changed = false;
      const nextMessages = new Map(s.messages);

      for (const [conversationId, messages] of s.messages) {
        const enriched = this.enrichReplySenderNames(conversationId, messages, s);
        if (enriched === messages) continue;
        nextMessages.set(conversationId, enriched);
        changed = true;
      }

      return changed ? { ...s, messages: nextMessages } : s;
    });
  }

  /**
   * Live displayName for a group sender, matching the contact list:
   * participant snapshot displayName ?? canonical session displayName.
   * Returns null when neither source has a usable name so callers fall back
   * to the versioned sender.displayName snapshot first, then sender.fullName,
   * via {@link resolveLiveChatSenderName}.
   */
  private lookupLiveSenderDisplayName(userId: number, conversationId: string, state?: InternalState): string | null {
    const snapshots = state?.snapshots ?? this.state().snapshots;
    const participant = snapshots.get(conversationId)?.participants?.find((candidate) => candidate.userId === userId);
    const participantDisplayName = participant?.displayName?.trim() ? participant.displayName.trim() : null;
    if (participantDisplayName) return participantDisplayName;
    const canonical = this.identityCache.get(userId)?.displayName;
    return hasUsableDisplayName(canonical) ? canonical.trim().replace(/\s+/g, ' ') : null;
  }

  private formatLiveSenderName(userId: number, conversationId: string, senderFullName: string | null | undefined, state?: InternalState): string {
    return resolveLiveChatSenderName({
      participantDisplayName: this.lookupLiveSenderDisplayName(userId, conversationId, state),
      fullName: senderFullName,
    });
  }

  /**
   * Overlays canonical group participant identities at read time. The stored
   * message and sender snapshots remain event-time records, while every chat
   * surface receives the same current sender label and therefore initials.
   */
  private projectLiveGroupMessageIdentities(
    conversationId: string,
    messages: ChatMessageUI[],
    state: InternalState,
  ): ChatMessageUI[] {
    if (state.snapshots.get(conversationId)?.type !== 'GROUP' || messages.length === 0) {
      return messages;
    }

    let changed = false;
    const projected = messages.map((message) => {
      let next = message;
      const senderUserId = Number(message.senderId);
      const senderName = isFinitePositiveUserId(senderUserId)
        ? this.lookupLiveSenderDisplayName(senderUserId, conversationId, state)
        : null;
      if (senderName && senderName !== message.senderName) {
        next = { ...next, senderName };
        changed = true;
      }

      const replySenderUserId = Number(message.replyTo?.senderId);
      const replySenderName = isFinitePositiveUserId(replySenderUserId)
        ? this.lookupLiveSenderDisplayName(replySenderUserId, conversationId, state)
        : null;
      if (next.replyTo && replySenderName && replySenderName !== next.replyTo.senderName) {
        next = { ...next, replyTo: { ...next.replyTo, senderName: replySenderName } };
        changed = true;
      }

      return next;
    });

    return changed ? projected : messages;
  }

  private buildReplySenderNameIndex(conversationId: string, messages: ChatMessageUI[], state: InternalState): Map<string, string> {
    const names = new Map<string, string>();
    // MOMSG-1208: participant.displayName ya viene resuelto (ChatDisplayNameMapper
    // de klab-people-api); sin preferirlo, formatChatDisplayName caía en la
    // heurística de respaldo ("primera + tercera palabra") y adivinaba mal con
    // apellidos compuestos (ej. "Luz Solano Quincho" → "Luz Quincho").
    // resolveLiveChatSenderName prefiere participantDisplayName y conserva ese
    // fallback solo cuando no hay displayName resuelto.
    const put = (senderId: number | string | null | undefined, liveDisplayName: string | null | undefined, fallbackName: string | null | undefined): void => {
      const key = String(senderId ?? '').trim();
      const name = resolveLiveChatSenderName({ participantDisplayName: liveDisplayName, fullName: fallbackName, senderName: fallbackName });
      if (!key || names.has(key) || !hasUsableDisplayName(name)) return;
      names.set(key, name);
    };

    // MOMSG-1208: participant.displayName ya viene resuelto correctamente
    // (primer token de nombre + primer token de apellido, ChatDisplayNameMapper
    // de klab-people-api) — sin pasarlo acá, el helper solo tenía el fullName
    // crudo y caía en su heurística de respaldo ("primera + tercera palabra"),
    // que adivina mal con Nombre + Apellido-compuesto de 3 tokens
    // (ej. "Luz Solano Quincho" → "Luz Quincho" en vez de "Luz Solano").
    const participantsById = new Map<number, MessagingSdk.ParticipantSnapshot>();
    for (const participant of state.snapshots.get(conversationId)?.participants ?? []) {
      if (!isFinitePositiveUserId(participant.userId) || participantsById.has(participant.userId)) continue;
      participantsById.set(participant.userId, participant);
      put(participant.userId, participant.displayName, participant.fullName);
    }
    for (const message of messages) {
      const senderUserId = Number(message.senderId);
      const snapshotDisplayName = (message.senderSnapshot as { displayName?: unknown } | undefined)?.displayName;
      const messageSnapshotDisplay = typeof snapshotDisplayName === 'string' && snapshotDisplayName.trim().length > 0
        ? snapshotDisplayName
        : null;
      const liveDisplayName = isFinitePositiveUserId(senderUserId)
        ? (messageSnapshotDisplay
          ?? participantsById.get(senderUserId)?.displayName
          ?? this.lookupLiveSenderDisplayName(senderUserId, conversationId, state))
        : messageSnapshotDisplay;
      put(message.senderId, liveDisplayName, message.senderName ?? message.senderSnapshot?.fullName);
    }
    for (const identity of this.identityCache.values()) {
      put(identity.userId, identity.displayName, identity.fullName);
    }
    for (const contact of this.directoryState().contacts) put(contact.userId, contact.fullName, contact.fullName);

    return names;
  }

  private applyLatestMessageToSnapshot(
    conversationId: string,
    payload: Pick<MessagingSdk.NewMessagePayload, 'messageId' | 'type' | 'content' | 'sender' | 'sentAt' | 'album'>,
    isOwn: boolean,
  ): void {
    this.state.update((s) => {
      const existing = s.snapshots.get(conversationId);
      if (!existing) return s;

      const incomingActivity = Date.parse(payload.sentAt);
      const currentActivity = existing.lastMessage?.sentAt
        ? Date.parse(existing.lastMessage.sentAt)
        : Number.NaN;
      const reconcilesCurrentOptimistic = !!existing.lastMessage?.messageId &&
        this.reconciledLocalMessageIds.get(existing.lastMessage.messageId) === payload.messageId;
      const updatesLatestActivity = reconcilesCurrentOptimistic ||
        !Number.isFinite(currentActivity) ||
        (Number.isFinite(incomingActivity) && incomingActivity >= currentActivity);

      const isActuallyVisible = document.visibilityState === 'visible' &&
        this.hasReadVisibilityClaim(conversationId);
      const nextUnreadCount = isOwn
        ? existing.unreadCount ?? 0
        : isActuallyVisible
          ? 0
          : (existing.unreadCount ?? 0) + 1;
      const next = new Map(s.snapshots);
      next.set(conversationId, {
        ...existing,
        ...(updatesLatestActivity ? {
          lastMessage: {
            messageId: payload.messageId,
            senderId: payload.sender?.userId,
            contentPreview: buildLastMessagePreview(payload.type, payload.content, payload.album),
            type: payload.type,
            sentAt: payload.sentAt,
          },
          updatedAt: payload.sentAt,
        } : {}),
        unreadCount: nextUnreadCount,
      });
      return { ...s, snapshots: next };
    });
  }

  private patchLastMessagePreview(conversationId: string, messageId: string, contentPreview: string): void {
    this.state.update((s) => {
      const existing = s.snapshots.get(conversationId);
      if (!existing?.lastMessage || existing.lastMessage.messageId !== messageId) return s;

      const next = new Map(s.snapshots);
      next.set(conversationId, {
        ...existing,
        lastMessage: {
          ...existing.lastMessage,
          contentPreview,
        },
      });
      return { ...s, snapshots: next };
    });
  }

  private updateReplyPreviews(conversationId: string, messageId: string, contentPreview: string): void {
    this.state.update((s) => {
      const list = s.messages.get(conversationId);
      if (!list) return s;

      let changed = false;
      const nextList = list.map((m) => {
        if (m.replyTo?.messageId !== messageId) return m;
        changed = true;
        return {
          ...m,
          replyTo: {
            ...m.replyTo,
            contentPreview,
          },
        };
      });
      if (!changed) return s;

      const messages = new Map(s.messages);
      messages.set(conversationId, nextList);
      return { ...s, messages };
    });
  }

  private resetUnreadCount(conversationId: string): void {
    this.state.update((s) => {
      const existing = s.snapshots.get(conversationId);
      if (!existing || existing.unreadCount === 0) return s;
      const snapshots = new Map(s.snapshots);
      snapshots.set(conversationId, { ...existing, unreadCount: 0 });
      return { ...s, snapshots };
    });
  }

  private removeMessageById(conversationId: string, messageId: string): void {
    this.state.update((s) => {
      const next = new Map(s.messages);
      const list = next.get(conversationId);
      if (!list) return s;

      const removed = list.filter((m) => m.id === messageId);
      if (removed.length === 0) return s;

      this.revokeLocalObjectUrls(removed);
      next.set(
        conversationId,
        list.filter((m) => m.id !== messageId),
      );
      return { ...s, messages: next };
    });
  }

  private revokeAllLocalObjectUrls(): void {
    this.state().messages.forEach((messages) => this.revokeLocalObjectUrls(messages));
  }

  private revokeLocalObjectUrls(messages: ChatMessageUI[]): void {
    messages.forEach((message) => {
      if (message.localObjectUrl) {
        this.mediaUrlPolicy.revokeObjectUrl(message.localObjectUrl);
      }
    });
  }

  private findMatchingOptimisticMessageIndex(messages: ChatMessageUI[], canonical: ChatMessageUI): number {
    if (!canonical.isOwn || canonical.id.startsWith('local-')) return -1;

    if (canonical.clientMessageId) {
      return messages.findIndex((candidate) => candidate.id.startsWith('local-') && candidate.isOwn && candidate.clientMessageId === canonical.clientMessageId);
    }

    const canonicalSentAt = canonicalMessageSentAt(canonical);
    if (canonicalSentAt === null) return -1;

    const now = Date.now();
    let bestIndex = -1;
    let bestDelta = Number.POSITIVE_INFINITY;

    messages.forEach((candidate, idx) => {
      if (!candidate.id.startsWith('local-')) return;
      if (!candidate.isOwn) return;
      if (candidate.content !== canonical.content) return;
      if (!sameReplyTarget(candidate.replyTo, canonical.replyTo)) return;

      const createdAt = candidate.optimisticCreatedAtMs ?? optimisticMessageCreatedAt(candidate.id);
      if (createdAt === null || now - createdAt > OPTIMISTIC_RECONCILIATION_WINDOW_MS) return;

      const canonicalDelta = Math.abs(canonicalSentAt - createdAt);
      if (canonicalDelta > OPTIMISTIC_CANONICAL_ECHO_WINDOW_MS) return;
      if (canonicalDelta >= bestDelta) return;

      bestIndex = idx;
      bestDelta = canonicalDelta;
    });

    return bestIndex;
  }

  private updateOwnMessageStatuses(conversationId: string): void {
    this.state.update((s) => {
      const list = s.messages.get(conversationId);
      const snapshot = s.snapshots.get(conversationId);
      if (!list || !snapshot) return s;

      const currentUserId = this.currentUserId();
      const readReceipts = this.mergeSnapshotReadReceipts(snapshot, conversationId);
      const deliveryReceipts = this.mergeSnapshotDeliveryReceipts(snapshot, conversationId);
      if (snapshot.type === 'GROUP') return s;

      const activeOthers = (snapshot.participants ?? [])
        .filter((participant) => participant.isActive !== false && participant.userId !== currentUserId)
        .filter((participant) => Number.isFinite(participant.userId));
      if (activeOthers.length === 0) return s;

      let changed = false;
      const nextList = list.map((message) => {
        if (!message.isOwn || message.isDeleted || isLocalMessageId(message.id)) return message;
        if (!Number.isFinite(message.sequence)) return message;
        const eligible = activeOthers.filter((participant) =>
          participant.historyVisibleAfterSequence == null || participant.historyVisibleAfterSequence < message.sequence!,
        );
        const readCount = eligible.filter((participant) => (readReceipts.get(participant.userId) ?? -1) >= message.sequence!).length;
        const deliveredCount = eligible.filter((participant) => Math.max(deliveryReceipts.get(participant.userId) ?? -1, readReceipts.get(participant.userId) ?? -1) >= message.sequence!).length;
        const nextStatus: ChatMessageUI['status'] = eligible.length > 0 && readCount === eligible.length ? 'read' : deliveredCount > 0 ? 'delivered' : 'sent';
        const mergedStatus = nextMessageStatus(message.status, nextStatus);
        if (mergedStatus === message.status) return message;
        changed = true;
        return { ...message, status: mergedStatus };
      });
      if (!changed) return s;

      const messages = new Map(s.messages);
      messages.set(conversationId, nextList);
      const files = new Map(s.files);
      const fileList = files.get(conversationId);
      if (fileList) {
        const statuses = new Map(nextList.map((message) => [message.id, message.status]));
        files.set(conversationId, fileList.map((file) => statuses.has(file.id) ? { ...file, status: statuses.get(file.id)! } : file));
      }
      return { ...s, messages, files };
    });
  }

  private mergeMessagesChronologically(conversationId: string, messages: ChatMessageUI[]): void {
    if (messages.length === 0) return;
    this.rememberMessageIdentities(messages);
    this.state.update((s) => {
      const next = new Map(s.messages);
      const ordered = orderMessages(dedupeMessages([...(next.get(conversationId) ?? []), ...messages]));
      const retained = this.retainHistoryWindow(ordered, 'newer');
      const merged = this.enrichReplySenderNames(conversationId, retained, s);
      next.set(conversationId, merged);
      if (retained.length !== ordered.length) {
        const history = new Map(s.history);
        const currentHistory = this.getHistoryState(conversationId);
        history.set(conversationId, {
          ...currentHistory,
          hasMore: true,
          nextBeforeId: retained[0]?.id ?? null,
          droppedBefore: true,
        });
        return { ...s, messages: next, history };
      }
      return { ...s, messages: next };
    });
  }

  private afterCanonicalMessagesMerged(conversationId: string): void {
    this.updateOwnMessageStatuses(conversationId);
    if (this.hasReadVisibilityClaim(conversationId)) {
      this.markConversationAsRead(conversationId);
    }
  }

  /**
   * A direct go-to starts a new refetchable history range. Keep the true recent
   * tail, but discard the now-unrelated intermediate range so a full window
   * cannot immediately evict the requested target.
   */
  private replaceHistoryRangeAroundMessage(conversationId: string, messages: ChatMessageUI[]): void {
    this.rememberMessageIdentities(messages);
    this.state.update((s) => {
      const current = s.messages.get(conversationId) ?? [];
      const recentTail = current.slice(-HISTORY_RECENT_TAIL_RESERVE);
      const retained = orderMessages(dedupeMessages([...messages, ...recentTail]));
      const next = new Map(s.messages);
      next.set(conversationId, this.enrichReplySenderNames(conversationId, retained, s));
      return { ...s, messages: next };
    });
  }

  /** Mantiene una ventana acotada sin expulsar nunca los cuarenta mensajes recientes. */
  private retainHistoryWindow(messages: ChatMessageUI[], direction: 'older' | 'newer'): ChatMessageUI[] {
    const overflow = messages.length - HISTORY_WINDOW_MAX_MESSAGES;
    if (overflow <= 0) return messages;
    if (direction === 'newer') return messages.slice(overflow);

    const reserveStart = messages.length - HISTORY_RECENT_TAIL_RESERVE;
    const activeLength = reserveStart;
    const retainedActive = messages.slice(0, Math.max(0, activeLength - overflow));
    return [...retainedActive, ...messages.slice(reserveStart)];
  }

  private mergeConversationFiles(conversationId: string, files: ChatMessageUI[]): void {
    const media = files.filter((message) => this.isMediaMessage(message));
    if (media.length === 0) return;
    this.rememberMessageIdentities(media);
    this.state.update((s) => {
      const next = new Map(s.files);
      const merged = orderMessages(
        dedupeMessages([...(next.get(conversationId) ?? []), ...media]),
      ).reverse();
      next.set(conversationId, merged);
      return { ...s, files: next };
    });
  }

  private removeConversationFile(conversationId: string, messageId: string): void {
    this.state.update((s) => {
      const current = s.files.get(conversationId);
      if (!current?.some((message) => message.id === messageId)) return s;

      const next = new Map(s.files);
      const remaining = current.filter((message) => message.id !== messageId);
      if (remaining.length > 0) {
        next.set(conversationId, remaining);
      } else {
        next.delete(conversationId);
      }
      return { ...s, files: next };
    });
  }

  private isMediaMessage(message: ChatMessageUI): boolean {
    if (message.isDeleted) return false;
    return message.type === 'IMAGE' || message.type === 'FILE' || !!message.mimeType?.startsWith('audio/') || !!message.mimeType?.startsWith('image/');
  }

  private handleMessageReadUpdate(payload: MessagingSdk.MessageReadUpdatePayload): void {
    if (!payload.conversationId || !Number.isFinite(payload.lastReadSequence) || !Number.isFinite(payload.userId)) return;
    const conversationReads = this.readReceiptsByConversation.get(payload.conversationId) ?? new Map<number, number>();
    const previousSequence = conversationReads.get(payload.userId) ?? -1;
    conversationReads.set(
      payload.userId,
      Math.max(previousSequence, payload.lastReadSequence!),
    );
    this.readReceiptsByConversation.set(payload.conversationId, conversationReads);
    if (this.isGroupConversation(payload.conversationId)) {
      this.applySafeGroupReceiptEvidence(payload.conversationId, payload.lastReadMessageId);
      this.scheduleGroupStatusRefresh(payload.conversationId);
      this.readReceiptUpdated$.next(payload.conversationId);
      return;
    }
    this.updateOwnMessageStatuses(payload.conversationId);
  }

  private handleMessageDeliveredUpdate(payload: MessagingSdk.MessageDeliveredUpdatePayload): void {
    const userId = payload.userId;
    if (!payload.conversationId || !Number.isFinite(payload.lastDeliveredSequence) || typeof userId !== 'number' || !Number.isFinite(userId)) return;
    const conversationDeliveries = this.deliveryReceiptsByConversation.get(payload.conversationId) ?? new Map<number, number>();
    conversationDeliveries.set(
      userId,
      Math.max(
        conversationDeliveries.get(userId) ?? -1,
        payload.lastDeliveredSequence!,
      ),
    );
    this.deliveryReceiptsByConversation.set(payload.conversationId, conversationDeliveries);
    if (this.isGroupConversation(payload.conversationId)) {
      this.applySafeGroupReceiptEvidence(payload.conversationId, payload.messageId);
      this.scheduleGroupStatusRefresh(payload.conversationId);
      return;
    }
    this.updateOwnMessageStatuses(payload.conversationId);
  }

  private mergeSnapshotReadReceipts(snapshot: MessagingSdk.ConversationSnapshot, conversationId: string): Map<number, number> {
    const receipts = new Map(this.readReceiptsByConversation.get(conversationId) ?? []);
    for (const participant of snapshot.participants ?? []) {
      if (Number.isFinite(participant.lastReadSequence)) {
        const previousSequence = receipts.get(participant.userId) ?? -1;
        receipts.set(
          participant.userId,
          Math.max(previousSequence, participant.lastReadSequence!),
        );
      }
    }
    this.readReceiptsByConversation.set(conversationId, receipts);
    return receipts;
  }

  private mergeSnapshotDeliveryReceipts(snapshot: MessagingSdk.ConversationSnapshot, conversationId: string): Map<number, number> {
    const receipts = new Map(this.deliveryReceiptsByConversation.get(conversationId) ?? []);
    for (const participant of snapshot.participants ?? []) {
      if (Number.isFinite(participant.lastDeliveredSequence)) {
        receipts.set(
          participant.userId,
          Math.max(
            receipts.get(participant.userId) ?? -1,
            participant.lastDeliveredSequence!,
          ),
        );
      }
    }
    this.deliveryReceiptsByConversation.set(conversationId, receipts);
    return receipts;
  }

  private isGroupConversation(conversationId: string): boolean {
    return this.state().snapshots.get(conversationId)?.type === 'GROUP';
  }

  private applySafeGroupReceiptEvidence(conversationId: string, messageId: string | null | undefined): void {
    if (!messageId) return;
    this.state.update((s) => {
      const current = s.messages.get(conversationId);
      if (!current) return s;
      const snapshot = s.snapshots.get(conversationId);
      const readReceipts = snapshot
        ? this.mergeSnapshotReadReceipts(snapshot, conversationId)
        : new Map(this.readReceiptsByConversation.get(conversationId) ?? []);
      const eligibleOthers = this.groupReceiptEligibleOthers(snapshot, conversationId, readReceipts);
      let changed = false;
      const nextList = current.map((message) => {
        if (!message.isOwn || message.isDeleted || isLocalMessageId(message.id)) return message;
        if (!Number.isFinite(message.sequence)) return message;

        let status = message.status;
        // Preserve the existing live 'delivered' promotion scoped to the receipt target.
        if (message.id === messageId) {
          status = nextMessageStatus(status, 'delivered');
        }
        // Promote live to 'read' when EVERY eligible reader's read cursor has passed
        // the message, mirroring the Store aggregateMessageStatus semantics.
        const eligible = eligibleOthers.filter((participant) =>
          participant.historyVisibleAfterSequence == null ||
          participant.historyVisibleAfterSequence < message.sequence!,
        );
        if (
          eligible.length > 0 &&
          eligible.every((participant) => (readReceipts.get(participant.userId) ?? -1) >= message.sequence!)
        ) {
          status = nextMessageStatus(status, 'read');
        }
        if (status === message.status) return message;
        changed = true;
        return { ...message, status };
      });
      if (!changed) return s;
      const messages = new Map(s.messages);
      messages.set(conversationId, nextList);
      return { ...s, messages };
    });
  }

  private groupReceiptEligibleOthers(
    snapshot: MessagingSdk.ConversationSnapshot | undefined,
    conversationId: string,
    readReceipts: Map<number, number>,
  ): Array<{ userId: number; historyVisibleAfterSequence: number | null | undefined }> {
    const snapshotParticipants = snapshot?.participants ?? [];
    if (snapshotParticipants.length > 0) {
      return snapshotParticipants
        .filter(
          (participant) =>
            participant.isActive !== false &&
            Number.isFinite(participant.userId) &&
            participant.userId !== this.currentUserId(),
        )
        .map((participant) => ({
          userId: participant.userId,
          historyVisibleAfterSequence: participant.historyVisibleAfterSequence,
        }));
    }
    // Approximation fallback: when the snapshot does not expose the participant list,
    // treat the union of the own userId and every userId present in the local read
    // receipt map as the eligible reader set.
    return Array.from(new Set([this.currentUserId(), ...readReceipts.keys()]))
      .filter((userId) => Number.isFinite(userId) && userId !== this.currentUserId())
      .map((userId) => ({ userId, historyVisibleAfterSequence: null }));
  }

  private scheduleGroupStatusRefresh(conversationId: string, refreshLoadedMessages = false): void {
    if (!this.isGroupConversation(conversationId)) return;
    const loaded = this.state().messages.get(conversationId) ?? [];
    if (!loaded.some((message) => !isLocalMessageId(message.id))) return;

    this.groupStatusRefreshLoadedMessages.set(
      conversationId,
      (this.groupStatusRefreshLoadedMessages.get(conversationId) ?? false) || refreshLoadedMessages,
    );
    if (this.groupStatusRefreshInFlight.has(conversationId) || this.groupStatusRefreshTimers.has(conversationId)) return;

    const retryAttempt = this.groupStatusRefreshAttempts.get(conversationId) ?? 0;
    const retryDelay = Math.min(
      GROUP_STATUS_REFRESH_MAX_DELAY_MS,
      GROUP_STATUS_REFRESH_DEBOUNCE_MS * 2 ** Math.min(retryAttempt, 6),
    );
    const timer = setTimeout(() => {
      this.groupStatusRefreshTimers.delete(conversationId);
      const refreshAll = this.groupStatusRefreshLoadedMessages.get(conversationId) ?? false;
      this.groupStatusRefreshLoadedMessages.delete(conversationId);
      void this.refreshGroupAuthoritativeStatuses(conversationId, refreshAll);
    }, retryDelay);
    this.groupStatusRefreshTimers.set(conversationId, timer);
  }

  private async refreshGroupAuthoritativeStatuses(conversationId: string, refreshLoadedMessages: boolean): Promise<void> {
    if (this.groupStatusRefreshInFlight.has(conversationId) || !this.isGroupConversation(conversationId)) return;
    if (this.getHistoryState(conversationId).isLoading) {
      this.groupStatusRefreshLoadedMessages.set(
        conversationId,
        (this.groupStatusRefreshLoadedMessages.get(conversationId) ?? false) || refreshLoadedMessages,
      );
      this.scheduleGroupStatusRefresh(conversationId);
      return;
    }
    const loaded = (this.state().messages.get(conversationId) ?? []).filter((message) => !isLocalMessageId(message.id));
    if (loaded.length === 0) return;

    const targetMessages = refreshLoadedMessages ? loaded : loaded.slice(-30);
    const remainingIds = new Set(targetMessages.map((message) => message.id));
    const records: MessagingSdk.MessageHistoryRecord[] = [];
    let beforeId: string | null = null;
    this.groupStatusRefreshInFlight.add(conversationId);
    try {
      do {
        const response: MessagingSdk.MessagesFetchedResponse = await lastValueFrom(
          this.port.fetchMessages({
            conversationId,
            beforeId,
            limit: GROUP_STATUS_REFRESH_PAGE_SIZE,
          }).pipe(takeUntil(this.destroy$)),
        );
        if (response.success === false) {
          throw new Error(`messages_fetch_${response.errorCode ?? 'failed'}`);
        }
        if (!this.responseMatchesCurrentVisibility(conversationId, response.visibilityEpoch)) return;
        for (const record of response.messages ?? []) {
          records.push(record);
          remainingIds.delete(record.messageId);
        }
        beforeId = response.hasMore ? response.nextBeforeId ?? null : null;
      } while (refreshLoadedMessages && remainingIds.size > 0 && beforeId !== null);

      this.applyAuthoritativeGroupStatuses(conversationId, records);
      this.groupStatusRefreshAttempts.delete(conversationId);
    } catch (err) {
      if (this.state().snapshots.has(conversationId)) {
        console.error('[ChatState] group status refresh failed', err);
        const attempt = (this.groupStatusRefreshAttempts.get(conversationId) ?? 0) + 1;
        this.groupStatusRefreshAttempts.set(conversationId, attempt);
        this.groupStatusRefreshLoadedMessages.set(conversationId, refreshLoadedMessages);
      }
    } finally {
      this.groupStatusRefreshInFlight.delete(conversationId);
      if (this.groupStatusRefreshLoadedMessages.has(conversationId)) {
        this.scheduleGroupStatusRefresh(conversationId);
      }
    }
  }

  private applyAuthoritativeGroupStatuses(
    conversationId: string,
    records: MessagingSdk.MessageHistoryRecord[],
  ): void {
    const statuses = new Map(
      records
        .filter((record) => record.aggregateStatus != null)
        .map((record) => [record.messageId, record.aggregateStatus!] as const),
    );
    if (statuses.size === 0) return;

    this.state.update((s) => {
      const current = s.messages.get(conversationId);
      if (!current) return s;
      let changed = false;
      const nextList = current.map((message) => {
        const status = statuses.get(message.id);
        if (!status || status === message.status) return message;
        changed = true;
        return { ...message, status };
      });
      if (!changed) return s;

      const messages = new Map(s.messages);
      messages.set(conversationId, nextList);
      const files = new Map(s.files);
      const currentFiles = files.get(conversationId);
      if (currentFiles) {
        files.set(conversationId, currentFiles.map((file) => {
          const status = statuses.get(file.id);
          return status && status !== file.status ? { ...file, status } : file;
        }));
      }
      return { ...s, messages, files };
    });
  }

  private clearGroupStatusRefreshes(): void {
    for (const timer of this.groupStatusRefreshTimers.values()) clearTimeout(timer);
    this.groupStatusRefreshTimers.clear();
    this.groupStatusRefreshInFlight.clear();
    this.groupStatusRefreshLoadedMessages.clear();
    this.groupStatusRefreshAttempts.clear();
  }

  private upsertSnapshot(snap: MessagingSdk.ConversationSnapshot): void {
    if (this.deletedConversationIds.has(snap.conversationId)) return;
    snap = this.rememberSnapshotIdentities(snap);
    const current = this.state().snapshots.get(snap.conversationId);
    if (!this.shouldAcceptSnapshot(current, snap)) return;
    if (this.visibilityEpochChanged(current, snap)) {
      this.purgeConversationVisibilityState(snap.conversationId);
    }
    this.removedMembershipGenerations.delete(snap.conversationId);
    this.state.update((s) => {
      const next = new Map(s.snapshots);
      next.set(snap.conversationId, snap);
      return { ...s, snapshots: next };
    });
    this.reconcileConfirmedReadFromSnapshot(snap);
    this.mergeSnapshotReadReceipts(snap, snap.conversationId); // MOMSG-1081, ver fetchInitialConversations
    this.refreshPresenceForRelevantUsers();
  }

  private removeConversationLocally(conversationId: string): void {
    this.purgeConversationVisibilityState(conversationId);
    this.state.update((s) => {
      const snapshots = new Map(s.snapshots);
      snapshots.delete(conversationId);
      return { ...s, snapshots };
    });

    this.removeTypingStateForConversation(conversationId);
    this.readReceiptsByConversation.delete(conversationId);
    this.deliveryReceiptsByConversation.delete(conversationId);
    const statusRefreshTimer = this.groupStatusRefreshTimers.get(conversationId);
    if (statusRefreshTimer) clearTimeout(statusRefreshTimer);
    this.groupStatusRefreshTimers.delete(conversationId);
    this.groupStatusRefreshInFlight.delete(conversationId);
    this.groupStatusRefreshLoadedMessages.delete(conversationId);
    this.groupStatusRefreshAttempts.delete(conversationId);
    this.clearHistoryLoadRetry(conversationId);
    this.historyLoadRetryAttempts.delete(conversationId);
    this.clearReadState(conversationId);
    for (const [owner, claimedConversationId] of this.readVisibilityClaims) {
      if (claimedConversationId === conversationId) this.readVisibilityClaims.delete(owner);
    }
    this.filesFetchInFlight.delete(conversationId);
    this.filesLoaded.delete(conversationId);
    this.clearLocalTypingStopTimer(conversationId);
    this.localTypingStartedAt.delete(conversationId);

    if (this.selectedIdSignal() === conversationId) {
      this.selectedIdSignal.set(null);
    }
    this.refreshPresenceForRelevantUsers();
  }

  private removeTypingStateForConversation(conversationId: string): void {
    for (const key of Array.from(this.typingFallbackTimers.keys())) {
      if (!key.startsWith(`${conversationId}:`)) continue;
      const timer = this.typingFallbackTimers.get(key);
      if (timer) clearTimeout(timer);
      this.typingFallbackTimers.delete(key);
    }

    this.typingSignal.update((prev) => {
      if (!prev.has(conversationId)) return prev;
      const next = new Map(prev);
      next.delete(conversationId);
      return next;
    });
  }

  private markConversationAsRead(conversationId: string): void {
    if (document.visibilityState !== 'visible') return;
    if (!this.hasReadVisibilityClaim(conversationId)) return;
    if (!this.readySignal()) return;
    const snapshot = this.state().snapshots.get(conversationId);
    const messages = this.state().messages.get(conversationId) ?? (snapshot ? mapSnapshotToConversation(snapshot, this.currentUserId()).messages : undefined);
    if (!messages || messages.length === 0) return;
    const currentUserId = this.currentUserId();
    const lastReadable = [...messages].reverse().find((message) => {
      if (!message?.id || isLocalMessageId(message.id)) return false;
      if (message.isOwn) return false;
      const senderId = Number(message.senderId);
      return !Number.isFinite(senderId) || senderId !== currentUserId;
    });
    if (!lastReadable) return;
    const existing = this.readStates.get(conversationId);
    const sequence = Number.isFinite(lastReadable.sequence) ? lastReadable.sequence! : null;
    const readState = existing ?? {
      desiredMessageId: '',
      desiredSequence: null,
      confirmedSequence: this.snapshotOwnReadSequence(snapshot),
      confirmedMessageId: this.snapshotOwnReadMessageId(snapshot),
      inFlight: null,
      retryAttempts: 0,
      retryTimer: null,
      unreadBackup: null,
      exhausted: false,
    };
    if (sequence === null) {
      readState.desiredMessageId = lastReadable.id;
      readState.desiredSequence = null;
      readState.retryAttempts = 0;
      readState.exhausted = false;
      this.readStates.set(conversationId, readState);
      return;
    }
    if (readState.desiredSequence === null || sequence > readState.desiredSequence) {
      readState.desiredMessageId = lastReadable.id;
      readState.desiredSequence = sequence;
      readState.retryAttempts = 0;
      readState.exhausted = false;
    }
    if (readState.exhausted) return;
    if (readState.unreadBackup === null && (snapshot?.unreadCount ?? 0) > 0) {
      readState.unreadBackup = snapshot!.unreadCount;
    }
    this.readStates.set(conversationId, readState);
    this.resetUnreadCount(conversationId);
    this.sendDesiredRead(conversationId, readState);
  }

  private sendDesiredRead(conversationId: string, state: PendingReadState): void {
    if (state.exhausted) return;
    if (state.desiredSequence === null || state.inFlight || state.retryTimer) return;
    if (state.confirmedSequence !== null && state.desiredSequence <= state.confirmedSequence) return;
    if (!this.socketConnectedSignal() || document.visibilityState !== 'visible' || !this.hasReadVisibilityClaim(conversationId)) return;
    if (state.retryAttempts >= READ_RETRY_MAX_ATTEMPTS) {
      this.exhaustReadRetries(conversationId, state);
      return;
    }

    const commandId = globalThis.crypto?.randomUUID?.() ?? `read-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const timeout = setTimeout(() => this.failReadAttempt(conversationId, commandId, 'result_timeout'), READ_RESULT_TIMEOUT_MS);
    state.inFlight = { commandId, sequence: state.desiredSequence, messageId: state.desiredMessageId, timeout };
    const ack$ = this.port.markAsRead({
      commandId,
      conversationId,
      lastReadMessageId: state.desiredMessageId,
      lastReadSequence: state.desiredSequence,
    });
    if (!ack$ || typeof (ack$ as { subscribe?: unknown }).subscribe !== 'function') return;
    ack$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (ack) => {
        if (ack.status === 'ERROR') this.failReadAttempt(conversationId, commandId, `ack_${ack.reasonCode ?? 'error'}`);
      },
      error: () => this.failReadAttempt(conversationId, commandId, 'ack_transport_error'),
    });
  }

  private handleMessageReadResult(result: MessagingSdk.MessageReadResultPayload): void {
    const state = this.readStates.get(result.conversationId);
    if (!state || state.inFlight?.commandId !== result.commandId) return;
    const completedSequence = state.inFlight.sequence;
    clearTimeout(state.inFlight.timeout);
    state.inFlight = null;
    if (result.status === 'REJECTED') {
      this.scheduleReadRetry(result.conversationId, state, `store_${result.reasonCode ?? 'rejected'}`);
      if (this.isGroupConversation(result.conversationId)) this.scheduleGroupStatusRefresh(result.conversationId, true);
      return;
    }

    const confirmedSequence = result.currentReadSequence ?? result.confirmedSequence ?? completedSequence;
    state.confirmedSequence = state.confirmedSequence === null
      ? confirmedSequence
      : Math.max(state.confirmedSequence, confirmedSequence);
    state.confirmedMessageId = result.currentReadMessageId ?? result.confirmedMessageId;
    state.retryAttempts = 0;
    state.unreadBackup = null;
    state.exhausted = false;
    if (this.isGroupConversation(result.conversationId)) this.scheduleGroupStatusRefresh(result.conversationId, true);
    this.sendDesiredRead(result.conversationId, state);
  }

  private failReadAttempt(conversationId: string, commandId: string, reason: string): void {
    const state = this.readStates.get(conversationId);
    if (!state || state.inFlight?.commandId !== commandId) return;
    clearTimeout(state.inFlight.timeout);
    state.inFlight = null;
    this.scheduleReadRetry(conversationId, state, reason);
  }

  private scheduleReadRetry(conversationId: string, state: PendingReadState, reason: string): void {
    state.retryAttempts += 1;
    console.warn('[ChatState] read receipt retry scheduled', { conversationId, reason, attempt: state.retryAttempts });
    if (document.visibilityState !== 'visible' || !this.hasReadVisibilityClaim(conversationId)) {
      if (state.unreadBackup !== null) this.restoreUnreadCount(conversationId, state.unreadBackup);
      this.fetchInitialConversations();
      return;
    }
    if (state.retryAttempts >= READ_RETRY_MAX_ATTEMPTS) {
      this.exhaustReadRetries(conversationId, state);
      return;
    }
    const delay = Math.min(4_000, READ_RETRY_BASE_MS * 2 ** (state.retryAttempts - 1));
    state.retryTimer = setTimeout(() => {
      state.retryTimer = null;
      this.sendDesiredRead(conversationId, state);
    }, delay);
  }

  private exhaustReadRetries(conversationId: string, state: PendingReadState): void {
    if (state.exhausted) return;
    state.exhausted = true;
    console.warn('[ChatState] read retries exhausted; pausing read emission until next trigger', {
      conversationId,
      desiredMessageId: state.desiredMessageId,
      desiredSequence: state.desiredSequence,
    });
    console.error('[ChatState] read receipt retries exhausted', { conversationId, attempts: state.retryAttempts });
    if (state.unreadBackup !== null) this.restoreUnreadCount(conversationId, state.unreadBackup);
    this.fetchInitialConversations();
  }

  private retryVisibleReads(trigger: string): void {
    for (const conversationId of new Set(this.readVisibilityClaims.values())) {
      const state = this.readStates.get(conversationId);
      if (state?.retryTimer) {
        clearTimeout(state.retryTimer);
        state.retryTimer = null;
      }
      // Re-arm previously exhausted conversations so a reconnect, focus, or visibility
      // trigger can re-attempt emission instead of leaving the read cursor frozen.
      if (state) {
        const wasExhausted = state.exhausted;
        state.exhausted = false;
        if (state.retryAttempts >= READ_RETRY_MAX_ATTEMPTS) state.retryAttempts = 0;
        if (wasExhausted) {
          console.warn('[ChatState] read retries re-armed after previous exhaustion', { conversationId, trigger });
        }
      }
      if (state && state.retryAttempts < READ_RETRY_MAX_ATTEMPTS) {
        console.debug('[ChatState] retrying pending read', { conversationId, trigger });
        this.sendDesiredRead(conversationId, state);
      } else {
        this.markConversationAsRead(conversationId);
      }
    }
  }

  private releaseReadCommandsForRetry(): void {
    for (const state of this.readStates.values()) {
      if (!state.inFlight) continue;
      clearTimeout(state.inFlight.timeout);
      state.inFlight = null;
    }
  }

  private hasReadVisibilityClaim(conversationId: string): boolean {
    return Array.from(this.readVisibilityClaims.values()).includes(conversationId);
  }

  private reconcileConfirmedReadFromSnapshot(snapshot: MessagingSdk.ConversationSnapshot): void {
    const confirmedSequence = this.snapshotOwnReadSequence(snapshot);
    const confirmedMessageId = this.snapshotOwnReadMessageId(snapshot);
    const state = this.readStates.get(snapshot.conversationId);
    if (!state || confirmedSequence === null) return;
    state.confirmedSequence = state.confirmedSequence === null
      ? confirmedSequence
      : Math.max(state.confirmedSequence, confirmedSequence);
    state.confirmedMessageId = confirmedMessageId;
    if (state.desiredSequence !== null && state.confirmedSequence >= state.desiredSequence) {
      if (state.inFlight) clearTimeout(state.inFlight.timeout);
      if (state.retryTimer) clearTimeout(state.retryTimer);
      state.inFlight = null;
      state.retryTimer = null;
      state.retryAttempts = 0;
      state.unreadBackup = null;
      state.exhausted = false;
    }
  }

  private snapshotOwnReadSequence(snapshot: MessagingSdk.ConversationSnapshot | undefined): number | null {
    const participant = snapshot?.participants?.find((candidate) => candidate.userId === this.currentUserId());
    return Number.isFinite(participant?.lastReadSequence) ? participant!.lastReadSequence! : null;
  }

  private snapshotOwnReadMessageId(snapshot: MessagingSdk.ConversationSnapshot | undefined): string | null {
    return snapshot?.participants?.find((candidate) => candidate.userId === this.currentUserId())?.lastReadMessageId ?? null;
  }

  private clearReadState(conversationId: string): void {
    const state = this.readStates.get(conversationId);
    if (state?.inFlight) clearTimeout(state.inFlight.timeout);
    if (state?.retryTimer) clearTimeout(state.retryTimer);
    this.readStates.delete(conversationId);
  }

  private clearReadStates(): void {
    for (const conversationId of this.readStates.keys()) this.clearReadState(conversationId);
  }

  private restoreUnreadCount(conversationId: string, unreadCount: number): void {
    this.state.update((current) => {
      const snapshot = current.snapshots.get(conversationId);
      if (!snapshot) return current;
      const snapshots = new Map(current.snapshots);
      snapshots.set(conversationId, { ...snapshot, unreadCount });
      return { ...current, snapshots };
    });
  }

  private visibilityEpochChanged(
    current: MessagingSdk.ConversationSnapshot | undefined,
    incoming: MessagingSdk.ConversationSnapshot,
  ): boolean {
    if (!current) return false;
    const currentEpoch = current.visibility?.epoch ?? null;
    const incomingEpoch = incoming.visibility?.epoch ?? null;
    return currentEpoch !== incomingEpoch && incomingEpoch !== null;
  }

  private shouldAcceptSnapshot(
    current: MessagingSdk.ConversationSnapshot | undefined,
    incoming: MessagingSdk.ConversationSnapshot,
  ): boolean {
    const incomingGeneration = incoming.visibility?.generation ?? 0;
    const removedGeneration = this.removedMembershipGenerations.get(incoming.conversationId);
    if (removedGeneration !== undefined && incomingGeneration <= removedGeneration) return false;
    if (!current) return removedGeneration === undefined || incomingGeneration > removedGeneration;

    const currentEpoch = current.visibility?.epoch ?? null;
    const incomingEpoch = incoming.visibility?.epoch ?? null;
    const currentGeneration = current.visibility?.generation ?? 0;
    if (currentEpoch && !incomingEpoch) return false;
    if (incomingGeneration < currentGeneration) return false;
    if (incomingGeneration === currentGeneration && currentEpoch !== incomingEpoch) return false;
    return true;
  }

  private responseMatchesCurrentVisibility(
    conversationId: string,
    responseEpoch: string | null | undefined,
  ): boolean {
    if (this.removedMembershipGenerations.has(conversationId)) return false;
    const currentEpoch = this.state().snapshots.get(conversationId)?.visibility?.epoch ?? null;
    return currentEpoch === null ? responseEpoch == null : responseEpoch === currentEpoch;
  }

  private purgeConversationVisibilityState(conversationId: string): void {
    this.clearPendingMessageReactionsForConversation(conversationId, false);
    this.state.update((s) => {
      const messages = new Map(s.messages);
      const history = new Map(s.history);
      const files = new Map(s.files);
      const existingMessages = messages.get(conversationId);
      if (existingMessages) this.revokeLocalObjectUrls(existingMessages);
      messages.delete(conversationId);
      history.delete(conversationId);
      files.delete(conversationId);
      return { ...s, messages, history, files };
    });

    this.readReceiptsByConversation.delete(conversationId);
    this.deliveryReceiptsByConversation.delete(conversationId);
    this.clearReadState(conversationId);
    this.historyNewerNoProgressAnchors.delete(conversationId);
    for (let index = this.pendingMessageMutations.length - 1; index >= 0; index -= 1) {
      const pending = this.pendingMessageMutations[index];
      if (pending.conversationId !== conversationId) continue;
      clearTimeout(pending.timeout);
      this.pendingMessageMutations.splice(index, 1);
    }
    for (let index = this.optimisticGroupBackups.length - 1; index >= 0; index -= 1) {
      const backup = this.optimisticGroupBackups[index];
      if (backup.conversationId !== conversationId) continue;
      if (backup.timeout) clearTimeout(backup.timeout);
      this.optimisticGroupBackups.splice(index, 1);
    }
    for (const [key, intent] of this.latestMessageMutationIntents) {
      if (intent.conversationId === conversationId) {
        this.latestMessageMutationIntents.delete(key);
      }
    }
    for (const key of this.messageMutationOccurredAt.keys()) {
      if (key.includes(`|${conversationId}|`)) this.messageMutationOccurredAt.delete(key);
    }
    for (const key of this.roleMutationOccurredAt.keys()) {
      if (key.startsWith(`${conversationId}|`)) this.roleMutationOccurredAt.delete(key);
    }
    this.removeTypingStateForConversation(conversationId);
    this.visibilityEpochChanged$.next(conversationId);
  }

  private hasCurrentMessage(conversationId: string, messageId: string): boolean {
    if (!messageId) return false;
    const state = this.state();
    if ((state.messages.get(conversationId) ?? []).some((message) => message.id === messageId)) {
      return true;
    }
    const snapshot = state.snapshots.get(conversationId);
    return snapshot?.lastMessage?.messageId === messageId ||
      (snapshot?.pinnedMessages ?? []).some((message) => message.messageId === messageId);
  }

  private normalizeReplyToForSend(conversationId: string, replyTo?: ChatMessageReplyUI | null): ChatMessageReplyUI | null {
    if (!replyTo) return null;

    let messageId = replyTo.messageId;
    if (isLocalMessageId(messageId)) {
      const canonicalMessageId = this.resolveConfirmedMessageId(conversationId, messageId);
      if (!canonicalMessageId) {
        console.warn('[ChatState] reply ignored: target message is not persisted yet', { conversationId, messageId });
        return null;
      }
      messageId = canonicalMessageId;
    }

    const senderId = Number(replyTo.senderId);
    if (!Number.isFinite(senderId)) {
      console.warn('[ChatState] reply ignored: target senderId is not canonical', { conversationId, replyTo });
      return null;
    }

    return { ...replyTo, messageId, senderId: String(senderId) };
  }

  private rememberPendingMessageMutation(event: PendingMessageMutationEvent, conversationId: string, messageId: string, action?: PendingMessageMutationAction, newText?: string): number {
    this.pruneLatestMessageMutationIntents();
    const token = ++this.pendingMessageMutationTokenSeq;
    const createdAtMs = Date.now();
    const snapshot = this.state().snapshots.get(conversationId);
    const backupMessage = this.state().messages.get(conversationId)?.find((message) => message.id === messageId) ?? (snapshot ? mapSnapshotToConversation(snapshot, this.currentUserId()).messages.find((message) => message.id === messageId) : null) ?? this.buildBackupMessageFromPinnedSnapshot(snapshot, messageId) ?? null;
    const timeout = setTimeout(() => this.rollbackPendingMessageMutation(token, 'confirmation_timeout'), MUTATION_CONFIRMATION_TIMEOUT_MS);

    this.pendingMessageMutations.push({
      token,
      event,
      action,
      conversationId,
      messageId,
      backupMessage: backupMessage ? cloneChatMessage(backupMessage) : null,
      optimisticMessage: null,
      backupLastMessage: snapshot?.lastMessage ? { ...snapshot.lastMessage } : snapshot?.lastMessage,
      backupUpdatedAt: snapshot?.updatedAt ?? '',
      optimisticLastMessage: undefined,
      optimisticUpdatedAt: undefined,
      backupPinnedMessages: snapshot?.pinnedMessages ? [...snapshot.pinnedMessages] : snapshot?.pinnedMessages,
      optimisticPinnedMessages: undefined,
      timeout,
    });

    this.latestMessageMutationIntents.set(this.messageMutationIntentKey(event, conversationId, messageId), {
      token,
      event,
      action,
      newText,
      conversationId,
      messageId,
      createdAtMs,
    });

    return token;
  }

  private rememberPendingMessageMutationOptimisticState(token: number): void {
    const pending = this.pendingMessageMutations.find((item) => item.token === token);
    if (!pending) return;

    const snapshot = this.state().snapshots.get(pending.conversationId);
    const optimisticMessage = this.state().messages.get(pending.conversationId)?.find((message) => message.id === pending.messageId) ?? (snapshot ? mapSnapshotToConversation(snapshot, this.currentUserId()).messages.find((message) => message.id === pending.messageId) : null) ?? null;
    pending.optimisticMessage = optimisticMessage ? cloneChatMessage(optimisticMessage) : this.buildOptimisticBackupMessageFromSnapshot(pending, snapshot);
    pending.optimisticLastMessage = snapshot?.lastMessage ? { ...snapshot.lastMessage } : snapshot?.lastMessage;
    pending.optimisticUpdatedAt = snapshot?.updatedAt;
    pending.optimisticPinnedMessages = snapshot?.pinnedMessages ? [...snapshot.pinnedMessages] : snapshot?.pinnedMessages;
  }

  private buildOptimisticBackupMessageFromSnapshot(pending: PendingMessageMutation, snapshot: MessagingSdk.ConversationSnapshot | undefined): ChatMessageUI | null {
    if (!pending.backupMessage || !snapshot) return null;
    const contentPreview = snapshot.lastMessage?.messageId === pending.messageId ? snapshot.lastMessage.contentPreview : snapshot.pinnedMessages?.find((item) => item.messageId === pending.messageId)?.contentPreview;
    if (contentPreview === undefined) return null;
    return {
      ...cloneChatMessage(pending.backupMessage),
      content: contentPreview ?? '',
      isEdited: pending.event === 'chat:message:edit' ? true : pending.backupMessage.isEdited,
      isDeleted: pending.event === 'chat:message:delete' ? true : pending.backupMessage.isDeleted,
      isPinned: pending.event === 'chat:message:pin' ? pending.action === 'pin' : pending.backupMessage.isPinned,
    };
  }

  private buildBackupMessageFromPinnedSnapshot(snapshot: MessagingSdk.ConversationSnapshot | undefined, messageId: string): ChatMessageUI | null {
    const pinned = snapshot?.pinnedMessages?.find((item) => item.messageId === messageId);
    if (!pinned) return null;
    return {
      id: pinned.messageId,
      senderId: String(pinned.senderId),
      senderName: formatChatDisplayName({ senderName: pinned.senderName }),
      content: pinned.contentPreview ?? '',
      type: pinned.type,
      timestamp: pinned.sentAt,
      status: 'delivered',
      isOwn: pinned.senderId === this.currentUserId(),
      isPinned: true,
      reactions: [],
      reactionsRevision: 0,
      serverSentAt: pinned.sentAt,
    };
  }

  private confirmPendingMessageMutation(event: PendingMessageMutationEvent, conversationId: string, messageId: string, confirmation: { action?: PendingMessageMutationAction; newText?: string } = {}): void {
    for (let index = this.pendingMessageMutations.length - 1; index >= 0; index--) {
      const pending = this.pendingMessageMutations[index];
      if (pending.event !== event || pending.conversationId !== conversationId || pending.messageId !== messageId) continue;
      if (event === 'chat:message:pin' && pending.action !== confirmation.action) continue;
      if (event === 'chat:message:edit' && confirmation.newText !== undefined && pending.optimisticMessage?.content !== confirmation.newText) continue;
      clearTimeout(pending.timeout);
      this.pendingMessageMutations.splice(index, 1);
    }
  }

  private isStaleLocalMessageMutationConfirmation(event: PendingMessageMutationEvent, conversationId: string, messageId: string, confirmation: { action?: PendingMessageMutationAction; newText?: string; actorUserId?: number; occurredAt?: string }): boolean {
    if (confirmation.actorUserId !== this.currentUserId()) return false;
    this.pruneLatestMessageMutationIntents();

    const latest = this.latestMessageMutationIntents.get(this.messageMutationIntentKey(event, conversationId, messageId));
    if (!latest) return false;

    if (event === 'chat:message:edit' && latest.newText === confirmation.newText) return false;
    if (event === 'chat:message:pin' && latest.action === confirmation.action) return false;

    const newerIntentStillPending = this.pendingMessageMutations.some((pending) => pending.event === event && pending.conversationId === conversationId && pending.messageId === messageId && pending.token === latest.token);
    if (newerIntentStillPending) return true;

    const occurredAtMs = confirmation.occurredAt ? Date.parse(confirmation.occurredAt) : Number.NaN;
    if (Number.isFinite(occurredAtMs)) {
      return occurredAtMs <= latest.createdAtMs;
    }

    return true;
  }

  private messageMutationIntentKey(event: PendingMessageMutationEvent, conversationId: string, messageId: string): string {
    return `${event}|${conversationId}|${messageId}`;
  }

  private pruneLatestMessageMutationIntents(nowMs = Date.now()): void {
    for (const [key, intent] of this.latestMessageMutationIntents) {
      if (nowMs - intent.createdAtMs > MUTATION_INTENT_STALE_GUARD_MS) {
        this.latestMessageMutationIntents.delete(key);
      }
    }
  }

  private rollbackPendingMessageMutation(token: number, reason: 'emit_failed' | 'confirmation_timeout'): void {
    const index = this.pendingMessageMutations.findIndex((pending) => pending.token === token);
    if (index === -1) return;
    const [pending] = this.pendingMessageMutations.splice(index, 1);
    const latestIntentKey = this.messageMutationIntentKey(pending.event, pending.conversationId, pending.messageId);
    if (this.latestMessageMutationIntents.get(latestIntentKey)?.token === pending.token) {
      this.latestMessageMutationIntents.delete(latestIntentKey);
    }
    clearTimeout(pending.timeout);
    console.warn('[ChatState] rolling back unconfirmed message mutation', {
      reason,
      event: pending.event,
      conversationId: pending.conversationId,
      messageId: pending.messageId,
    });

    this.state.update((s) => {
      const messages = new Map(s.messages);
      const list = messages.get(pending.conversationId);
      const currentMessage = list?.find((message) => message.id === pending.messageId) ?? null;
      const canRestoreMessage = !!pending.backupMessage && !!currentMessage && !!pending.optimisticMessage && chatMessagesEqualForRollback(currentMessage, pending.optimisticMessage);
      if (list && canRestoreMessage) {
        messages.set(
          pending.conversationId,
          list.map((message) => (message.id === pending.messageId ? pending.backupMessage! : message)),
        );
      }
      if (list && pending.backupMessage && pending.optimisticMessage) {
        // MOMSG-1172: preview del "responder a" citando UN mensaje puntual —
        // nunca resume como álbum (ver individualMessagePreview).
        const optimisticPreview = individualMessagePreview(pending.optimisticMessage);
        const backupPreview = individualMessagePreview(pending.backupMessage);
        messages.set(
          pending.conversationId,
          (messages.get(pending.conversationId) ?? list).map((message) => {
            if (message.replyTo?.messageId !== pending.messageId) return message;
            if (message.replyTo.contentPreview !== optimisticPreview) return message;
            return {
              ...message,
              replyTo: {
                ...message.replyTo,
                contentPreview: backupPreview,
              },
            };
          }),
        );
      }

      const snapshots = new Map(s.snapshots);
      const snapshot = snapshots.get(pending.conversationId);
      if (snapshot) {
        let nextSnapshot = snapshot;
        if (lastMessageEquals(snapshot.lastMessage, pending.optimisticLastMessage) && snapshot.updatedAt === pending.optimisticUpdatedAt) {
          nextSnapshot = {
            ...nextSnapshot,
            lastMessage: pending.backupLastMessage,
            updatedAt: pending.backupUpdatedAt,
          };
        }
        if (pinnedMessagesEqual(snapshot.pinnedMessages, pending.optimisticPinnedMessages)) {
          nextSnapshot = {
            ...nextSnapshot,
            pinnedMessages: pending.backupPinnedMessages,
          };
        }
        snapshots.set(pending.conversationId, nextSnapshot);
      }

      return { ...s, messages, snapshots };
    });
  }

  private clearPendingMessageMutationTimeouts(): void {
    for (const pending of this.pendingMessageMutations) clearTimeout(pending.timeout);
    this.pendingMessageMutations.length = 0;
    for (const pending of this.pendingConversationPinMutations) clearTimeout(pending.timeout);
    this.pendingConversationPinMutations.length = 0;
  }

  private rememberPendingConversationPinMutation(conversationId: string, action: MessagingSdk.PinAction): number {
    this.pruneLatestConversationPinMutationIntents();
    const token = ++this.pendingConversationPinMutationTokenSeq;
    const createdAtMs = Date.now();
    const snapshot = this.state().snapshots.get(conversationId);
    const backupPinnedAt = snapshot?.participants?.find((p) => p.userId === this.currentUserId())?.pinnedAt;
    const timeout = setTimeout(() => this.rollbackPendingConversationPinMutation(token, 'confirmation_timeout'), MUTATION_CONFIRMATION_TIMEOUT_MS);

    this.pendingConversationPinMutations.push({ token, conversationId, action, backupPinnedAt, timeout, createdAtMs });
    this.latestConversationPinMutationIntents.set(conversationId, { token, action, createdAtMs });

    return token;
  }

  private confirmPendingConversationPinMutation(conversationId: string, action: MessagingSdk.PinAction): void {
    const index = this.pendingConversationPinMutations.findIndex((pending) => pending.conversationId === conversationId && pending.action === action);
    if (index === -1) return;
    const [pending] = this.pendingConversationPinMutations.splice(index, 1);
    // Only forget the tracked intent once it was both the latest recorded one AND there is no
    // older still-pending mutation left for this conversation (e.g. an out-of-order late
    // confirmation for a superseded local intent). Otherwise a later, legitimate remote
    // confirmation (multi-device sync) would be wrongly matched against a stale intent that
    // was never cleared, or a genuinely stale late confirmation would be wrongly accepted.
    const hasOtherPendingForConversation = this.pendingConversationPinMutations.some((other) => other.conversationId === pending.conversationId);
    if (!hasOtherPendingForConversation && this.latestConversationPinMutationIntents.get(pending.conversationId)?.token === pending.token) {
      this.latestConversationPinMutationIntents.delete(pending.conversationId);
    }
    clearTimeout(pending.timeout);
  }

  private isStaleLocalConversationPinConfirmation(conversationId: string, action: MessagingSdk.PinAction): boolean {
    this.pruneLatestConversationPinMutationIntents();
    const latest = this.latestConversationPinMutationIntents.get(conversationId);
    if (!latest) return false;
    if (latest.action === action) return false;

    return true;
  }

  private pruneLatestConversationPinMutationIntents(nowMs = Date.now()): void {
    for (const [key, intent] of this.latestConversationPinMutationIntents) {
      if (nowMs - intent.createdAtMs > MUTATION_INTENT_STALE_GUARD_MS) {
        this.latestConversationPinMutationIntents.delete(key);
      }
    }
  }

  private rollbackPendingConversationPinMutation(token: number, reason: 'emit_failed' | 'confirmation_timeout'): void {
    const index = this.pendingConversationPinMutations.findIndex((pending) => pending.token === token);
    if (index === -1) return;
    const [pending] = this.pendingConversationPinMutations.splice(index, 1);
    if (this.latestConversationPinMutationIntents.get(pending.conversationId)?.token === pending.token) {
      this.latestConversationPinMutationIntents.delete(pending.conversationId);
    }
    clearTimeout(pending.timeout);

    // Si un refetch completo de conversaciones (ej. al reconectar el socket
    // tras un corte) ya aplicó datos frescos DESPUÉS de que este pending se
    // armó, ese refetch ya trajo el pinnedAt real del servidor — más
    // confiable que este backup local, capturado antes del toggle. Revertir
    // igual pisaría un valor correcto recién llegado con uno viejo.
    if (pending.createdAtMs < this.lastConversationsSnapshotAppliedAtMs) {
      console.warn('[ChatState] skipping conversation pin rollback — a full conversations refresh already applied fresher data', {
        reason,
        conversationId: pending.conversationId,
        action: pending.action,
      });
      return;
    }

    // El servidor emite chat:conversation:pinned "fire-and-forget" (sin ack):
    // si el socket está caído justo cuando el timer de 8s dispara, no hay forma
    // de saber si la confirmación se perdió en el corte o si el pin nunca llegó
    // a procesarse. Revertir al backup local acá sería adivinar a ciegas —
    // mejor dejar el valor optimista como está y confiar en el refetch completo
    // que dispara la reconexión (ver fetchInitialConversations) para establecer
    // el estado real.
    if (!this.socketConnectedSignal()) {
      console.warn('[ChatState] deferring conversation pin rollback — socket is currently disconnected, waiting for reconnect refresh instead', {
        reason,
        conversationId: pending.conversationId,
        action: pending.action,
      });
      return;
    }

    // MOMSG-1021: no confiamos más en el backup local como verdad — es una
    // foto de ANTES del toggle, y puede estar tan desactualizada como el
    // valor optimista que estamos por pisar. La confirmación puntual es
    // fire-and-forget (sin ack) y puede perderse por motivos ajenos al
    // código (blip de red/VPN del entorno, no solo desconexión detectada).
    // En vez de adivinar, pedimos el estado real al servidor — fetchInitialConversations()
    // ya sabe reconciliar con cualquier otra mutación todavía pendiente
    // (ver preserveInFlightPinnedAt) y es la misma fuente que ya usa el
    // flujo de reconexión.
    console.warn('[ChatState] unconfirmed conversation pin mutation — verifying real state from server instead of guessing', {
      reason,
      conversationId: pending.conversationId,
      action: pending.action,
    });
    // forceTrustPinnedAtFor: si OTRA mutación de pin para esta MISMA
    // conversación sigue pendiente (ej. un pin y un unpin en cola), el
    // guard preserveInFlightPinnedAt normalmente protegería el pinnedAt
    // local pensando que hay algo más "en vuelo" — pero acá ya decidimos
    // que no confiamos en ningún valor local para esta conversación,
    // así que el fetch debe ganar sin excepciones.
    this.fetchInitialConversations(new Set([pending.conversationId]));
  }

  private acceptMutationTimestamp(target: Map<string, number>, key: string, occurredAt: string | null | undefined): boolean {
    const occurredAtMs = occurredAt ? Date.parse(occurredAt) : Number.NaN;
    if (!Number.isFinite(occurredAtMs)) return true;
    const latest = target.get(key);
    if (latest !== undefined && occurredAtMs <= latest) return false;
    target.set(key, occurredAtMs);
    return true;
  }

  private flushQueuedRoleCommand(): void {
    if (!this.queuedRoleCommand || !this.socketConnectedSignal()) return;
    this.sendRoleCommand(this.queuedRoleCommand);
  }

  private sendRoleCommand(command: QueuedRoleCommand): void {
    try {
      this.port.updateGroupMemberRole(command.payload);
      if (this.queuedRoleCommand?.payload.commandId === command.payload.commandId) this.queuedRoleCommand = null;
    } catch (error) {
      console.error('[ChatState] role command emit failed', error);
      this.rollbackOptimisticGroupBackup(command.token);
    }
  }

  private ensureSocketActionReady(action: string): boolean {
    if (this.readySignal() && this.socketConnectedSignal()) return true;
    console.warn(`[ChatState] ${action}: chat socket is not ready or not connected`);
    return false;
  }

  private canCurrentUserManageGroup(conversationId: string): boolean {
    const snap = this.state().snapshots.get(conversationId);
    if (!snap || snap.type !== 'GROUP') return false;
    const currentUserId = this.currentUserId();
    return (snap.participants ?? []).some(
      (participant) =>
        participant.userId === currentUserId &&
        (participant.role === 'ADMIN' || participant.role === 'MODERATOR') &&
        participant.isActive === true,
    );
  }

  private canCurrentUserDeleteGroup(conversationId: string): boolean {
    return this.canCurrentUserAdminGroup(conversationId);
  }

  private canCurrentUserDesignateModerator(conversationId: string): boolean {
    return this.canCurrentUserManageGroup(conversationId);
  }

  private canCurrentUserAdminGroup(conversationId: string): boolean {
    const snap = this.state().snapshots.get(conversationId);
    if (!snap || snap.type !== 'GROUP') return false;
    const currentUserId = this.currentUserId();
    return (snap.participants ?? []).some(
      (participant) =>
        participant.userId === currentUserId &&
        participant.role === 'ADMIN' &&
        participant.isActive === true,
    );
  }

  private currentUserId(): number {
    return this.sessionCtl.getUserId() ?? 0;
  }

  private currentEmployeeId(): number {
    return this.sessionCtl.getEmployeeId() ?? 0;
  }
}

// ── Pure helpers (presence overlay) ────────────────────────────────────────
// Combinan el snapshot recibido por el init con el mapa de presencia
// reactivo, sin mutar nada del state interno.

/**
 * Aplica presencia real al contact derivado de un DIRECT snapshot.
 * Si no hay info de presencia para el otro participante, conserva el
 * estado del snapshot (que viene del último valor conocido del server).
 */
function overlayPresenceOnContact(base: ChatContactUI, snap: MessagingSdk.ConversationSnapshot, currentUserId: number, presence: Map<number, UserPresence>): ChatContactUI {
  const other = (snap.participants ?? []).find((p) => p.userId !== currentUserId);
  if (!other) return base;

  const live = presence.get(other.userId);
  if (!live) return base;

  return {
    ...base,
    isConnected: live.isOnline,
    lastSeen: live.isOnline ? undefined : formatLastSeen(live.lastSeen),
  };
}

interface PendingReadState {
  desiredMessageId: string;
  desiredSequence: number | null;
  confirmedSequence: number | null;
  confirmedMessageId: string | null;
  inFlight: { commandId: string; sequence: number; messageId: string; timeout: ReturnType<typeof setTimeout> } | null;
  retryAttempts: number;
  retryTimer: ReturnType<typeof setTimeout> | null;
  unreadBackup: number | null;
  exhausted: boolean;
}

function overlayPresenceOnConversation(base: ChatConversationUI, snap: MessagingSdk.ConversationSnapshot, currentUserId: number, presence: Map<number, UserPresence>): ChatConversationUI {
  if (snap.type !== 'DIRECT' || !base.contact) return base;
  return { ...base, contact: overlayPresenceOnContact(base.contact, snap, currentUserId, presence) };
}

/**
 * Aplica presencia real a un contact de miembro de grupo. El id del
 * contact en este caso es el userId stringificado (ver
 * mapParticipantsToContacts).
 */
function overlayPresenceOnGroupContact(base: ChatContactUI, presence: Map<number, UserPresence>): ChatContactUI {
  const userId = Number(base.id);
  if (!Number.isFinite(userId)) return base;
  const live = presence.get(userId);
  if (!live) return base;

  return {
    ...base,
    isConnected: live.isOnline,
    lastSeen: live.isOnline ? undefined : formatLastSeen(live.lastSeen),
  };
}

/**
 * Maps the minimal People Chat directory response to {@link GlobalContactUI}.
 * Chat actions and presence use the canonical IAM userId. Do not fall back to
 * employeeId because Messaging, WebSocket presence, and conversation members
 * are keyed by userId.
 */
function mapDirectoryContactToGlobalContact(contact: PeopleSdk.ChatContactResponse): GlobalContactUI {
  const fullName = formatChatDisplayName({ displayName: contact.displayName, firstName: contact.name, lastName: contact.lastname, fallback: 'Sin nombre' });
  const searchText = [contact.name, contact.lastname].filter(Boolean).join(' ');
  const userId = contact.userId ?? null;
  const colorSeed = userId ?? contact.employeeId;

  return {
    id: userId !== null ? String(userId) : `employee-${contact.employeeId}`,
    userId,
    employeeId: contact.employeeId,
    fullName,
    searchText,
    roleName: contact.roleName?.trim() ?? '',
    departmentName: '',
    locationName: '',
    color: colorForUserId(colorSeed),
    isOnline: false,
    lastSeen: undefined,
  };
}

function identityContact(
  userId: number,
  fullNameValue: string | null | undefined,
  displayNameValue: string | null | undefined,
): CanonicalIdentityEntry {
  const fullName = fullNameValue?.trim().replace(/\s+/g, ' ') || 'Usuario';
  const displayName = formatChatDisplayName({ displayName: displayNameValue, fullName, fallback: 'Usuario' });
  return {
    userId,
    displayName,
    fullName,
    identityVersion: null,
    source: 'unversioned',
  };
}

function identityToGlobalContact(identity: CanonicalIdentityEntry, contact?: GlobalContactUI): GlobalContactUI {
  return {
    ...contact,
    id: String(identity.userId),
    userId: identity.userId,
    employeeId: contact?.employeeId ?? 0,
    fullName: identity.displayName,
    searchText: [contact?.searchText, identity.fullName].filter(Boolean).join(' '),
    roleName: contact?.roleName ?? '',
    departmentName: contact?.departmentName ?? '',
    locationName: contact?.locationName ?? '',
    color: contact?.color ?? colorForUserId(identity.userId),
    isOnline: contact?.isOnline ?? false,
    avatarUrl: contact?.avatarUrl,
  };
}

function mergeIdentityContacts(
  activeContacts: readonly GlobalContactUI[],
  cachedContacts: Iterable<GlobalContactUI>,
): GlobalContactUI[] {
  const merged = activeContacts.slice();
  const seenUserIds = new Set(activeContacts.flatMap((contact) => isFinitePositiveUserId(contact.userId) ? [contact.userId] : []));
  for (const contact of cachedContacts) {
    if (!isFinitePositiveUserId(contact.userId) || seenUserIds.has(contact.userId)) continue;
    seenUserIds.add(contact.userId);
    merged.push(contact);
  }
  return merged;
}

function appendDedupedDirectoryContacts(
  existing: readonly GlobalContactUI[],
  incoming: readonly GlobalContactUI[],
): GlobalContactUI[] {
  const result = existing.slice();
  const seenIdentities = new Set(existing.map(directoryContactIdentity));
  for (const contact of incoming) {
    const identity = directoryContactIdentity(contact);
    if (seenIdentities.has(identity)) continue;
    seenIdentities.add(identity);
    result.push(contact);
  }
  return result;
}

function directoryContactIdentity(contact: GlobalContactUI): string {
  return isFinitePositiveUserId(contact.userId)
    ? `user:${contact.userId}`
    : `employee:${contact.employeeId}`;
}

function finiteNonNegativeInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}

/**
 * Preview del `lastMessage` de la CONVERSACIÓN (no de un mensaje puntual) —
 * único lugar donde corresponde el resumen de álbum. Para eso usá
 * {@link individualMessagePreview} (fijados, quote de "responder a"): fijar
 * o citar UNA sola foto de un álbum no debe decir "📷 N imágenes" como si se
 * hubiera fijado/citado el álbum entero (hallazgo de revisión adversarial).
 */
function messagePreviewForSnapshot(message: ChatMessageUI): string {
  if (message.isDeleted) return 'Mensaje eliminado';
  // MOMSG-1172: ruta optimista/en vivo, transitoria — acá SÍ corresponde usar
  // el albumCount declarado por el cliente, todavía no hay nada persistido
  // que contar (a diferencia del backend, que usa un conteo real).
  if (message.albumId && !message.content) return `📷 ${message.albumCount ?? 1} imágenes`;
  return individualMessagePreview(message);
}

/** Preview de UN mensaje puntual (fijado, quote de "responder a") — nunca resume como álbum, aunque pertenezca a uno. */
function individualMessagePreview(message: ChatMessageUI): string {
  if (message.isDeleted) return 'Mensaje eliminado';
  return message.content || message.fileName || (message.type === 'IMAGE' ? 'Imagen' : 'Adjunto');
}

function messageTypeForSnapshot(message: ChatMessageUI): MessagingSdk.MessageType {
  return message.type === 'FILE' || message.type === 'IMAGE' ? message.type : 'TEXT';
}

function dedupeGlobalContacts(contacts: GlobalContactUI[]): GlobalContactUI[] {
  return appendDedupedDirectoryContacts([], contacts);
}

function buildLastMessagePreview(
  type: MessagingSdk.MessageType,
  content: MessagingSdk.MessageContent | undefined,
  album?: MessagingSdk.AlbumAssociation | null,
): string {
  const text = content?.text?.trim();
  if (text) return text;
  // MOMSG-1172: álbum de imágenes sin caption propio (ver plan FASE 1 punto 7).
  // Mismo trade-off aceptado que en buildMessagePreview: usa el albumCount
  // DECLARADO, no un conteo real — ver el comentario largo ahí.
  if (album) return `📷 ${album.albumCount} imágenes`;
  if (type === 'IMAGE') return 'Imagen';
  if (type === 'FILE') return content?.fileName?.trim() || 'Archivo adjunto';
  return '';
}

function isSelfContact(contact: GlobalContactUI, currentUserId: number, currentEmployeeId: number): boolean {
  const sameUser = contact.userId !== null && currentUserId > 0 && contact.userId === currentUserId;
  const sameEmployee = contact.userId === null && currentEmployeeId > 0 && contact.employeeId === currentEmployeeId;
  return sameUser || sameEmployee;
}

function isFinitePositiveUserId(userId: number | null | undefined): userId is number {
  return typeof userId === 'number' && Number.isFinite(userId) && userId > 0;
}

/**
 * Compares identity versions for live displayName fencing. ISO timestamps
 * compare by instant (Date.parse) so equivalent instants with different
 * serializations (millis, timezone offsets) fence as duplicates; non-date
 * versions fall back to lexicographic comparison.
 */
function isStaleIdentityVersion(previousVersion: string, incomingVersion: string): boolean {
  const previousMs = Date.parse(previousVersion);
  const incomingMs = Date.parse(incomingVersion);
  if (Number.isFinite(previousMs) && Number.isFinite(incomingMs)) {
    return incomingMs <= previousMs;
  }
  return incomingVersion <= previousVersion;
}

function positiveIntegerFromUnknown(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value > 0 ? value : null;
  }
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

function isPresenceUpdateStale(current: UserPresence, incoming: UserPresence): boolean {
  if (current.epoch && incoming.epoch && current.epoch !== incoming.epoch) return false;
  if (current.revision !== undefined && incoming.revision !== undefined) {
    return incoming.revision <= current.revision;
  }
  const currentTime = presenceEventTimeMs(current);
  const incomingTime = presenceEventTimeMs(incoming);
  return currentTime !== null && incomingTime !== null && incomingTime < currentTime;
}

function presenceEventTimeMs(presence: UserPresence): number | null {
  const raw = presence.updatedAt ?? presence.lastSeen;
  if (!raw) return null;
  const time = new Date(raw).getTime();
  return Number.isFinite(time) ? time : null;
}

function hasUsableDisplayName(value: string | null | undefined): value is string {
  const normalized = value?.trim().replace(/\s+/g, ' ') ?? '';
  if (!normalized) return false;
  if (/^\d+$/.test(normalized)) return false;
  if (/^Usuario$/i.test(normalized)) return false;
  if (/^Usuario\s+\d+$/i.test(normalized)) return false;
  return true;
}

/** Versioned SenderSnapshot displayName (event-time authoritative); null for legacy snapshots. */
function readVersionedSenderDisplayName(sender: { displayName?: unknown } | null | undefined): string | null {
  const displayName = sender?.displayName;
  return typeof displayName === 'string' && displayName.trim().length > 0 ? displayName : null;
}

function typingTimerKey(conversationId: string, userId: number): string {
  return `${conversationId}:${userId}`;
}

function optimisticMessageCreatedAt(messageId: string): number | null {
  const raw = messageId.slice('local-'.length).split('-')[0];
  const createdAt = Number(raw);
  return Number.isFinite(createdAt) ? createdAt : null;
}

function isLocalMessageId(messageId: string): boolean {
  return messageId.startsWith('local-');
}

function nextMessageStatus(current: ChatMessageUI['status'], incoming: ChatMessageUI['status']): ChatMessageUI['status'] {
  const rank: Record<ChatMessageUI['status'], number> = { sent: 0, delivered: 1, read: 2 };
  return rank[incoming] > rank[current] ? incoming : current;
}

function cloneChatMessage(message: ChatMessageUI): ChatMessageUI {
  return {
    ...message,
    replyTo: message.replyTo ? { ...message.replyTo } : message.replyTo,
    reactions: cloneMessageReactions(message.reactions),
  };
}

function chatMessagesEqualForRollback(a: ChatMessageUI, b: ChatMessageUI): boolean {
  return (
    a.id === b.id &&
    a.content === b.content &&
    a.isEdited === b.isEdited &&
    a.isDeleted === b.isDeleted &&
    a.isPinned === b.isPinned &&
    (a.replyTo?.messageId ?? null) === (b.replyTo?.messageId ?? null) &&
    (a.replyTo?.contentPreview ?? null) === (b.replyTo?.contentPreview ?? null) &&
    a.reactionsRevision === b.reactionsRevision &&
    messageReactionsEqual(a.reactions, b.reactions)
  );
}

function lastMessageEquals(a: MessagingSdk.ConversationSnapshot['lastMessage'] | undefined, b: MessagingSdk.ConversationSnapshot['lastMessage'] | undefined): boolean {
  if (!a || !b) return a === b;
  return a.messageId === b.messageId && a.contentPreview === b.contentPreview && a.type === b.type && a.sentAt === b.sentAt && a.senderId === b.senderId;
}

function pinnedMessagesEqual(a: MessagingSdk.PinnedMessageSnapshot[] | undefined, b: MessagingSdk.PinnedMessageSnapshot[] | undefined): boolean {
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  return a.every((item, index) => {
    const other = b[index];
    return item.messageId === other.messageId && item.contentPreview === other.contentPreview && item.type === other.type && item.pinnedAt === other.pinnedAt;
  });
}

function rewriteReplyTarget(message: ChatMessageUI, oldMessageId: string, newMessageId: string): ChatMessageUI {
  if (message.replyTo?.messageId !== oldMessageId) return message;
  return {
    ...message,
    replyTo: {
      ...message.replyTo,
      messageId: newMessageId,
    },
  };
}

function dedupeMessages(messages: ChatMessageUI[]): ChatMessageUI[] {
  const indexById = new Map<string, number>();
  const result: ChatMessageUI[] = [];
  for (const message of messages) {
    const existingIndex = indexById.get(message.id);
    if (existingIndex === undefined) {
      indexById.set(message.id, result.length);
      result.push(message);
      continue;
    }
    const existing = result[existingIndex];
    result[existingIndex] = {
      ...existing,
      sequence: Number.isFinite(message.sequence)
        ? message.sequence
        : existing.sequence,
      status: nextMessageStatus(existing.status, message.status),
      ...highestReactionSnapshot(existing, message),
    };
  }
  return result;
}

function highestReactionSnapshot(
  existing: ChatMessageUI,
  incoming: ChatMessageUI,
): Pick<ChatMessageUI, 'reactions' | 'reactionsRevision'> {
  const existingRevision = existing.reactionsRevision ?? 0;
  const incomingRevision = incoming.reactionsRevision ?? 0;
  if (incomingRevision > existingRevision) {
    return {
      reactions: cloneMessageReactions(incoming.reactions),
      reactionsRevision: incomingRevision,
    };
  }
  return {
    reactions: cloneMessageReactions(existing.reactions),
    reactionsRevision: existingRevision,
  };
}

function messageReactionPendingKey(conversationId: string, messageId: string, actorUserId: number): string {
  return `${conversationId}|${messageId}|${actorUserId}`;
}

function cloneMessageReactions(
  reactions: readonly MessagingSdk.MessageReaction[] | null | undefined,
): MessagingSdk.MessageReaction[] {
  return (reactions ?? []).map((reaction) => ({ ...reaction }));
}

function cloneMessageReaction(
  reaction: MessagingSdk.MessageReaction | null | undefined,
): MessagingSdk.MessageReaction | null {
  return reaction ? { ...reaction } : null;
}

function findActorReaction(
  reactions: readonly MessagingSdk.MessageReaction[],
  actorUserId: number,
): MessagingSdk.MessageReaction | null {
  return cloneMessageReaction(reactions.find((reaction) => reaction.userId === actorUserId));
}

function actorReactionsSemanticallyEqual(
  left: MessagingSdk.MessageReaction | null | undefined,
  right: MessagingSdk.MessageReaction | null | undefined,
): boolean {
  return (left?.emoji ?? null) === (right?.emoji ?? null);
}

function toggleActorReaction(
  reactions: readonly MessagingSdk.MessageReaction[],
  actorUserId: number,
  emoji: string,
): MessagingSdk.MessageReaction[] {
  const existing = reactions.find((reaction) => reaction.userId === actorUserId);
  const withoutActor = reactions.filter((reaction) => reaction.userId !== actorUserId);
  if (existing?.emoji === emoji) return cloneMessageReactions(withoutActor);
  const now = new Date().toISOString();
  return [
    ...cloneMessageReactions(withoutActor),
    {
      userId: actorUserId,
      emoji,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    },
  ];
}

function overlayActorReaction(
  reactions: readonly MessagingSdk.MessageReaction[],
  actorUserId: number,
  actorReaction: MessagingSdk.MessageReaction | null,
): MessagingSdk.MessageReaction[] {
  const withoutActor = reactions.filter((reaction) => reaction.userId !== actorUserId);
  return actorReaction
    ? [...cloneMessageReactions(withoutActor), { ...actorReaction }]
    : cloneMessageReactions(withoutActor);
}

function messageReactionsEqual(
  left: readonly MessagingSdk.MessageReaction[] | null | undefined,
  right: readonly MessagingSdk.MessageReaction[] | null | undefined,
): boolean {
  const normalizedLeft = canonicalMessageReactions(left);
  const normalizedRight = canonicalMessageReactions(right);
  return normalizedLeft.length === normalizedRight.length && normalizedLeft.every((reaction, index) => {
    const other = normalizedRight[index];
    return reaction.userId === other.userId &&
      reaction.emoji === other.emoji &&
      reaction.createdAt === other.createdAt &&
      reaction.updatedAt === other.updatedAt;
  });
}

type ReactionSnapshotRelation = 'lower' | 'equal-identical' | 'equal-conflicting' | 'higher';

function compareReactionSnapshot(
  currentReactions: readonly MessagingSdk.MessageReaction[],
  currentRevision: number,
  incomingReactions: readonly MessagingSdk.MessageReaction[],
  incomingRevision: number,
): ReactionSnapshotRelation {
  if (incomingRevision < currentRevision) return 'lower';
  if (incomingRevision > currentRevision) return 'higher';
  return messageReactionsEqual(currentReactions, incomingReactions)
    ? 'equal-identical'
    : 'equal-conflicting';
}

function canonicalMessageReactions(
  reactions: readonly MessagingSdk.MessageReaction[] | null | undefined,
): MessagingSdk.MessageReaction[] {
  return cloneMessageReactions(reactions).sort((left, right) =>
    left.userId - right.userId ||
    compareReactionField(left.emoji, right.emoji) ||
    compareReactionField(left.createdAt, right.createdAt) ||
    compareReactionField(left.updatedAt, right.updatedAt),
  );
}

function compareReactionField(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function orderMessages(messages: ChatMessageUI[]): ChatMessageUI[] {
  return [...messages].sort((a, b) => {
    const aHasSequence = Number.isFinite(a.sequence);
    const bHasSequence = Number.isFinite(b.sequence);
    if (aHasSequence && bHasSequence && a.sequence !== b.sequence) {
      return a.sequence! - b.sequence!;
    }
    if (aHasSequence !== bHasSequence) return aHasSequence ? -1 : 1;

    const timeDifference = messageTimeMs(a) - messageTimeMs(b);
    if (timeDifference) return timeDifference;

    // messageTimeMs cae en 0 para CUALQUIER mensaje local todavía sin
    // sequence confirmado (serverSentAt ausente, y `timestamp` es un string
    // ya formateado para mostrar, no un ISO parseable) — es decir, el
    // desempate anterior por `a.id.localeCompare(b.id)` era el criterio
    // real cada vez que dos o más mensajes optimistas coexistían (ej: batch
    // de adjuntos, todos creados en el mismo tick síncrono). Como el id
    // local incluye un clientMessageId (UUID) random, esa comparación
    // lexicográfica producía un orden esencialmente aleatorio — la causa
    // real del "casi ordenado pero con desorden" al enviar varios archivos.
    // Se desempata acá por el contador monótono embebido al final del id
    // (`local-{clientMessageId}-{createdAtMs}-{seq}`), que sí refleja el
    // orden real de creación sin importar colisiones de milisegundo.
    const aSeq = optimisticSequenceOf(a.id);
    const bSeq = optimisticSequenceOf(b.id);
    if (aSeq !== null && bSeq !== null && aSeq !== bSeq) return aSeq - bSeq;

    return a.id.localeCompare(b.id);
  });
}

function optimisticSequenceOf(messageId: string): number | null {
  if (!messageId.startsWith('local-')) return null;
  const parts = messageId.split('-');
  const seq = Number(parts[parts.length - 1]);
  return Number.isFinite(seq) ? seq : null;
}

function messageTimeMs(message: ChatMessageUI): number {
  const raw = message.serverSentAt ?? message.timestamp;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function canonicalMessageSentAt(message: ChatMessageUI): number | null {
  const rawSentAt = message.serverSentAt;
  if (!rawSentAt) return null;

  const sentAt = Date.parse(rawSentAt);
  return Number.isFinite(sentAt) ? sentAt : null;
}

function sameReplyTarget(a: ChatMessageReplyUI | null | undefined, b: ChatMessageReplyUI | null | undefined): boolean {
  return (a?.messageId ?? null) === (b?.messageId ?? null);
}
